/**
 * AI-OS strings — Slice 8, Gate 2.
 *
 * Staged-content rule (owner ruling, frozen design נ.1/י״א.14): the
 * ARCHITECTURE is 5-language-native (direction-agnostic components, logical
 * CSS, dynamic text lengths); CONTENT ships he+en in Gates 1-3 and completes
 * ar/ru/es in Gate 4. Gate 4 completed the full five-language content; English stays the fallback for any unknown key.
 *
 * Kept as a dedicated AI-OS module (not merged into translate/languages/*)
 * so Gate-2 work does not touch the giant shared language files the team
 * edits in parallel; migration into the main i18n tree is a Gate-4 item.
 */
import { i18n } from "../translate/i18n";

const he = {
  candidateContributors: "תורמים עצמאיים",
  candidatesPageNote: "מוצגים {shown} הראשונים על פני כל הסטטוסים — זהו עמוד, לא כל התור.",
  // ── knowledge · what the resolver admitted, and what disagreed ──────
  provSourcesTitle: "מקורות הידע",
  provSourcesSub: "מה נכנס לתשובות ומה נחסם בשער",
  provGateNote: "עובדה בלי מקור תקף או עם טריות שפגה לא נכנסת לתשובות — זו הסיבה שהמוח לא ממציא.",
  provNoSourceRead: "לא נקרא אף מקור — לא שלא נמצאה בעיה. הטעינה קוראת מ-AIKnowledge, שהוא מאגר הידע של אסטרטגיית godeal-pipeline בלבד.",
  provNoSourceReadHint: "הידע של הדייר הזה יושב ב-companyKnowledge, ומסך זה עדיין לא קורא ממנו.",
  provAdmitted: "התקבלו",
  provRejected: "נדחו",
  provSourceValid: "תקף",
  provSourceBlocked: "נחסם",
  provSourceEmpty: "ריק",
  provConflictsTitle: "סתירות שזוהו",
  provConflictsSub: "שני מקורות, תשובות שונות — וההכרעה שנבחרה",
  provNoConflicts: "לא זוהתה אף סתירה בין המקורות שנקראו.",
  provWinner: "הוכרע לטובת",
  provLosers: "מקורות שנדחו",
  provDecide: "הכרעה",
  intelLoadWindowNote: "הספירות האלה נלקחות מ-{limit} הרשומות האחרונות בכל צד, ולכן הן חלון ולא סך הכול; דייר שעבר את התקרה יראה בדיוק {limit}.",
  intelJourneyEmptyNoCause: "עדיין לא נרשמו תצפיות. המסך הזה קורא ספירות בלבד ואינו יודע לומר למה — מצב הדגלים נמצא באזור «הגדרות המוח».",
  contactMemoryWhyItMatters: "זיכרון לקוח מאושר מזין את קטגוריית היסטוריית־הלקוחות, שחוסמת היום שני תפקידים: גבייה ועוזר CRM.",
  // ── design-file conformance · departments + candidates footnote ─────
  deptSummaryTitle: "מחלקות",
  deptSummarySub: "מוכנות ממוצעת לכל מחלקה",
  deptScoredOf: "ממוצע על {scored} מתוך {roles} תפקידים",
  deptUnplaced: "{count} תפקידים שהתרשים לא מכיר: {roles} — נספרים בסיכומים שלמעלה",
  deptReady: "מוכן",
  deptBlocked: "חסום",
  candidatesApprovalNote: "רק אישור אנושי מקדם מועמד לזיכרון הארגוני. אישור ≠ הפעלה.",
  // ── system · the audit pair discloses its window ────────────────────
  auditWindowApprovals: "מוצגים {shown} האישורים החדשים ביותר — זהו חלון ולא כל השרשרת: הבקשה מוגבלת ל-{requested} שורות, ולכן הסך הכולל אינו ידוע במסך הזה.",
  auditWindowVersions: "מוצגות {shown} הגרסאות האחרונות מתוך {total}.",
  auditWindowDecisions: "מוצגות {shown} ההחלטות האחרונות — חלון, לא היומן המלא: הבקשה מוגבלת ל-{requested} שורות, ולכן הסך הכולל אינו ידוע במסך הזה.",
  // ── coverage · eight categories read a store most tenants never use ──
  coverageUnteachableCaveat:
    "{unteachable} מהקטגוריות עדיין אין דרך ללמד — הן מוצגות עם הכיסוי האמיתי שלהן, אך אינן נספרות בציון, שהוא ממוצע על {scored} מתוך {total} הקטגוריות שניתן ללמד.",
  // ── knowledge · the clamp says how much was clamped ─────────────────
  contentChars: "תווים",
  candidateApprove: "אשר",
  candidateReject: "דחה",
  candidateResolving: "מעבד…",
  candidateResolveError: "הפעולה נכשלה — נסו שוב",
  // ── employees · published companion fields, now read ─────────────────
  promptChars: "הנחיה: {chars} תווים",
  deferredInput_connectedChannels: "ערוצים מחוברים",
  deferredInput_hasActiveLearningAssets: "נכסי למידה פעילים",
  // ── brain · calibration, per the design file ─────────────────────────
  calibrationKvTitle: "כיול ביטחון",
  calibrationKvSub: "האם הביטחון של המוח מוצדק",
  calibrationOverallApproval: "שיעור אישור כולל",
  calibrationOverallApprovalHint: "מאושרים מתוך אלה שהוכרעו (אושרו או נדחו) — ממתינים ופגי־תוקף אינם נספרים",
  calibrationHighConfidence: "ביטחון גבוה (≥0.8)",
  calibrationHighConfidenceHint: "אין עדיין מקור — דוח הכיול מחזיר ספירות לפי סוג, בלי פילוח לפי רמת ביטחון",
  calibrationFloorNote: "אחוז מוצג לסוג רק אחרי {floor} הכרעות באותו סוג; מתחת לזה מוצגות הספירות עצמן.",
  confidenceDistributionTitle: "התפלגות ביטחון",
  confidenceDistributionNoFeed: "אין מסך שמזין את הגרף הזה — הכיול נמדד היום לפי סוג מועמד, לא לפי רמת ביטחון.",
  confidenceDistributionHint: "יידרש פילוח לפי רמת ביטחון בצד השרת; עד אז הכרטיס נשאר ריק במכוון.",
  // ── brain · observation kinds said in words, per the design file ──────
  obsKind_memory_confirm: "זיכרון שאושר",
  obsKind_memory_edit: "זיכרון שנערך",
  obsKind_memory_dismiss: "זיכרון שנדחה",
  obsKind_ai_handoff: "העברה לנציג אנושי",
  obsKind_ai_disable: "השבתת ה-AI",
  obsKind_execution_outcome: "תוצאת ביצוע",
  obsKind_appointment_outcome: "תוצאת פגישה",
  obsKind_payment_outcome: "תוצאת תשלום",
  obsKind_cancellation: "ביטול",
  obsKind_human_correction: "תיקון של אדם",
  obsKind_business_outcome: "תוצאה עסקית",
  obsKind_evaluation: "הערכת איכות תשובה",
  observationsScopeAllTime: "מכל הזמן — לא חלון של 30 יום",
  observationsRecentTitle: "תצפיות אחרונות",
  observationsRecentSub: "מה המוח ראה, לפי סדר",
  // ── brain · coverage split into all / needs-you, per the design file ──
  coverageAllTitle: "כיסוי ידע לפי קטגוריה",
  coverageAllSub: "כל קטגוריה נמדדת מול סף 60%",
  coverageNeedsYouTitle: "קטגוריות שדורשות אותך",
  coverageNeedsYouSub: "אלה שמתחת לסף — הן שחוסמות את הרמה הבאה",
  // ── brain · the health card's four rows, per the design file ───────
  healthLevel: "רמה",
  healthOpenIssues: "בעיות פתוחות",
  healthNoOpenIssues: "אין",
  // ── brain · the health card explains its own dash ──────────────────
  healthConfidenceCaveat: "\"ביטחון ממוצע\" נשאר \"—\" עד שיצטברו החלטות ממושלות — לא ממציאים מספר.",
  // ── D1 · no area rollups is not the same as no data ────────────────
  stNotWiredTitle: "אין מקור נתונים לאזור הזה",
  stNotWiredBody: "השרת מחזיר סטטוס וחיבורים בלבד, בלי נתוני אזורים — כלומר אין כאן «עדיין אין נתונים», אלא אין ממה לקרוא. זה מצב של המערכת, לא של הפעילות שלכם.",
  // ── F3.1 · Growth tabs in the shell strip ──────────────────────────
  gtabOverview: "סקירה",
  gtabCampaigns: "קמפיינים",
  gtabConnections: "חיבורים",
  gtabLearning: "למידה ותובנות",
  gtabReports: "דוחות וציר זמן",
  gtabMemory: "זיכרון והמלצות",
  gtabHealth: "בריאות ואבחון",
  gtabGovernance: "ממשל והגדרות",
  gtabCampaignsEmpty: "ביצועי קמפיינים עדיין לא מחוברים לסביבת העבודה הזו. הקמפיינים עצמם קיימים במערכת, אך אינם נכללים בנתונים שהמסך הזה טוען.",
  gtabCampaignsEmptyHint: "מסך הקמפיינים הקיים מציג אותם עד שהחיבור יושלם.",
  // ── F3.2 · honest empty states ───────────────────────────────────────
  // Every reason below is the cause traced in the backend, not a restatement
  // of the title. Emptiness without a cause reads as "all is well" when it
  // usually means "nothing is connected", and three of the strings these
  // replace said something stronger and untrue.
  queuesNoneAtAll:
    "לא הוגדר אף תור, ולכן אין מה לבדוק — זו לא בדיקה שעברה בהצלחה.",
  queuesNoneAtAllHint:
    "יצירת התור הראשון במסך התורים תפעיל את הבדיקה הזו.",
  channelsNoneConfigured:
    "אף ערוץ לא הוגדר — לא WhatsApp, לא Meta ולא קו קולי. זה שונה מערוץ שהוגדר ומנותק.",
  channelsNoneConfiguredHint:
    "חיבור ערוץ במסך החיבורים ייצור כאן שורה, גם לפני שהחיבור מאומת.",
  diagScanScope:
    "נסרקו יומנים פעילים בלבד ותבניות WhatsApp שנדחו. הגדרה כבויה אינה תקלה ולכן אינה נספרת כאן.",
  orgMemoryEmptyReal:
    "אף לקח לא אושר עדיין. הזיכרון הארגוני נכתב רק מאישור אנושי — לא מהשיחות עצמן.",
  orgMemoryEmptyRealHint:
    "האישור נעשה בשלב נפרד ומגודר-בעלים — לא בטאב הזה, שהוא לקריאה בלבד.",
  candidatesEmptyReal:
    "אין מועמדים ממתינים — ולא בגלל שאין מספיק שיחות. מעבר ההסקה שיוצר מועמדים אינו מתוזמן לרוץ בשום מקום, ולכן שום נפח שיחות לא ייצור אחד.",
  bpUpcomingEmptyReal:
    "אין תפקידים נעולים — כל תפקיד שהוגדר כבר עומד בתנאי הפתיחה שלו.",
  bpTrainingFullyTaught:
    "אין צעד לימוד הבא כי כל תחומי הידע מכוסים במלואם — זו הצלחה, לא חוסר.",
  calibrationZeroRows:
    "לחשבון הזה לא נרשמו עדיין תצפיות או מועמדים. המונה סופר שורות בפועל — הוא לא סף שממתין להיחצות.",
  calibrationZeroRowsHint:
    "שורות נוצרות רק כשלמידת המוח פעילה. אם היא כבויה, המסך הזה יישאר ריק — וזה נכון.",
  audienceNoTickets:
    "הפילוח נגזר מהפניות בטווח הזמן שנסרק, ובטווח הזה אין אף פנייה.",
  audienceNoTicketsHint:
    "הפנייה הראשונה בטווח תיצור את הפילוח הראשון.",
  contactNotResolved:
    "לא נמצאה זהות מאוחדת לאיש הקשר הזה. אותה תוצאה מוחזרת גם כשאיש הקשר שייך לחשבון אחר.",
  coverageUnavailable:
    "נתוני הכיסוי לא הגיעו מהשרת בטעינה הזו. זה מצב שגיאה, לא כיסוי אפס.",
  coverageUnavailableHint:
    "רענון הדף יטען אותם מחדש; אם זה חוזר, זו תקלה בשרת.",
  effectiveConfigUnavailable:
    "ההגדרות בפועל לא הגיעו מהשרת. אין להסיק מכך שלא הוגדר דבר.",
  capabilitiesNoneActive:
    "אין יכולת פעילה. חלק מהקלטים לחישוב הזה עדיין לא מחוברים, ולכן ייתכן שהרשימה קצרה מהמצב האמיתי.",
  growthAllAreasDenied:
    "לחשבון המשתמש הזה אין הרשאה לאף אחד מאזורי השיווק.",
  growthAllAreasDeniedHint:
    "מנהל החשבון יכול לפתוח אזורים במסך ההרשאות.",
  autoPoliciesEmpty: "אין עדיין רשומות מדיניות — הן נוצרות מאישורי-מועמדים אנושיים, כשהדגלים יודלקו בשער נפרד",
  autoFlowsEmpty: "אין עדיין תורים מוגדרים — יוצרים תור במסך התורים הקיים",
  // Shell
  shellTitle: "AI-OS",
  brainOf: "המוח של {name}",
  layerBrain: "מוח",
  layerEmployees: "עובדים",
  layerKnowledge: "ידע",
  layerIntelligence: "אינטליגנציה",
  layerAutomation: "אוטומציה",
  layerSystem: "מערכת",
  // Redesign IA — the 11 areas (each opens as its own page with tabs)
  areaHome: "סקירה",
  areaBrain: "המוח",
  areaEmployees: "עובדים דיגיטליים",
  areaKnowledge: "ידע",
  areaIntelligence: "אינטליגנציה",
  areaDashboard: "לוח בקרה AI",
  areaGrowth: "שיווק",
  areaVoice: "קול",
  areaAutomation: "אוטומציה וממשל",
  areaDiscovery: "לימוד המוח",
  areaSetup: "הגדרות המוח",
  // Redesign IA — tabs
  tabOverview: "סקירה",
  tabReadiness: "מוכנות",
  tabCoverage: "כיסוי ידע",
  tabObservations: "תצפיות",
  tabCalibration: "כיול ביטחון",
  tabOrg: "הארגון",
  tabReadyToHire: "מוכנים להעסקה",
  tabOpenPositions: "עמדות פתוחות",
  tabEffectiveConfig: "תצורה אפקטיבית",
  tabSources: "מקורות",
  tabKnowledgeItems: "פריטי ידע",
  tabCustomerMemory: "זיכרון לקוחות",
  tabCandidates: "מועמדים",
  tabInsights: "תובנות",
  tabJourney: "מסע ולקוחות",
  tabSegments: "פילוחי קהל",
  tabAgentPerformance: "ביצועי סוכנים",
  tabTopIssues: "בעיות עיקריות",
  tabSentimentTrends: "מגמות סנטימנט",
  tabSalesInsights: "תובנות מכירות",
  tabCampaigns: "קמפיינים",
  tabConnections: "חיבורים",
  tabLearningInsights: "למידה ותובנות",
  tabReportsTimeline: "דוחות וציר זמן",
  tabMemoryRecommendations: "זיכרון והמלצות",
  tabHealthDiagnostics: "בריאות ואבחון",
  tabGovernanceSettings: "ממשל והגדרות",
  tabCalls: "שיחות",
  tabCallSearch: "חיפוש",
  tabVoiceAgents: "נציגים",
  tabInCallBilling: "גבייה",
  tabVoiceSettings: "הגדרות Voice",
  tabFlows: "תהליכים",
  tabPolicies: "מדיניות",
  tabDecisionLog: "יומן החלטות",
  tabAuditChain: "שרשרת ביקורת",
  tabInterview: "ראיון היכרות",
  tabBlueprint: "תוכנית המוח",
  tabBrainInScreens: "המוח במסכים",
  tabDiagnostics: "אבחון ותקלות",
  tabSetupWizard: "אשף הגדרה",
  tabPersonaTone: "אישיות וטון",
  tabSystemFlags: "דגלי מערכת",
  tabConfigVersions: "גרסאות",
  tabWorkforceConsole: "קונסולת כוח עבודה",
  candidateSupport: "תצפיות תומכות",
  candidateSupportHint: "כמה תצפיות עצמאיות עומדות מאחורי ההצעה",
  employeesNoneReady: "אף תפקיד לא עבר עדיין את סף המוכנות — לימוד הידע החסר יפתח את הראשון.",
  employeesNoneOpen: "אין כרגע עמדה פתוחה — כל התפקידים שהמוח מכיר עברו את הסף.",
  homeAreasSub: "כל אזור נפתח לעמוד משלו עם טאבים — בלי גלילה אינסופית",
  areaBrainSub: "מוכנות, כיסוי ידע, תצפיות וכיול ביטחון",
  areaEmployeesSub: "14 תפקידים ב-5 מחלקות — מי מוכן, מי חסום ומה חסר",
  areaKnowledgeSub: "מה המוח יודע לצטט, ומה עדיין חסר לו",
  areaIntelligenceSub: "תובנות עם ראיות, מסע הלקוח ופילוחי קהל",
  areaDashboardSub: "המספרים התפעוליים של ה-AI",
  areaGrowthSub: "נתונים, חיבורים ותובנות שיווק במקום אחד",
  areaVoiceSub: "הסוכן הקולי: מוכנות, שיחות וחיבורים",
  areaAutomationSub: "מה רץ לבד, מה דורש אישור, ומי אישר",
  areaDiscoverySub: "ראיון היכרות, תוכנית הבנייה, והמוח בכל מסך",
  areaSetupSub: "הגדרה, אישיות, תצורה בתוקף, דגלים וגרסאות",
  // Setup area — what each of the two classic setup screens actually sets
  setupWizardSub:
    "ההגדרה הראשונית של המוח: מי אתם, איך הוא מדבר, מאיפה הוא שואב נתונים ומתי הוא מעביר לאדם",
  setupPersonaSub:
    "האישיות, הטון והמודל שמאחורי כל תשובה — ולצידם הברכה, המקורות והחיבורים שמזינים אותה",
  // Home area ("סקירה")
  homeReadinessSubtitle: "המספרים שמאחורי הציון",
  homeAsksTitle: "מה המוח מבקש ממך",
  homeAsksSubtitle: "כל אישור מזיז את המוח צעד",
  homePendingApprovals: "החלטות שנרשמו כדורשות אישור",
  seamUnavailable: "הנתון אינו זמין כרגע",
  homeShadowVerdictsSub: "פסיקות במצב צל — נרשמו בלבד. אין תור אישורים להחלטות, ואף אחת לא הופעלה.",
  homeReadyRoles: "תפקידים מוכנים להעסקה",
  homeReadyRolesHint: "עברו את סף המוכנות וממתינים לאישורך",
  homeReadyRolesBlocked: "אף תפקיד לא ייפתח עדיין: כל תפקיד דורש גם ביטחון מוח של 50% ומעלה, ואין עדיין מדידת ביטחון — לימוד ידע לבדו לא יפתח תפקיד.",
  homeBlockersSubtitle: "שלושת החסמים המובילים",
  homeBlockersEmpty: "אין כרגע חסם מדווח — כשיצטבר מידע, מה שחוסם יופיע כאן.",
  homeJumpTitle: "לאן להיכנס",
  homeJumpSubtitle: "האזורים שמקדמים את המוח הכי הרבה",
  // "The Brain in your screens" — where the same brain is already injected
  inScreenNote:
    "אותו מוח בדיוק מופיע בתוך המסכים היומיומיים — כקלף קריאה בלבד. הוא לא כותב דבר, ולמי שאין הרשאה הקלף פשוט לא מוצג.",
  inScreenOpen: "פתח מסך",
  inScreenWhere: "מיקום",
  inScreenTicketsTitle: "המוח על הלקוח (פניות)",
  inScreenTicketsBody:
    "שלב במסע, כוונת קנייה, סנטימנט, מה חסר כדי להתקדם, והשאלה הבאה שכדאי לשאול — עם הראיות שמאחורי כל טענה.",
  inScreenQueuesTitle: "פערים בתסריטי הבוט (תורים)",
  inScreenQueuesBody:
    "זיהוי פערים על התורים הקיימים — למשל תור בלי הודעת פתיחה — כדי שתדע מה לסגור קודם.",
  inScreenCampaignsTitle: "פילוחי קהל (קמפיינים)",
  inScreenCampaignsBody:
    "פילוח דטרמיניסטי מתוך הנתונים הקיימים, כהצעה בלבד: אף אחד לא מתווסף אוטומטית והשליחה נשארת במסך הקמפיינים.",
  inScreenSettingsTitle: "אבחון תקלות (הגדרות)",
  inScreenSettingsBody:
    "מה נשבר, למה, מי אמור לתקן ומה הצעד הבא — כל תיקון הוא קישור למסך הקיים, בלי תיקון אוטומטי.",
  // Areas whose engine is not built yet — honest planned-state copy
  areaPlannedTitle: "האזור הזה עדיין לא מחובר",
  areaPlannedReason:
    "המסך קיים, אבל המנוע שמזין אותו עדיין לא נבנה — ולכן לא יוצג כאן מספר מומצא.",
  areaPlannedHint: "ברגע שהמנוע ינחת, הנתונים האמיתיים יופיעו כאן אוטומטית.",
  loading: "טוען נתונים…",
  loadError: "טעינת הנתונים נכשלה",
  retry: "נסה שוב",

  // Brain layer (MRI)
  readinessTitle: "מוכנות המוח",
  levelLabel: "רמה {level}",
  scoreLabel: "ציון מוכנות",
  healthTitle: "בריאות המוח",
  healthOperational: "פעיל ומוגדר",
  healthNotOperational: "עדיין לא מוגדר במלואו",
  healthConfidence: "ביטחון ממוצע",
  healthLearningQuality: "איכות אות הלמידה",
  issuesTitle: "מה מעכב את המוח",
  noIssues: "אין בעיות פתוחות",
  coverageTitle: "כיסוי ידע לפי קטגוריה",
  coverageEmpty: "עדיין אין כיסוי ידע — למדו את המוח דרך מסכי הידע",
  coverageMissingPrefix: "חסר:",
  observationsTitle: "תצפיות (למידה)",
  observationsEmpty:
    "המוח שלך עוד לא צופה בשיחות — דגל התצפיות (BRAIN_LEARNING_OBSERVE) כבוי. זה המצב המתוכנן בשלב זה, לא תקלה.",
  observationsTotal: "סה‏“כ תצפיות",
  lastSeen: "נצפה לאחרונה",
  decisionsTitle: "יומן החלטות (Governance)",
  decisionsEmpty:
    "אין עדיין החלטות רשומות — דגל רישום ה-Governance (BRAIN_GOVERNANCE_RECORD) כבוי. זה המצב המתוכנן בשלב זה.",
  governanceMetricsTitle: "מדדי Governance",
  totalDecisions: "סה‏“כ החלטות",
  calibrationTitle: "כיול ביטחון",
  calibrationNoData: "אין עדיין מספיק נתונים לחישוב",
  approvalRate: "שיעור אישור",
  quarantinedConfidence: "ערכי ביטחון בהסגר",
  distinctHumanActors: "תורמים אנושיים שונים",
  shadowNote:
    "המוח רץ במצב צל (shadow) — צופה ומייעץ בלבד; שום החלטה אינה נאכפת על שיחות אמיתיות.",
  discoveryBtn: "ראיון היכרות (Discovery)",
  blueprintBtn: "תוכנית המוח (Blueprint)",
  backToBrain: "חזרה למסך המוח",

  // Employees layer
  orgChartTitle: "הארגון הדיגיטלי",
  orgChartSubtitle: "תרשים חי — כולו נגזר מנתוני אמת; דבר אינו נשמר",
  deptSales: "מכירות",
  deptSupport: "שירות ותמיכה",
  deptMarketing: "שיווק",
  deptVoice: "קול",
  deptOperations: "תפעול",
  statusReady: "מוכן לגיוס",
  statusLocked: "עמדה פתוחה",
  statusHired: "מגויס",
  openPositionHint: "מה המוח עוד צריך כדי לפתוח את התפקיד:",
  readinessOf: "מוכנות",
  trainingMinutes: "כ-{min} דק' לימוד לפתיחה",
  kpiEmpty: "מדידת ביצועים תופעל כשהעובדים יפעלו דרך המוח",
  agentsTitle: "מופעים חיים (AIAgents)",
  agentsEmpty: "אין עדיין סוכני AI מוגדרים לחברה זו",
  agentsTruncated: "מוצגים {shown} הראשונים מתוך יותר — הרשימה קוצצה",
  agentEnabled: "פעיל",
  agentDisabled: "כבוי",
  effectiveConfigTitle: "התצורה האפקטיבית (One Brain)",
  effectiveConfigSubtitle: "כל העובדים יורשים את אותה תצורה — מוח אחד לחברה",
  cfgModel: "מודל",
  cfgCredentialSource: "מקור אישורים",
  cfgCredentialMasked: "מוצג כתווית בלבד — סודות לעולם אינם נשלחים",
  cfgKnowledgeSource: "מקור ידע",
  cfgLanguage: "שפה",
  cfgPersona: "פרומפט פרסונה",
  cfgTone: "טון",
  cfgToneEmpty: "לא הוגדר (המנוע אינו ממציא ברירת מחדל)",
  capabilitiesTitle: "יכולות",
  capabilitiesDeferredNote:
    "חלק מהיכולות ({inputs}) עדיין נפתרות חלקית ב-resolver הצל — הן עשויות להיות מוצגות בחסר, לעולם לא ביתר.",
  channelsTitle: "ערוצים מחוברים",
  channelsEmpty: "אין ערוצים מחוברים עדיין",
  channelConnected: "{connected} מתוך {total} מחוברים",

  // Knowledge layer
  knowledgeTitle: "Knowledge Explorer",
  knowledgeSubtitle: "כל מה שהמוח יודע — ממקורות אמת, במילון של המוח עצמו",
  strategyLabel: "אסטרטגיית ידע",
  strategyAiKnowledge: "מחסן AIKnowledge",
  strategyCompanyKnowledge: "ידע חברה מובנה (companyKnowledge)",
  itemsTitle: "פריטי ידע",
  itemsEmpty: "המוח עוד לא לימדו אותו — הוסיפו ידע במסכי ההגדרות הקיימים",
  itemsTruncated: "מוצגים {shown} פריטים — הרשימה קוצצה בשרת",
  itemActive: "פעיל",
  itemInactive: "כבוי",
  contactMemoryTitle: "זיכרון לקוחות (ContactMemory)",
  contactMemoryNote:
    "מוצג כספירות בלבד — פרטי זיכרון פר-לקוח נשארים בכרטיס הלקוח",
  cmSuggested: "הצעות ממתינות",
  cmConfirmed: "מאושרים",
  cmDismissed: "נדחו",
  cmTotal: "סה‏“כ",
  orgMemoryTitle: "זיכרון ארגוני (BrainOrgMemory)",
  orgMemoryEmpty:
    "הזיכרון הארגוני ריק — דגל BRAIN_ORG_MEMORY_ENABLED כבוי. זה המצב המתוכנן.",
  candidatesTitle: "הצעות המוח (מועמדים)",
  candidatesEmpty:
    "המוח עוד לא הציע דבר — דגלי הלמידה כבויים. כשיודלקו (בשער נפרד), הצעות יופיעו כאן לאישור אנושי.",
  candidatesViewOnly:
    "תצוגה בלבד — אישור/דחייה ייפתחו בשער בעלים נפרד (Tier-2)",
  statusPending: "ממתין לבדיקה",
  statusApproved: "אושר",
  statusRejected: "נדחה",
  statusExpired: "פג",

  // Discovery
  discoveryTitle: "ראיון היכרות — המוח לומד את העסק",
  discoveryIntro:
    "כמה שאלות קצרות שמתאימות את המוח לעסק שלך. שום דבר לא מופעל ולא נשמר — בסוף תקבלו תוכנית מוצעת (Blueprint) בלבד.",
  ephemeralBanner:
    "הצעה בלבד — התשובות אינן נשמרות ושום התנהגות אינה משתנה. הפעלה בפועל נעשית תמיד במסכי ההגדרות הקיימים, באישור אדם.",
  stepBusinessType: "סוג העסק",
  stepMainGoal: "המטרה המרכזית",
  stepIntake: "מה חשוב לאסוף מכל פונה",
  stepHandoff: "מתי מעבירים לבן-אדם",
  stepChannels: "ערוצים",
  stepKpi: "מדד ההצלחה",
  // Short labels for the two steps whose question is a full sentence — used
  // ONLY under the progress dots; the questions themselves stay as they are.
  stepIntakeShort: "איסוף מידע",
  stepHandoffShort: "העברה לאדם",
  archLaw: "משרד עורכי דין",
  archRealEstate: "תיווך נדל“ן",
  archTravel: "סוכנות נסיעות",
  archClinic: "קליניקה / מרפאה",
  archEcommerce: "חנות / מסחר",
  archServices: "עסק שירותים",
  archOther: "אחר",
  goalSales: "יותר מכירות",
  goalSupport: "שירות מהיר יותר",
  goalScheduling: "קביעת פגישות",
  goalCollections: "גבייה ומעקב",
  handoffStrict: "קשיח — כל שאלה מהותית עוברת מיד לאדם",
  handoffMedium: "בינוני — הבוט מטפל בתיאומים; משא ומתן עובר לאדם",
  handoffSoft: "רך — הבוט מלווה עד סגירה; אדם נכנס רק בסוף",
  channelConnectedTag: "מחובר",
  channelConfiguredNotConnectedTag: "מוגדר אך לא מחובר — בדקו במסך החיבורים",
  channelNotConnectedTag: "לא מחובר — מחברים במסך החיבורים",
  next: "הבא",
  back: "חזרה",
  finish: "הצג את ה-Blueprint",
  otherFieldPlaceholder: "פרט…",

  // Blueprint
  blueprintTitle: "תוכנית המוח המוצעת (Blueprint)",
  blueprintHonesty:
    "לפי מבנה התהליך שהצהרת — אין עדיין נתוני שימוש. ההמלצות יתעדכנו כשהמוח יתחיל לצפות (בשער נפרד).",
  bpRecommended: "מומלץ לגיוס עכשיו",
  bpRecommendedEmpty: "אין עדיין עובדים מוכנים לגיוס — למדו את המוח כדי לפתוח עמדות",
  bpUpcoming: "עמדות פתוחות (לפי קרבה לפתיחה)",
  bpTraining: "הצעדים הבאים ללימוד המוח",
  bpTrainingEmpty: "אין המלצות לימוד פעילות",
  bpUnlocks: "פותח את:",
  bpChannels: "ערוצים בתוכנית",
  bpProposed: "מוצע",
  bpKeep: "מאשר",
  bpRemove: "מסיר",
  bpLater: "אחר-כך",
  bpLegend:
    "מוצע = המוח מציע, כלום לא קרה · מאושר = אדם אישר, התנהגות לא השתנתה · הוחל = רק אחרי שער הפעלה עתידי",
  bpFromInterview: "מהראיון שלך",
  // The third state of the frozen legend (מוצע → אושר → הוחל); the first two
  // already exist as bpProposed / statusApproved.
  bpApplied: "הוחל",

  // ── Gate 3: Insight panels ──
  insightTicketTitle: "המוח על הלקוח הזה",
  insightTicketSubtitle: "תמונת אינטליגנציה חיה — קריאה בלבד, אפס כתיבות",
  insightCampaignsTitle: "קהלים מוסברים מהמוח",
  insightCampaignsSubtitle: "פילוח דטרמיניסטי מנתונים קיימים — הצעה בלבד",
  insightQueuesTitle: "המוח על הזרימות",
  insightSettingsTitle: "האבחון של המוח — Error Copilot",
  noContextData: "אין עדיין נתוני מוח ללקוח זה",
  stage_new: "חדש",
  stage_engaged: "בקשר",
  stage_qualifying: "באפיון",
  stage_proposal: "בהצעה",
  stage_negotiation: "במשא ומתן",
  stage_won: "נסגר בהצלחה",
  stage_lost: "אבוד",
  stage_dormant: "רדום",
  intent_hot: "חם",
  intent_warm: "פושר",
  intent_cold: "קר",
  intent_unknown: "לא ידוע",
  sentiment_positive: "חיובי",
  sentiment_neutral: "ניטרלי",
  sentiment_negative: "שלילי",
  sentimentNotDerived: "עדיין לא נגזר — אין אות סנטימנט, ולא ממציאים",
  journeyLabel: "שלב במסע",
  intentLabel: "כוונת קנייה",
  sentimentLabel: "סנטימנט",
  leadScoreLabel: "ציון ליד",
  leadUnscored: "עדיין ללא ניקוד — אין אות אמיתי, ולא ממציאים",
  missingInfoTitle: "מידע חסר",
  nextQuestionTitle: "השאלה הבאה המומלצת",
  intakeTitle: "מה כבר נאסף",
  confirmedMemoryTitle: "זיכרון מאושר",
  evidenceLabel: "עוגני ראיות",
  blocksStagePrefix: "חוסם את שלב:",

  // Audience preview
  audienceScan: "נסרקו {tickets} הכרטיסים שעודכנו לאחרונה · {pool} אנשי קשר · נותחו לעומק {analyzed}",
  audienceTruncatedNote: "החלון קוצץ — אלו אינם כל הנתונים, רק הפעילות האחרונה",
  audienceProposalOnly: "הצעה בלבד — איש אינו נוסף לרשימת תפוצה אוטומטית; השליחה נשארת במסך הקמפיינים, בתבניות מאושרות בלבד ({approved} מאושרות)",
  basis_intent: "כוונת קנייה",
  basis_stage: "שלב במסע",
  "basis_ticket-status": "סטטוס כרטיס",
  basis_recency: "פעילות",
  basis_channel: "ערוץ",
  "recency_active-7d": "פעיל בשבוע האחרון",
  "recency_active-30d": "פעיל בחודש האחרון",
  recency_older: "ותיק יותר",
  moreNames: "ועוד {n}",

  // Queues insight + Flow-Builder preview skeleton
  flowGapsTitle: "פערי זרימה",
  flowNoGaps: "לא נמצאו פערים — כל התורים מוגדרים",
  "gap_missing-greeting": "חסרה הודעת פתיחה",
  "gap_no-flow-options": "אין אפשרויות זרימה מוגדרות",
  "gap_no-enabled-channels": "לא הוגדרו ערוצים לתור",
  optionsCountLabel: "{n} אפשרויות",
  flowBuilderTitle: "Flow Builder — Preview",
  flowBuilderHonesty: "מנוע החילול מתיאור-חופשי עדיין אינו קיים — הוא יגיע כ-slice נפרד, באישור בעלים, מאחורי דגל. כאן יוצג Preview של זרימה מחוללת, לאישור אדם, לפני כל ייבוא.",
  flowBuilderPlaceholder: "תיאור השירות בטקסט חופשי — ייפתח בשער עתידי",

  // Error Copilot / diagnostics
  diagTitle: "האבחון של המוח",
  diagClean: "לא נמצאו תקלות תצורה — כל הבדיקות עברו",
  diagCheckedAt: "נבדק",
  diagEvidence: "ראיה",
  diagSeverity_critical: "קריטי",
  diagSeverity_warning: "אזהרה",
  diagSeverity_info: "מידע",
  diagRetryHelps: "ניסיון חוזר עשוי לעזור",
  diagRetryWontHelp: "ניסיון חוזר לא יעזור",
  diagNeedsAdmin: "דורש אדמין",
  diagNeedsSupport: "דורש תמיכה",
  diagFixBtn: "לתיקון במסך הקיים",
  diagUncertainTitle: "תקלה לא מזוהה",
  diagUncertainBody: "לא הצלחתי לקבוע את הסיבה; מוצג בדיוק מה שנבדק — בלי ניחושים.",
  diagStage: "באיזה שלב",
  diagFault: "באשמת מי",
  fault_provider: "הספק",
  fault_config: "הגדרה",
  fault_system: "מערכת",
  diagShownOf: "מוצגות {shown} מתוך {total} תבניות שנדחו",
  diag_channel_disconnected_title: "ערוץ מנותק",
  diag_channel_disconnected_cause: "החיבור קיים אך אינו מדווח מחובר — לרוב session שפג או מכשיר לא מקוון.",
  diag_channel_disconnected_steps: "פתחו את מסך החיבורים וחברו מחדש.",
  diag_channel_disconnected_stage: "ה-session מול הערוץ",
  diag_channel_in_setup_title: "חיבור בתהליך הקמה",
  diag_channel_in_setup_cause: "החיבור בתהליך הקמה — ממתין לסריקת QR או לסיום ההתחברות; זו אינה תקלה.",
  diag_channel_in_setup_steps: "השלימו את ההקמה במסך החיבורים.",
  diag_channel_in_setup_stage: "הקמת החיבור",
  diag_voice_invalid_token_title: "אישורי ספק הקול נדחו",
  diag_voice_invalid_token_cause: "הספק דחה את פרטי-הגישה — ייתכן שסובבו או בוטלו.",
  diag_voice_invalid_token_steps: "עדכנו את פרטי-הגישה במסך הגדרות הקול; ניסיון חוזר לא יעזור לפני כן.",
  diag_voice_invalid_token_stage: "אימות מול הספק",
  diag_voice_suspended_title: "חיבור קול מושהה",
  diag_voice_suspended_cause: "החיבור סומן כמושהה.",
  diag_voice_suspended_steps: "נדרשת בדיקה מול התמיכה לפני הפעלה מחדש.",
  diag_voice_suspended_stage: "ניהול המערכת",
  diag_voice_disconnected_title: "חיבור קול לא מאומת",
  diag_voice_disconnected_cause: "החיבור הוגדר אך טרם אומת מול הספק.",
  diag_voice_disconnected_steps: "הריצו בדיקת חיבור במסך הגדרות הקול.",
  diag_voice_disconnected_stage: "בדיקת החיבור",
  diag_calendar_error_title: "שגיאת יומן אחרונה",
  diag_calendar_error_cause: "הקריאה האחרונה לספק היומן נכשלה; השגיאה תנוקה בהצלחה הבאה.",
  diag_calendar_error_steps: "בדקו את החיבור במסך הפגישות; אם חוזר — התחברות מחדש.",
  diag_calendar_error_stage: "קריאה לספק היומן",
  diag_template_rejected_title: "תבנית נדחתה על ידי Meta",
  diag_template_rejected_cause: "Meta דחתה את התבנית — לא ניתן לשלוח בה עד תיקון ואישור מחדש.",
  diag_template_rejected_steps: "ערכו והגישו מחדש את התבנית במסך ההגדרות.",
  diag_template_rejected_stage: "אישור תבניות אצל Meta",

  // Long-running task contract (י״א.16)
  ltStarted: "התחיל",
  ltScope: "היקף",
  ltStage: "שלב נוכחי",
  ltCancel: "ביטול",
  ltPartial: "תוצאות חלקיות",
  ltNotify: "התראה בסיום",
  ltCounter: "{done} מתוך {total}",
  ltNoFake: "אין מונה אמיתי מהמנוע — מוצג חיווי בלתי-מוגדר, לא אחוזים",

  // Intelligence layer
  intelJourneyTitle: "מסע והסלמות (Handoff)",
  intelJourneyEmpty: "אין עדיין תצפיות — דגלי הלמידה כבויים; זה המצב המתוכנן. כשיודלקו, יוצג כאן איפה ולמה הבוט העביר לאדם.",
  intelDashLink: "לדשבורד ה-AI הקיים",
  intelAudienceTitle: "פילוחי קהל (Tier-1)",
  intelCalibrationRatio: "מועמדים / תצפיות",

  // Automation layer
  autoPoliciesTitle: "מדיניות (Policy Registry)",
  autoPoliciesBanner: "רשומות מאושרות — אף אחת אינה פעילה, בעיצוב (Approved ≠ Active). אין מנוע אכיפה שצורך אותן.",
  policyInactive: "לא פעיל",
  autoFlowsTitle: "זרימות בוט (מהתורים הקיימים)",
  autoApprovalsTitle: "שרשרת אישורים",
  approvalsEmpty: "אין עדיין רשומות אישור — יופיעו כשיהיו מועמדים ואישורים אנושיים",
  configVersionsTitle: "גרסאות תצורה",
  configVersionsEmpty:
    "עדיין לא נרשמו גרסאות. הכותב קיים ורץ בכל תור ממושל שבו האש הקונפיגורציה השתנה — אבל רק כאשר BRAIN_GOVERNANCE_RECORD דלוק.",

  // System layer
  sysFlagsTitle: "דגלי מערכת (Brain)",
  flagsProcessNote: "דגלי-תהליך — זהים לכל הדיירים על השרת המשותף; שינוי דגל = פריסה, לא הגדרה",
  flagOn: "דולק",
  flagOff: "כבוי",
  sysConfigTitle: "תצורה אפקטיבית",
  sysAuditTitle: "ביקורת",

  // The design closes every HOME queue card with a gradient CTA and an
  // outlined "details". Ours point at two different screens on purpose:
  // coverage is where the missing knowledge gets taught, observations is
  // where the raw finding lives.
  homeAskCta: "ללמד עכשיו",
  homeAskDetails: "פרטים",

  // ── Brain prose authored by the BACKEND, said in this language ─────────
  // evaluateBrainEvolution.ts writes readiness.health.issues[],
  // coverage[].missing[] and requirements[].guidance as English sentences,
  // and four screens printed them verbatim — so a Hebrew tenant read English
  // on the HOME hero, on all three queue cards, under every coverage bar and
  // in the Blueprint. brainText() below matches each form and renders it from
  // here; anything it does not recognise still shows the raw sentence.
  biNoSettings: "לא הוגדרו הגדרות AI לחברה הזאת",
  biAiDisabled: "ה-AI כבוי לחברה הזאת",
  biNoKnowledge: "עדיין לא נלמד אף פריט ידע",
  biNoObservations: "לא נרשמו תצפיות למידה (ייתכן שדגלי הלמידה כבויים)",
  biNoMemories: "אין עדיין זיכרונות לקוח מאושרים",
  biRolesLocked: "{n} מתוך {total} תפקידים עדיין לא ניתנים לפתיחה — {reasons}",
  biReasonCoverage: "ידע מתחת ל-{pct}% ב{cats}",
  biReasonNoConfidence: "עדיין לא נרשם ביטחון החלטה",
  biReasonThinSample: "אין מספיק ראיות לציון ביטחון ({n} מתוך {min} החלטות)",
  biReasonLowConfidence: "ביטחון ההחלטה {value} נמוך מ-{min}",
  bcConfigureSettings: "להגדיר את הגדרות ה-AI לחברה",
  bcConnectCrm: "לחבר מקור נתונים מה-CRM",
  bcMoreMemories: "לאשר עוד זיכרונות לקוח ({n}/{target})",
  bcMoreObservations: "דרושות עוד אינטראקציות נצפות ({n}/{target})",
  bcDefineTone: "להגדיר את טון הדיבור של החברה",
  bcVoiceUnavailable: "לימוד סגנון קולי עדיין לא זמין",
  bcAddKnowledge: "להוסיף פריטי ידע בקטגוריית {cat} ({n}/{target})",
  bgTeachCategory: "ללמד את המוח על {cat} עד כיסוי של {pct}% לפחות",
  bgConfidenceGrows: "ביטחון ההחלטה של המוח גדל ככל שמצטברות החלטות תחת ממשל",
  bgThinEvidence: "אין עדיין מספיק ראיות — דרושות {n} מתוך {min} החלטות מתועדות לפני שציון ביטחון אומר משהו",

  // The shadow-mode banner is a `note` card in the design, and a design note
  // card always has a title. We had the sentence but no name for it, so the
  // card rendered headless in a grid where every sibling is titled.
  shadowNoteTitle: "מצב צל",

  // Data-First: an absent dataset renders an honest empty state saying WHAT is
  // missing and WHY. The flags card had no such copy, so a failed read drew a
  // card with a head and a footnote and nothing between them.
  flagsEmpty: "לא הוחזר אף דגל-תהליך. אלה דגלים ברמת השרת — רשימה ריקה משמעה שהקריאה לא הצליחה, לא שאין דגלים.",

  // The roadmap counts card had no name — it was the one untitled cell in a
  // grid where every sibling is titled, because nothing in the dictionary
  // named the summary itself (only the three things it counts).
  bpRoadmapTitle: "תמונת התוכנית",
};

const en = {
  candidateContributors: "Independent contributors",
  candidatesPageNote: "Showing the first {shown} across all statuses — this is a page, not the whole queue.",
  // ── knowledge · what the resolver admitted, and what disagreed ──────
  provSourcesTitle: "Knowledge sources",
  provSourcesSub: "What reaches the answers and what the gate blocks",
  provGateNote: "A fact with no valid source, or with expired freshness, never reaches an answer — that is why the Brain does not invent.",
  provNoSourceRead: "No source was read — which is not the same as no problem found. The loader reads AIKnowledge, the knowledge store of the godeal-pipeline strategy only.",
  provNoSourceReadHint: "This tenant's knowledge lives in companyKnowledge, which this screen does not read yet.",
  provAdmitted: "Admitted",
  provRejected: "Rejected",
  provSourceValid: "Valid",
  provSourceBlocked: "Blocked",
  provSourceEmpty: "Empty",
  provConflictsTitle: "Detected contradictions",
  provConflictsSub: "Two sources, different answers — and the resolution that was chosen",
  provNoConflicts: "No contradiction was detected among the sources that were read.",
  provWinner: "Resolved to",
  provLosers: "Sources overruled",
  provDecide: "Resolved",
  intelLoadWindowNote: "These counts are taken from the most recent {limit} rows on each side, so they are a window and not a total; a tenant past the cap reads exactly {limit}.",
  intelJourneyEmptyNoCause: "No observations have been recorded yet. This screen reads counts only and cannot say why — the flag state lives in the Brain settings area.",
  contactMemoryWhyItMatters: "A confirmed contact memory feeds the customer-history category, which today gates two roles: collections and the CRM assistant.",
  // ── design-file conformance · departments + candidates footnote ─────
  deptSummaryTitle: "Departments",
  deptSummarySub: "Average readiness per department",
  deptScoredOf: "Averaged over {scored} of {roles} roles",
  deptUnplaced: "{count} role(s) this chart cannot place: {roles} — counted in the totals above",
  deptReady: "Ready",
  deptBlocked: "Blocked",
  candidatesApprovalNote: "Only a human approval promotes a candidate into organizational memory. Approval is not activation.",
  // ── system · the audit pair discloses its window ────────────────────
  auditWindowApprovals: "Showing the newest {shown} approvals — a window, not the full trail: the request is capped at {requested} rows, so the total is not known on this screen.",
  auditWindowVersions: "Showing the newest {shown} of {total} versions.",
  auditWindowDecisions: "Showing the newest {shown} decisions — a window, not the full log: the request is capped at {requested} rows, so the total is not known on this screen.",
  // ── coverage · eight categories read a store most tenants never use ──
  coverageUnteachableCaveat:
    "{unteachable} of these categories have no way to teach them yet — they are shown with their real coverage but are not counted in the score, which is averaged over the {scored} of {total} categories you can actually teach.",
  // ── knowledge · the clamp says how much was clamped ─────────────────
  contentChars: "Content",
  candidateApprove: "Approve",
  candidateReject: "Reject",
  candidateResolving: "Resolving…",
  candidateResolveError: "Action failed — try again",
  // ── employees · published companion fields, now read ─────────────────
  promptChars: "Prompt: {chars} chars",
  deferredInput_connectedChannels: "connected channels",
  deferredInput_hasActiveLearningAssets: "active learning assets",
  // ── brain · calibration, per the design file ─────────────────────────
  calibrationKvTitle: "Confidence calibration",
  calibrationKvSub: "Is the brain's confidence justified",
  calibrationOverallApproval: "Overall approval",
  calibrationOverallApprovalHint: "Approved out of those decided (approved or rejected) — pending and expired are not counted",
  calibrationHighConfidence: "High confidence (≥0.8)",
  calibrationHighConfidenceHint: "No source yet — the calibration report returns counts by kind, with no breakdown by confidence level",
  calibrationFloorNote: "A percentage appears for a kind only after {floor} decisions of that kind; below that the counts themselves are shown.",
  confidenceDistributionTitle: "Confidence distribution",
  confidenceDistributionNoFeed: "Nothing feeds this graph — calibration is measured today by candidate kind, not by confidence level.",
  confidenceDistributionHint: "It needs a breakdown by confidence level on the server; until then this card stays deliberately empty.",
  // ── brain · observation kinds said in words, per the design file ──────
  obsKind_memory_confirm: "Memory confirmed",
  obsKind_memory_edit: "Memory edited",
  obsKind_memory_dismiss: "Memory dismissed",
  obsKind_ai_handoff: "Handover to a human agent",
  obsKind_ai_disable: "AI switched off",
  obsKind_execution_outcome: "Execution outcome",
  obsKind_appointment_outcome: "Appointment outcome",
  obsKind_payment_outcome: "Payment outcome",
  obsKind_cancellation: "Cancellation",
  obsKind_human_correction: "Human correction",
  obsKind_business_outcome: "Business outcome",
  obsKind_evaluation: "Answer quality evaluation",
  observationsScopeAllTime: "All time — not a 30-day window",
  observationsRecentTitle: "Recent observations",
  observationsRecentSub: "What the brain saw, in order",
  // ── brain · coverage split into all / needs-you, per the design file ──
  coverageAllTitle: "Knowledge coverage by category",
  coverageAllSub: "Each category is measured against a 60% threshold",
  coverageNeedsYouTitle: "Categories that need you",
  coverageNeedsYouSub: "The ones below the threshold — these are what hold the next level back",
  // ── brain · the health card's four rows, per the design file ───────
  healthLevel: "Level",
  healthOpenIssues: "Open issues",
  healthNoOpenIssues: "None",
  // ── brain · the health card explains its own dash ──────────────────
  healthConfidenceCaveat: "Average confidence stays \"—\" until governed decisions accumulate. We do not invent a number.",
  // ── D1 · no area rollups is not the same as no data ────────────────
  stNotWiredTitle: "This area has no data source",
  stNotWiredBody: "The server returns status and connections only, with no area rollups — so this is not \"no data yet\", it is nothing to read from. That is a fact about the system, not about your activity.",
  // ── F3.1 · Growth tabs in the shell strip ──────────────────────────
  gtabOverview: "Overview",
  gtabCampaigns: "Campaigns",
  gtabConnections: "Connections",
  gtabLearning: "Learning & insights",
  gtabReports: "Reports & timeline",
  gtabMemory: "Memory & recommendations",
  gtabHealth: "Health & diagnostics",
  gtabGovernance: "Governance & settings",
  gtabCampaignsEmpty: "Campaign performance is not wired into this workspace yet. The campaigns themselves exist in the system, but they are not part of the data this screen loads.",
  gtabCampaignsEmptyHint: "The existing Campaigns screen shows them until this connection is finished.",
  // ── F3.2 · honest empty states ───────────────────────────────────────
  // Every reason below is the cause traced in the backend, not a restatement
  // of the title. Emptiness without a cause reads as "all is well" when it
  // usually means "nothing is connected", and three of the strings these
  // replace said something stronger and untrue.
  queuesNoneAtAll:
    "No queue is defined, so there was nothing to check — this is not a passed check.",
  queuesNoneAtAllHint:
    "Creating the first queue on the Queues screen turns this check on.",
  channelsNoneConfigured:
    "No channel is configured at all — no WhatsApp, no Meta, no voice line. That is different from a channel that is set up and disconnected.",
  channelsNoneConfiguredHint:
    "Connecting a channel on the Connections screen creates a row here, even before the connection is verified.",
  diagScanScope:
    "Scanned active calendars only, and WhatsApp templates that were rejected. A disabled setting is not a fault, so it is not counted here.",
  orgMemoryEmptyReal:
    "No lesson has been approved yet. Organizational memory is written only by a human approval, never by the conversations themselves.",
  orgMemoryEmptyRealHint:
    "Approval happens in a separate, owner-gated step — not on this tab, which is read-only.",
  candidatesEmptyReal:
    "No candidates are waiting — and not because there are too few conversations. The inference pass that creates candidates is not scheduled to run anywhere, so no volume of conversation will produce one.",
  bpUpcomingEmptyReal:
    "No roles are locked — every role that exists already meets its unlock conditions.",
  bpTrainingFullyTaught:
    "There is no next teaching step because every knowledge area is fully covered — this is a success, not a gap.",
  calibrationZeroRows:
    "No observations or candidates have been recorded for this account. The counter is a row count, not a threshold waiting to be crossed.",
  calibrationZeroRowsHint:
    "Rows appear only while the brain's learning is switched on. With it off this stays empty, and that is correct.",
  audienceNoTickets:
    "Segments are derived from the tickets inside the scanned time window, and there are none in that window.",
  audienceNoTicketsHint:
    "The first ticket inside the window produces the first segment.",
  contactNotResolved:
    "No unified identity was found for this contact. The same result is returned when the contact belongs to another account.",
  coverageUnavailable:
    "Coverage data did not arrive from the server on this load. That is an error state, not zero coverage.",
  coverageUnavailableHint:
    "Reloading the page fetches them again; if it repeats, the fault is server-side.",
  effectiveConfigUnavailable:
    "The effective configuration did not arrive from the server. Do not read this as nothing being configured.",
  capabilitiesNoneActive:
    "No capability is active. Some inputs to this calculation are not connected yet, so the list may under-state what is actually available.",
  growthAllAreasDenied:
    "This user account has permission for none of the marketing areas.",
  growthAllAreasDeniedHint:
    "An account administrator can open areas on the Permissions screen.",
  autoPoliciesEmpty: "No policy records yet — they are created from human candidate approvals, once the flags are enabled behind a separate gate",
  autoFlowsEmpty: "No queues configured yet — create one in the existing Queues screen",
  shellTitle: "AI-OS",
  brainOf: "The Brain of {name}",
  layerBrain: "Brain",
  layerEmployees: "Employees",
  layerKnowledge: "Knowledge",
  layerIntelligence: "Intelligence",
  layerAutomation: "Automation",
  layerSystem: "System",
  // Redesign IA — the 11 areas (each opens as its own page with tabs)
  areaHome: "Overview",
  areaBrain: "The Brain",
  areaEmployees: "Digital employees",
  areaKnowledge: "Knowledge",
  areaIntelligence: "Intelligence",
  areaDashboard: "AI dashboard",
  areaGrowth: "Marketing",
  areaVoice: "Voice",
  areaAutomation: "Automation & governance",
  areaDiscovery: "Teaching the Brain",
  areaSetup: "Brain settings",
  // Redesign IA — tabs
  tabOverview: "Overview",
  tabReadiness: "Readiness",
  tabCoverage: "Knowledge coverage",
  tabObservations: "Observations",
  tabCalibration: "Confidence calibration",
  tabOrg: "The organization",
  tabReadyToHire: "Ready to hire",
  tabOpenPositions: "Open positions",
  tabEffectiveConfig: "Effective configuration",
  tabSources: "Sources",
  tabKnowledgeItems: "Knowledge items",
  tabCustomerMemory: "Customer memory",
  tabCandidates: "Candidates",
  tabInsights: "Insights",
  tabJourney: "Journey & customers",
  tabSegments: "Audience segments",
  tabAgentPerformance: "Agent performance",
  tabTopIssues: "Top issues",
  tabSentimentTrends: "Sentiment trends",
  tabSalesInsights: "Sales insights",
  tabCampaigns: "Campaigns",
  tabConnections: "Connections",
  tabLearningInsights: "Learning & insights",
  tabReportsTimeline: "Reports & timeline",
  tabMemoryRecommendations: "Memory & recommendations",
  tabHealthDiagnostics: "Health & diagnostics",
  tabGovernanceSettings: "Governance & settings",
  tabCalls: "Calls",
  tabCallSearch: "Search",
  tabVoiceAgents: "Agents",
  tabInCallBilling: "In-call billing",
  tabVoiceSettings: "Voice settings",
  tabFlows: "Flows",
  tabPolicies: "Policies",
  tabDecisionLog: "Decision log",
  tabAuditChain: "Audit chain",
  tabInterview: "Discovery interview",
  tabBlueprint: "Brain blueprint",
  tabBrainInScreens: "The Brain in your screens",
  tabDiagnostics: "Diagnostics",
  tabSetupWizard: "Setup wizard",
  tabPersonaTone: "Persona & tone",
  tabSystemFlags: "System flags",
  tabConfigVersions: "Versions",
  tabWorkforceConsole: "Workforce console",
  candidateSupport: "Supporting observations",
  candidateSupportHint: "How many independent observations back this suggestion",
  employeesNoneReady: "No role has passed the readiness threshold yet — teaching the missing knowledge opens the first one.",
  employeesNoneOpen: "There is no open position right now — every role the Brain knows has passed the threshold.",
  homeAreasSub: "Each area opens as its own page with tabs — no endless scrolling",
  areaBrainSub: "Readiness, knowledge coverage, observations and calibration",
  areaEmployeesSub: "14 roles across 5 departments — who is ready, who is blocked",
  areaKnowledgeSub: "What the Brain can cite, and what it is still missing",
  areaIntelligenceSub: "Evidence-backed insights, customer journey and segments",
  areaDashboardSub: "The operational numbers behind the AI",
  areaGrowthSub: "Marketing data, connections and insights in one place",
  areaVoiceSub: "The voice agent: readiness, calls and connections",
  areaAutomationSub: "What runs alone, what needs approval, and who approved",
  areaDiscoverySub: "Discovery interview, the blueprint, and the Brain in every screen",
  areaSetupSub: "Setup, persona, effective configuration, flags and versions",
  // Setup area — what each of the two classic setup screens actually sets
  setupWizardSub:
    "The Brain's first-run setup: who you are, how it speaks, where it reads data from, and when it hands off to a human",
  setupPersonaSub:
    "The persona, tone and model behind every answer — plus the greeting, the sources and the connections that feed it",
  // Home area ("Overview")
  homeReadinessSubtitle: "The numbers behind the score",
  homeAsksTitle: "What the Brain is asking of you",
  homeAsksSubtitle: "Every approval moves the Brain one step",
  homePendingApprovals: "Decisions logged as needs-approval",
  seamUnavailable: "Not available right now",
  homeShadowVerdictsSub: "Shadow-mode verdicts — recorded only. There is no approval queue for decisions; nothing acted on them.",
  homeReadyRoles: "Roles ready to hire",
  homeReadyRolesHint: "Passed the readiness threshold and waiting for you",
  homeReadyRolesBlocked: "No role can open yet: every role also requires Brain confidence at 50% or more, and no confidence has been measured — teaching knowledge alone will not open one.",
  homeBlockersSubtitle: "The top three blockers",
  homeBlockersEmpty:
    "No blocker is reported right now — once data accumulates, what's holding the Brain back appears here.",
  homeJumpTitle: "Where to go next",
  homeJumpSubtitle: "The areas that move the Brain the most",
  // "The Brain in your screens" — where the same brain is already injected
  inScreenNote:
    "The exact same Brain appears inside your day-to-day screens as a read-only card. It never writes anything, and for anyone without permission the card simply does not appear.",
  inScreenOpen: "Open screen",
  inScreenWhere: "Location",
  inScreenTicketsTitle: "The Brain on this customer (tickets)",
  inScreenTicketsBody:
    "Journey stage, buying intent, sentiment, what is missing to move forward, and the next question worth asking — each with the evidence behind it.",
  inScreenQueuesTitle: "Bot-script gaps (queues)",
  inScreenQueuesBody:
    "Gap detection over your existing queues — for example a queue with no greeting message — so you know what to close first.",
  inScreenCampaignsTitle: "Audience segments (campaigns)",
  inScreenCampaignsBody:
    "Deterministic segmentation over existing data, as a proposal only: nobody is auto-added and sending stays in the campaigns screen.",
  inScreenSettingsTitle: "Error diagnosis (settings)",
  inScreenSettingsBody:
    "What broke, why, who should fix it and the next step — every fix is a deep link to the existing screen, never an auto-fix.",
  // Areas whose engine is not built yet — honest planned-state copy
  areaPlannedTitle: "This area is not connected yet",
  areaPlannedReason:
    "The screen exists, but the engine that feeds it has not been built — so no invented number will be shown here.",
  areaPlannedHint: "The moment the engine lands, real data appears here automatically.",
  loading: "Loading…",
  loadError: "Failed to load data",
  retry: "Retry",

  readinessTitle: "Brain Readiness",
  levelLabel: "Level {level}",
  scoreLabel: "Readiness score",
  healthTitle: "Brain Health",
  healthOperational: "Configured and operational",
  healthNotOperational: "Not fully configured yet",
  healthConfidence: "Average confidence",
  healthLearningQuality: "Learning signal quality",
  issuesTitle: "What is holding the Brain back",
  noIssues: "No open issues",
  coverageTitle: "Knowledge coverage by category",
  coverageEmpty: "No knowledge coverage yet — teach the Brain via the knowledge screens",
  coverageMissingPrefix: "Missing:",
  observationsTitle: "Observations (Learning)",
  observationsEmpty:
    "Your Brain is not watching conversations yet — the observation flag (BRAIN_LEARNING_OBSERVE) is off. This is the planned state, not a fault.",
  observationsTotal: "Total observations",
  lastSeen: "Last seen",
  decisionsTitle: "Decision Log (Governance)",
  decisionsEmpty:
    "No recorded decisions yet — the governance-record flag (BRAIN_GOVERNANCE_RECORD) is off. This is the planned state.",
  governanceMetricsTitle: "Governance metrics",
  totalDecisions: "Total decisions",
  calibrationTitle: "Confidence calibration",
  calibrationNoData: "Not enough data yet",
  approvalRate: "Approval rate",
  quarantinedConfidence: "Quarantined confidence values",
  distinctHumanActors: "Distinct human contributors",
  shadowNote:
    "The Brain runs in shadow mode — it observes and advises only; no decision is enforced on real conversations.",
  discoveryBtn: "Discovery interview",
  blueprintBtn: "Brain Blueprint",
  backToBrain: "Back to Brain",

  orgChartTitle: "The Digital Organization",
  orgChartSubtitle: "A living chart — fully derived from real data; nothing is stored",
  deptSales: "Sales",
  deptSupport: "Support",
  deptMarketing: "Marketing",
  deptVoice: "Voice",
  deptOperations: "Operations",
  statusReady: "Ready to hire",
  statusLocked: "Open position",
  statusHired: "Hired",
  openPositionHint: "What the Brain still needs to open this role:",
  readinessOf: "Readiness",
  trainingMinutes: "~{min} min of teaching to unlock",
  kpiEmpty: "Performance measurement activates once employees act through the Brain",
  agentsTitle: "Live instances (AIAgents)",
  agentsEmpty: "No AI agents configured for this company yet",
  agentsTruncated: "Showing the first {shown} — the list was truncated",
  agentEnabled: "Enabled",
  agentDisabled: "Disabled",
  effectiveConfigTitle: "Effective configuration (One Brain)",
  effectiveConfigSubtitle: "Every employee inherits the same configuration — one Brain per company",
  cfgModel: "Model",
  cfgCredentialSource: "Credential source",
  cfgCredentialMasked: "Shown as a label only — secrets are never sent",
  cfgKnowledgeSource: "Knowledge source",
  cfgLanguage: "Language",
  cfgPersona: "Persona prompt",
  cfgTone: "Tone",
  cfgToneEmpty: "Not set (the engine invents no default)",
  capabilitiesTitle: "Capabilities",
  capabilitiesDeferredNote:
    "Some capabilities ({inputs}) are still partially resolved by the shadow resolver — they may be under-stated, never over-stated.",
  channelsTitle: "Connected channels",
  channelsEmpty: "No channels connected yet",
  channelConnected: "{connected} of {total} connected",

  knowledgeTitle: "Knowledge Explorer",
  knowledgeSubtitle: "Everything the Brain knows — from real sources, in the Brain's own vocabulary",
  strategyLabel: "Knowledge strategy",
  strategyAiKnowledge: "AIKnowledge store",
  strategyCompanyKnowledge: "Structured company knowledge (companyKnowledge)",
  itemsTitle: "Knowledge items",
  itemsEmpty: "The Brain has not been taught yet — add knowledge in the existing settings screens",
  itemsTruncated: "Showing {shown} items — the list was truncated on the server",
  itemActive: "Active",
  itemInactive: "Inactive",
  contactMemoryTitle: "Customer memory (ContactMemory)",
  contactMemoryNote: "Counts only — per-customer memory items stay on the contact card",
  cmSuggested: "Pending suggestions",
  cmConfirmed: "Confirmed",
  cmDismissed: "Dismissed",
  cmTotal: "Total",
  orgMemoryTitle: "Organizational memory (BrainOrgMemory)",
  orgMemoryEmpty:
    "Organizational memory is empty — BRAIN_ORG_MEMORY_ENABLED is off. This is the planned state.",
  candidatesTitle: "Brain suggestions (candidates)",
  candidatesEmpty:
    "The Brain has not suggested anything yet — the learning flags are off. Once enabled (behind a separate gate), suggestions will appear here for human approval.",
  candidatesViewOnly: "View only — approve/reject opens behind a separate owner gate (Tier-2)",
  statusPending: "Pending review",
  statusApproved: "Approved",
  statusRejected: "Rejected",
  statusExpired: "Expired",

  discoveryTitle: "Discovery interview — the Brain learns your business",
  discoveryIntro:
    "A few short questions that adapt the Brain to your business. Nothing is activated or stored — you get a proposed Blueprint at the end.",
  ephemeralBanner:
    "Proposal only — answers are not stored and no behavior changes. Actual activation always happens in the existing settings screens, with human approval.",
  stepBusinessType: "Business type",
  stepMainGoal: "Main goal",
  stepIntake: "What to collect from every inquiry",
  stepHandoff: "When to hand off to a human",
  stepChannels: "Channels",
  stepKpi: "Success metric",
  // Short labels for the two steps whose question is a full sentence — used
  // ONLY under the progress dots; the questions themselves stay as they are.
  stepIntakeShort: "Intake",
  stepHandoffShort: "Handoff",
  archLaw: "Law firm",
  archRealEstate: "Real-estate agency",
  archTravel: "Travel agency",
  archClinic: "Clinic",
  archEcommerce: "Store / e-commerce",
  archServices: "Services business",
  archOther: "Other",
  goalSales: "More sales",
  goalSupport: "Faster support",
  goalScheduling: "Appointment scheduling",
  goalCollections: "Collections & follow-up",
  handoffStrict: "Strict — any substantive question goes straight to a human",
  handoffMedium: "Medium — the bot handles scheduling; negotiation goes to a human",
  handoffSoft: "Soft — the bot accompanies to closing; a human steps in at the end",
  channelConnectedTag: "Connected",
  channelConfiguredNotConnectedTag: "Configured but not connected — check the Connections screen",
  channelNotConnectedTag: "Not connected — connect in the Connections screen",
  next: "Next",
  back: "Back",
  finish: "Show Blueprint",
  otherFieldPlaceholder: "Details…",

  blueprintTitle: "Proposed Brain Blueprint",
  blueprintHonesty:
    "Based on the process structure you declared — there is no usage data yet. Recommendations update once the Brain starts observing (behind a separate gate).",
  bpRecommended: "Recommended to hire now",
  bpRecommendedEmpty: "No employees are ready to hire yet — teach the Brain to open positions",
  bpUpcoming: "Open positions (by proximity to unlock)",
  bpTraining: "Next teaching steps",
  bpTrainingEmpty: "No active teaching recommendations",
  bpUnlocks: "Unlocks:",
  bpChannels: "Channels in the plan",
  bpProposed: "Proposed",
  bpKeep: "Approve",
  bpRemove: "Remove",
  bpLater: "Later",
  bpLegend:
    "Proposed = the Brain suggests, nothing happened · Approved = a human approved, behavior unchanged · Applied = only after a future activation gate",
  bpFromInterview: "From your interview",
  // The third state of the frozen legend (Proposed → Approved → Applied); the
  // first two already exist as bpProposed / statusApproved.
  bpApplied: "Applied",

  // ── Gate 3: Insight panels ──
  insightTicketTitle: "The Brain on this customer",
  insightTicketSubtitle: "Live intelligence picture — read-only, zero writes",
  insightCampaignsTitle: "Explained audiences from the Brain",
  insightCampaignsSubtitle: "Deterministic segmentation over existing data — proposal only",
  insightQueuesTitle: "The Brain on your flows",
  insightSettingsTitle: "Brain diagnostics — Error Copilot",
  noContextData: "No Brain data for this customer yet",
  stage_new: "New",
  stage_engaged: "Engaged",
  stage_qualifying: "Qualifying",
  stage_proposal: "Proposal",
  stage_negotiation: "Negotiation",
  stage_won: "Won",
  stage_lost: "Lost",
  stage_dormant: "Dormant",
  intent_hot: "Hot",
  intent_warm: "Warm",
  intent_cold: "Cold",
  intent_unknown: "Unknown",
  sentiment_positive: "Positive",
  sentiment_neutral: "Neutral",
  sentiment_negative: "Negative",
  sentimentNotDerived: "Not derived yet — no sentiment signal, and none is invented",
  journeyLabel: "Journey stage",
  intentLabel: "Buying intent",
  sentimentLabel: "Sentiment",
  leadScoreLabel: "Lead score",
  leadUnscored: "Not scored yet — no real signal, and none is invented",
  missingInfoTitle: "Missing information",
  nextQuestionTitle: "Next best question",
  intakeTitle: "Collected so far",
  confirmedMemoryTitle: "Confirmed memory",
  evidenceLabel: "Evidence anchors",
  blocksStagePrefix: "Blocks stage:",

  // Audience preview
  audienceScan: "Scanned the last {tickets} recently-updated tickets · {pool} contacts · {analyzed} deeply analyzed",
  audienceTruncatedNote: "The window was truncated — this is recent activity, not all data",
  audienceProposalOnly: "Proposal only — nobody is auto-added to any list; sending stays in the Campaigns screen, APPROVED templates only ({approved} approved)",
  basis_intent: "Buying intent",
  basis_stage: "Journey stage",
  "basis_ticket-status": "Ticket status",
  basis_recency: "Activity",
  basis_channel: "Channel",
  "recency_active-7d": "Active this week",
  "recency_active-30d": "Active this month",
  recency_older: "Older",
  moreNames: "and {n} more",

  // Queues insight + Flow-Builder preview skeleton
  flowGapsTitle: "Flow gaps",
  flowNoGaps: "No gaps found — every queue is configured",
  "gap_missing-greeting": "Missing greeting message",
  "gap_no-flow-options": "No flow options defined",
  "gap_no-enabled-channels": "No channels enabled for this queue",
  optionsCountLabel: "{n} options",
  flowBuilderTitle: "Flow Builder — Preview",
  flowBuilderHonesty: "The describe-to-flow generation engine does not exist yet — it arrives as a separate owner-approved, flag-gated slice. This is where a generated flow's Preview will appear, for human approval, before any import.",
  flowBuilderPlaceholder: "Describe your service in free text — opens behind a future gate",

  // Error Copilot / diagnostics
  diagTitle: "Brain diagnostics",
  diagClean: "No configuration faults found — all checks passed",
  diagCheckedAt: "Checked",
  diagEvidence: "Evidence",
  diagSeverity_critical: "Critical",
  diagSeverity_warning: "Warning",
  diagSeverity_info: "Info",
  diagRetryHelps: "Retry may help",
  diagRetryWontHelp: "Retry won't help",
  diagNeedsAdmin: "Needs an admin",
  diagNeedsSupport: "Needs support",
  diagFixBtn: "Fix in the existing screen",
  diagUncertainTitle: "Unrecognized fault",
  diagUncertainBody: "I could not determine the cause; here is exactly what was checked — no guessing.",
  diagStage: "At which stage",
  diagFault: "Whose fault",
  fault_provider: "Provider",
  fault_config: "Configuration",
  fault_system: "System",
  diagShownOf: "Showing {shown} of {total} rejected templates",
  diag_channel_disconnected_title: "Channel disconnected",
  diag_channel_disconnected_cause: "The connection exists but does not report connected — usually an expired session or an offline device.",
  diag_channel_disconnected_steps: "Open the Connections screen and reconnect.",
  diag_channel_disconnected_stage: "The channel session",
  diag_channel_in_setup_title: "Connection being set up",
  diag_channel_in_setup_cause: "The connection is mid-setup — waiting for a QR scan or for sign-in to finish; this is not a fault.",
  diag_channel_in_setup_steps: "Complete the setup in the Connections screen.",
  diag_channel_in_setup_stage: "Connection setup",
  diag_voice_invalid_token_title: "Voice provider rejected the credentials",
  diag_voice_invalid_token_cause: "The provider rejected the credentials — they may have been rotated or revoked.",
  diag_voice_invalid_token_steps: "Update the credentials in Voice Settings; retry won't help before that.",
  diag_voice_invalid_token_stage: "Provider authentication",
  diag_voice_suspended_title: "Voice connection suspended",
  diag_voice_suspended_cause: "The connection is marked suspended.",
  diag_voice_suspended_steps: "Check with support before re-enabling.",
  diag_voice_suspended_stage: "System administration",
  diag_voice_disconnected_title: "Voice connection not validated",
  diag_voice_disconnected_cause: "The connection is configured but was never validated with the provider.",
  diag_voice_disconnected_steps: "Run a connection test in Voice Settings.",
  diag_voice_disconnected_stage: "Connection validation",
  diag_calendar_error_title: "Last calendar error",
  diag_calendar_error_cause: "The last call to the calendar provider failed; the error clears on the next success.",
  diag_calendar_error_steps: "Check the connection in the Appointments screen; if it repeats — reconnect.",
  diag_calendar_error_stage: "Calendar provider call",
  diag_template_rejected_title: "Template rejected by Meta",
  diag_template_rejected_cause: "Meta rejected this template — it cannot be used for sending until fixed and re-approved.",
  diag_template_rejected_steps: "Edit and resubmit the template in Settings.",
  diag_template_rejected_stage: "Meta template review",

  // Long-running task contract (י״א.16)
  ltStarted: "Started",
  ltScope: "Scope",
  ltStage: "Current stage",
  ltCancel: "Cancel",
  ltPartial: "Partial results",
  ltNotify: "Notify on completion",
  ltCounter: "{done} of {total}",
  ltNoFake: "No real counter from the engine — showing an indeterminate indicator, not percentages",

  // Intelligence layer
  intelJourneyTitle: "Journey & handoffs",
  intelJourneyEmpty: "No observations yet — the learning flags are off; this is the planned state. Once enabled, you'll see where and why the bot handed off to a human.",
  intelDashLink: "Open the existing AI dashboard",
  intelAudienceTitle: "Audience segments (Tier-1)",
  intelCalibrationRatio: "Candidates / observations",

  // Automation layer
  autoPoliciesTitle: "Policies (Policy Registry)",
  autoPoliciesBanner: "Approved records — none is active, by design (Approved ≠ Active). No enforcement engine consumes them.",
  policyInactive: "Inactive",
  autoFlowsTitle: "Bot flows (from existing queues)",
  autoApprovalsTitle: "Approval chain",
  approvalsEmpty: "No approval records yet — they appear once candidates and human approvals exist",
  configVersionsTitle: "Config versions",
  configVersionsEmpty:
    "No versions recorded yet. The writer exists and runs on every governed turn whose config hash changed — but only while BRAIN_GOVERNANCE_RECORD is on.",

  // System layer
  sysFlagsTitle: "System flags (Brain)",
  flagsProcessNote: "Process-level flags — identical for all tenants on the shared host; changing one = a deployment, not a setting",
  flagOn: "On",
  flagOff: "Off",
  sysConfigTitle: "Effective configuration",
  sysAuditTitle: "Audit",

  // The design closes every HOME queue card with a gradient CTA and an
  // outlined "details". Ours point at two different screens on purpose:
  // coverage is where the missing knowledge gets taught, observations is
  // where the raw finding lives.
  homeAskCta: "Teach it now",
  homeAskDetails: "Details",

  // ── Brain prose authored by the BACKEND, said in this language ─────────
  // evaluateBrainEvolution.ts writes readiness.health.issues[],
  // coverage[].missing[] and requirements[].guidance as English sentences,
  // and four screens printed them verbatim — so a Hebrew tenant read English
  // on the HOME hero, on all three queue cards, under every coverage bar and
  // in the Blueprint. brainText() below matches each form and renders it from
  // here; anything it does not recognise still shows the raw sentence.
  biNoSettings: "No AI settings configured for this company",
  biAiDisabled: "AI is disabled for this company",
  biNoKnowledge: "No knowledge items taught yet",
  biNoObservations: "No learning observations recorded (learning flags may be off)",
  biNoMemories: "No confirmed contact memories yet",
  biRolesLocked: "{n} of {total} roles cannot be unlocked yet — {reasons}",
  biReasonCoverage: "knowledge under {pct}% in {cats}",
  biReasonNoConfidence: "no decision confidence recorded yet",
  biReasonThinSample: "not enough evidence for a confidence score ({n} of {min} decisions)",
  biReasonLowConfidence: "decision confidence {value} is under {min}",
  bcConfigureSettings: "Configure the AI settings for this company",
  bcConnectCrm: "Connect a CRM data source",
  bcMoreMemories: "Confirm more contact memories ({n}/{target})",
  bcMoreObservations: "More observed interactions needed ({n}/{target})",
  bcDefineTone: "Define the company tone of voice",
  bcVoiceUnavailable: "Voice-style teaching is not yet available",
  bcAddKnowledge: "Add knowledge items in category {cat} ({n}/{target})",
  bgTeachCategory: "Teach the Brain about {cat} to at least {pct}% coverage",
  bgConfidenceGrows: "Brain decision confidence grows as governed decisions accumulate",
  bgThinEvidence: "Not enough evidence yet — {n} of {min} recorded decisions needed before a confidence score is meaningful",

  // The shadow-mode banner is a `note` card in the design, and a design note
  // card always has a title. We had the sentence but no name for it, so the
  // card rendered headless in a grid where every sibling is titled.
  shadowNoteTitle: "Shadow mode",

  // Data-First: an absent dataset renders an honest empty state saying WHAT is
  // missing and WHY. The flags card had no such copy, so a failed read drew a
  // card with a head and a footnote and nothing between them.
  flagsEmpty: "No process flag was returned. These are host-level flags — an empty list means the read failed, not that there are none.",

  // The roadmap counts card had no name — it was the one untitled cell in a
  // grid where every sibling is titled, because nothing in the dictionary
  // named the summary itself (only the three things it counts).
  bpRoadmapTitle: "The plan at a glance",
};

const ROLE_LABELS = {
  he: {
    sales: "נציג מכירות",
    collections: "נציג גבייה",
    support: "נציג תמיכה",
    receptionist: "פקיד/ת קבלה",
    appointment: "מתאם פגישות",
    marketing: "משווק",
    "campaign-manager": "מנהל קמפיינים",
    "crm-assistant": "עוזר CRM",
    "voice-agent": "סוכן קולי",
    "qa-manager": "מנהל איכות",
    "knowledge-manager": "מנהל ידע",
    "reporting-analyst": "אנליסט דוחות",
    supervisor: "מפקח משמרת",
    "executive-assistant": "עוזר אישי למנהל",
  },
  en: {
    sales: "Sales Rep",
    collections: "Collections Rep",
    support: "Support Rep",
    receptionist: "Receptionist",
    appointment: "Appointment Setter",
    marketing: "Marketer",
    "campaign-manager": "Campaign Manager",
    "crm-assistant": "CRM Assistant",
    "voice-agent": "Voice Agent",
    "qa-manager": "QA Manager",
    "knowledge-manager": "Knowledge Manager",
    "reporting-analyst": "Reporting Analyst",
    supervisor: "Supervisor",
    "executive-assistant": "Executive Assistant",
  },
};

const CATEGORY_LABELS = {
  he: {
    products: "מוצרים",
    services: "שירותים",
    "company-policies": "מדיניות החברה",
    documents: "מסמכים",
    "crm-understanding": "הבנת ה-CRM",
    "customer-history": "היסטוריית לקוחות",
    "conversation-history": "היסטוריית שיחות",
    "writing-style": "סגנון כתיבה",
    "voice-style": "סגנון דיבור",
    "sales-process": "תהליך המכירה",
    "support-process": "תהליך התמיכה",
    "payment-policies": "מדיניות תשלומים",
    "automation-rules": "כללי אוטומציה",
  },
  en: {
    products: "Products",
    services: "Services",
    "company-policies": "Company Policies",
    documents: "Documents",
    "crm-understanding": "CRM Understanding",
    "customer-history": "Customer History",
    "conversation-history": "Conversation History",
    "writing-style": "Writing Style",
    "voice-style": "Voice Style",
    "sales-process": "Sales Process",
    "support-process": "Support Process",
    "payment-policies": "Payment Policies",
    "automation-rules": "Automation Rules",
  },
};

/** Interview intake-field labels (content, not contract — he/en staged rule). */
const INTAKE_LABELS = {
  he: {
    "legal-domain": "תחום משפטי",
    urgency: "דחיפות",
    "opposing-party": "צד שכנגד (בדיקת ניגוד עניינים)",
    "buy-sell-rent": "קנייה / מכירה / שכירות",
    "property-type": "סוג נכס",
    areas: "אזורים",
    "mortgage-approved": "אישור משכנתא",
    destination: "יעד",
    dates: "תאריכים",
    budget: "תקציב",
    travellers: "מספר נוסעים",
    "service-type": "סוג שירות",
    "preferred-times": "זמנים מועדפים",
    "product-interest": "מוצר מבוקש",
    "order-number": "מספר הזמנה",
  },
  en: {
    "legal-domain": "Legal domain",
    urgency: "Urgency",
    "opposing-party": "Opposing party (conflict check)",
    "buy-sell-rent": "Buy / sell / rent",
    "property-type": "Property type",
    areas: "Areas",
    "mortgage-approved": "Mortgage approval",
    destination: "Destination",
    dates: "Dates",
    budget: "Budget",
    travellers: "Travellers",
    "service-type": "Service type",
    "preferred-times": "Preferred times",
    "product-interest": "Product interest",
    "order-number": "Order number",
  },
};

/** Interview KPI labels — the mandated vertical KPIs, human-named (י״א.1). */
const KPI_LABELS = {
  he: {
    "time-to-first-consult": "זמן עד ייעוץ ראשון",
    "tours-scheduled": "סיורים שנקבעו",
    "offers-sent": "הצעות שנשלחו",
    "appointments-set": "פגישות שנקבעו",
    "orders-assisted": "הזמנות שטופלו",
    "quotes-sent": "הצעות מחיר שנשלחו",
    "conversations-resolved": "שיחות שנפתרו",
  },
  en: {
    "time-to-first-consult": "Time to first consult",
    "tours-scheduled": "Tours scheduled",
    "offers-sent": "Offers sent",
    "appointments-set": "Appointments set",
    "orders-assisted": "Orders assisted",
    "quotes-sent": "Quotes sent",
    "conversations-resolved": "Conversations resolved",
  },
};

/** EmployeeUnlockRequirement.kind labels (8-member union, evolution.ts). */
const REQ_KIND_LABELS = {
  he: {
    knowledge: "ידע",
    capability: "יכולת",
    policy: "מדיניות",
    confidence: "ביטחון",
    tool: "כלי",
    channel: "ערוץ",
    permission: "הרשאה",
    governance: "בקרה",
  },
  en: {
    knowledge: "Knowledge",
    capability: "Capability",
    policy: "Policy",
    confidence: "Confidence",
    tool: "Tool",
    channel: "Channel",
    permission: "Permission",
    governance: "Governance",
  },
};

const HANDOFF_LABEL_KEYS = {
  strict: "handoffStrict",
  medium: "handoffMedium",
  soft: "handoffSoft",
};

// Gate 4: full five-language content (R3 staged-content completion)
const ar = {
  candidateContributors: "مساهمون مستقلون",
  candidatesPageNote: "يتم عرض أول {shown} عبر جميع الحالات — هذه صفحة وليست الطابور كاملاً.",
  // ── knowledge · what the resolver admitted, and what disagreed ──────
  provSourcesTitle: "مصادر المعرفة",
  provSourcesSub: "ما يصل إلى الإجابات وما يحجبه البوّابة",
  provGateNote: "أي معلومة بلا مصدر صالح أو انتهت حداثتها لا تصل إلى الإجابة — لهذا لا يختلق الدماغ.",
  provNoSourceRead: "لم تتم قراءة أي مصدر — وهذا ليس كعدم وجود مشكلة. يقرأ المحمّل من AIKnowledge، وهو مخزن معرفة استراتيجية godeal-pipeline وحدها.",
  provNoSourceReadHint: "معرفة هذا المستأجر موجودة في companyKnowledge، وهذه الشاشة لا تقرأ منه بعد.",
  provAdmitted: "مقبولة",
  provRejected: "مرفوضة",
  provSourceValid: "صالح",
  provSourceBlocked: "محجوب",
  provSourceEmpty: "فارغ",
  provConflictsTitle: "التناقضات المكتشفة",
  provConflictsSub: "مصدران وإجابتان مختلفتان — والحسم الذي تم اختياره",
  provNoConflicts: "لم يُكتشف أي تناقض بين المصادر التي تمت قراءتها.",
  provWinner: "حُسم لصالح",
  provLosers: "مصادر مرفوضة",
  provDecide: "محسوم",
  intelLoadWindowNote: "تُؤخذ هذه الأعداد من أحدث {limit} صفاً على كل جانب، فهي نافذة وليست إجمالياً؛ والمستأجر الذي تجاوز الحد يقرأ {limit} بالضبط.",
  intelJourneyEmptyNoCause: "لم تُسجَّل أي ملاحظات بعد. تقرأ هذه الشاشة الأعداد فقط ولا يمكنها بيان السبب — حالة الرايات موجودة في منطقة إعدادات الدماغ.",
  contactMemoryWhyItMatters: "الذاكرة المؤكدة لجهة الاتصال تغذّي فئة تاريخ العملاء، وهي التي تحجب اليوم دورين: التحصيل ومساعد إدارة العلاقات.",
  // ── design-file conformance · departments + candidates footnote ─────
  deptSummaryTitle: "الأقسام",
  deptSummarySub: "متوسط الجاهزية لكل قسم",
  deptScoredOf: "المتوسط على {scored} من {roles} أدوار",
  deptUnplaced: "{count} دور لا يعرفه هذا المخطط: {roles} — محسوبة ضمن الإجماليات أعلاه",
  deptReady: "جاهز",
  deptBlocked: "محجوب",
  candidatesApprovalNote: "الموافقة البشرية وحدها تُرقّي المرشّح إلى الذاكرة المؤسسية. الموافقة ليست تفعيلاً.",
  // ── system · the audit pair discloses its window ────────────────────
  auditWindowApprovals: "يتم عرض أحدث {shown} موافقات — نافذة وليست السجل الكامل: الطلب محدود بـ {requested} صفاً، لذا فإن الإجمالي غير معروف في هذه الشاشة.",
  auditWindowVersions: "يتم عرض أحدث {shown} إصدارات من أصل {total}.",
  auditWindowDecisions: "يتم عرض أحدث {shown} قرارات — نافذة وليست السجل الكامل: الطلب محدود بـ {requested} صفاً، لذا فإن الإجمالي غير معروف في هذه الشاشة.",
  // ── coverage · eight categories read a store most tenants never use ──
  coverageUnteachableCaveat:
    "{unteachable} من هذه الفئات لا توجد بعد وسيلة لتعليمها — تُعرض بتغطيتها الحقيقية لكنها لا تُحتسب في النتيجة، التي تُحسب كمتوسط على {scored} من {total} فئة يمكن تعليمها فعلياً.",
  // ── knowledge · the clamp says how much was clamped ─────────────────
  contentChars: "المحتوى",
  // ── employees · published companion fields, now read ─────────────────
  promptChars: "التعليمة: {chars} حرفاً",
  deferredInput_connectedChannels: "القنوات المتصلة",
  deferredInput_hasActiveLearningAssets: "أصول التعلّم النشطة",
  // ── brain · calibration, per the design file ─────────────────────────
  calibrationKvTitle: "معايرة الثقة",
  calibrationKvSub: "هل ثقة الدماغ مبررة",
  calibrationOverallApproval: "نسبة الموافقة الإجمالية",
  calibrationOverallApprovalHint: "الموافق عليها من بين ما تم البت فيه (موافقة أو رفض) — المعلّقة والمنتهية لا تُحتسب",
  calibrationHighConfidence: "ثقة عالية (≥0.8)",
  calibrationHighConfidenceHint: "لا مصدر بعد — يعيد تقرير المعايرة أعداداً حسب النوع، دون تفصيل حسب مستوى الثقة",
  calibrationFloorNote: "تظهر النسبة المئوية لنوع ما فقط بعد {floor} قراراً من ذلك النوع؛ دون ذلك تُعرض الأعداد نفسها.",
  confidenceDistributionTitle: "توزيع الثقة",
  confidenceDistributionNoFeed: "لا شيء يغذّي هذا الرسم — تُقاس المعايرة اليوم حسب نوع المرشّح، لا حسب مستوى الثقة.",
  confidenceDistributionHint: "يتطلب تفصيلاً حسب مستوى الثقة على الخادم؛ حتى ذلك الحين تبقى هذه البطاقة فارغة عن قصد.",
  // ── brain · observation kinds said in words, per the design file ──────
  obsKind_memory_confirm: "ذاكرة مؤكدة",
  obsKind_memory_edit: "ذاكرة معدّلة",
  obsKind_memory_dismiss: "ذاكرة مرفوضة",
  obsKind_ai_handoff: "تحويل إلى موظف بشري",
  obsKind_ai_disable: "إيقاف الذكاء الاصطناعي",
  obsKind_execution_outcome: "نتيجة التنفيذ",
  obsKind_appointment_outcome: "نتيجة الموعد",
  obsKind_payment_outcome: "نتيجة الدفع",
  obsKind_cancellation: "إلغاء",
  obsKind_human_correction: "تصحيح بشري",
  obsKind_business_outcome: "نتيجة تجارية",
  obsKind_evaluation: "تقييم جودة الإجابة",
  observationsScopeAllTime: "منذ البداية — وليست نافذة 30 يوماً",
  observationsRecentTitle: "أحدث الملاحظات",
  observationsRecentSub: "ما رآه الدماغ، بالترتيب",
  // ── brain · coverage split into all / needs-you, per the design file ──
  coverageAllTitle: "تغطية المعرفة حسب الفئة",
  coverageAllSub: "تُقاس كل فئة مقابل عتبة 60%",
  coverageNeedsYouTitle: "فئات تحتاج إليك",
  coverageNeedsYouSub: "تلك التي تحت العتبة — هي ما يعيق المستوى التالي",
  // ── brain · the health card's four rows, per the design file ───────
  healthLevel: "المستوى",
  healthOpenIssues: "المشكلات المفتوحة",
  healthNoOpenIssues: "لا شيء",
  // ── brain · the health card explains its own dash ──────────────────
  healthConfidenceCaveat: "يبقى \"متوسط الثقة\" على \"—\" إلى أن تتراكم قرارات محكومة. نحن لا نختلق رقماً.",
  // ── D1 · no area rollups is not the same as no data ────────────────
  stNotWiredTitle: "لا يوجد مصدر بيانات لهذه المنطقة",
  stNotWiredBody: "يعيد الخادم الحالة والاتصالات فقط، دون بيانات المناطق — أي أن هذه ليست حالة «لا توجد بيانات بعد» بل لا يوجد ما يُقرأ منه. هذه حقيقة عن النظام لا عن نشاطكم.",
  // ── F3.1 · Growth tabs in the shell strip ──────────────────────────
  gtabOverview: "نظرة عامة",
  gtabCampaigns: "الحملات",
  gtabConnections: "الاتصالات",
  gtabLearning: "التعلّم والرؤى",
  gtabReports: "التقارير والخط الزمني",
  gtabMemory: "الذاكرة والتوصيات",
  gtabHealth: "الصحة والتشخيص",
  gtabGovernance: "الحوكمة والإعدادات",
  gtabCampaignsEmpty: "أداء الحملات غير موصول بمساحة العمل هذه بعد. الحملات نفسها موجودة في النظام لكنها ليست ضمن البيانات التي تحمّلها هذه الشاشة.",
  gtabCampaignsEmptyHint: "شاشة الحملات الحالية تعرضها إلى أن يكتمل هذا الربط.",
  // ── F3.2 · honest empty states ───────────────────────────────────────
  // Every reason below is the cause traced in the backend, not a restatement
  // of the title. Emptiness without a cause reads as "all is well" when it
  // usually means "nothing is connected", and three of the strings these
  // replace said something stronger and untrue.
  queuesNoneAtAll:
    "لا يوجد أي قائمة انتظار معرّفة، لذلك لم يكن هناك ما يُفحص — هذه ليست نتيجة فحص ناجح.",
  queuesNoneAtAllHint:
    "إنشاء أول قائمة انتظار في شاشة قوائم الانتظار يُفعّل هذا الفحص.",
  channelsNoneConfigured:
    "لم يتم إعداد أي قناة — لا WhatsApp ولا Meta ولا خط صوتي. هذا يختلف عن قناة مُعدّة وغير متصلة.",
  channelsNoneConfiguredHint:
    "ربط قناة في شاشة الاتصالات يُنشئ سطراً هنا، حتى قبل التحقق من الاتصال.",
  diagScanScope:
    "تم فحص التقويمات النشطة فقط وقوالب WhatsApp المرفوضة. الإعداد المعطّل ليس خللاً ولذلك لا يُحتسب هنا.",
  orgMemoryEmptyReal:
    "لم تتم الموافقة على أي درس بعد. الذاكرة التنظيمية تُكتب بموافقة بشرية فقط، لا من المحادثات نفسها.",
  orgMemoryEmptyRealHint:
    "تتم الموافقة في خطوة منفصلة محكومة بصلاحية المالك — لا في هذا التبويب، فهو للقراءة فقط.",
  candidatesEmptyReal:
    "لا يوجد مرشحون في الانتظار — وليس بسبب قلة المحادثات. عملية الاستدلال التي تُنشئ المرشحين غير مجدولة للتشغيل في أي مكان، لذا لن ينتج أي حجم من المحادثات مرشحاً واحداً.",
  bpUpcomingEmptyReal:
    "لا توجد أدوار مقفلة — كل دور معرّف يستوفي بالفعل شروط فتحه.",
  bpTrainingFullyTaught:
    "لا توجد خطوة تعليم تالية لأن جميع مجالات المعرفة مغطاة بالكامل — هذا نجاح وليس نقصاً.",
  calibrationZeroRows:
    "لم تُسجَّل أي ملاحظات أو مرشحين لهذا الحساب. العدّاد يحصي سجلات فعلية، وليس عتبة تنتظر التجاوز.",
  calibrationZeroRowsHint:
    "تظهر السجلات فقط عندما يكون تعلّم الدماغ مفعّلاً. وإذا كان متوقفاً تبقى هذه الشاشة فارغة، وهذا صحيح.",
  audienceNoTickets:
    "تُشتق الشرائح من التذاكر ضمن النافذة الزمنية المفحوصة، ولا توجد أي تذكرة فيها.",
  audienceNoTicketsHint:
    "أول تذكرة داخل النافذة تُنتج أول شريحة.",
  contactNotResolved:
    "لم يتم العثور على هوية موحّدة لجهة الاتصال هذه. تُرجَع النتيجة نفسها عندما تعود جهة الاتصال إلى حساب آخر.",
  coverageUnavailable:
    "لم تصل بيانات التغطية من الخادم في هذا التحميل. هذه حالة خطأ وليست تغطية صفرية.",
  coverageUnavailableHint:
    "إعادة تحميل الصفحة تجلبها مجدداً؛ وإذا تكرر ذلك فالخلل من جهة الخادم.",
  effectiveConfigUnavailable:
    "لم تصل الإعدادات الفعلية من الخادم. لا يعني ذلك أن شيئاً لم يُضبط.",
  capabilitiesNoneActive:
    "لا توجد قدرة نشطة. بعض مدخلات هذا الحساب غير موصولة بعد، لذا قد تكون القائمة أقل من الواقع.",
  growthAllAreasDenied:
    "هذا الحساب لا يملك صلاحية لأي من مناطق التسويق.",
  growthAllAreasDeniedHint:
    "يمكن لمسؤول الحساب فتح المناطق من شاشة الصلاحيات.",
  autoPoliciesEmpty: "لا توجد سجلات سياسات بعد — تُنشأ من موافقات بشرية على المرشّحات، عند تفعيل العلامات خلف بوابة منفصلة",
  autoFlowsEmpty: "لا توجد قوائم انتظار مهيأة بعد — أنشئ واحدة في شاشة قوائم الانتظار القائمة",
  // Shell
  shellTitle: "AI-OS",
  brainOf: "دماغ {name}",
  layerBrain: "الدماغ",
  layerEmployees: "الموظفون",
  layerKnowledge: "المعرفة",
  layerIntelligence: "الذكاء",
  layerAutomation: "الأتمتة",
  layerSystem: "النظام",
  loading: "جارٍ التحميل…",
  loadError: "فشل تحميل البيانات",
  retry: "إعادة المحاولة",

  // Brain layer (MRI)
  readinessTitle: "جاهزية الدماغ",
  levelLabel: "المستوى {level}",
  scoreLabel: "درجة الجاهزية",
  healthTitle: "صحة الدماغ",
  healthOperational: "مُهيَّأ وقيد التشغيل",
  healthNotOperational: "لم يُهيَّأ بالكامل بعد",
  healthConfidence: "متوسط الثقة",
  healthLearningQuality: "جودة إشارة التعلّم",
  issuesTitle: "ما الذي يعيق الدماغ",
  noIssues: "لا توجد مشكلات مفتوحة",
  coverageTitle: "تغطية المعرفة حسب الفئة",
  coverageEmpty: "لا توجد تغطية معرفية بعد — علِّم الدماغ عبر شاشات المعرفة",
  coverageMissingPrefix: "الناقص:",
  observationsTitle: "الملاحظات (التعلّم)",
  observationsEmpty:
    "دماغك لا يراقب المحادثات بعد — علامة المراقبة (BRAIN_LEARNING_OBSERVE) مُطفأة. هذه هي الحالة المخطَّط لها، وليست عطلًا.",
  observationsTotal: "إجمالي الملاحظات",
  lastSeen: "آخر ظهور",
  decisionsTitle: "سجل القرارات (الحوكمة)",
  decisionsEmpty:
    "لا توجد قرارات مسجَّلة بعد — علامة تسجيل الحوكمة (BRAIN_GOVERNANCE_RECORD) مُطفأة. هذه هي الحالة المخطَّط لها.",
  governanceMetricsTitle: "مؤشرات الحوكمة",
  totalDecisions: "إجمالي القرارات",
  calibrationTitle: "معايرة الثقة",
  calibrationNoData: "لا توجد بيانات كافية بعد",
  approvalRate: "معدل الموافقة",
  quarantinedConfidence: "قيم الثقة المعزولة في الحجر",
  distinctHumanActors: "مساهمون بشريون مختلفون",
  shadowNote:
    "يعمل الدماغ في وضع الظل (shadow) — يراقب ويقدّم المشورة فقط؛ ولا يُفرض أي قرار على محادثات حقيقية.",
  discoveryBtn: "مقابلة التعارف (Discovery)",
  blueprintBtn: "مخطط الدماغ (Blueprint)",
  backToBrain: "العودة إلى الدماغ",

  // Employees layer
  orgChartTitle: "المؤسسة الرقمية",
  orgChartSubtitle: "مخطط حي — مشتق بالكامل من بيانات حقيقية؛ لا شيء يُخزَّن",
  deptSales: "المبيعات",
  deptSupport: "الدعم",
  deptMarketing: "التسويق",
  deptVoice: "الصوت",
  deptOperations: "العمليات",
  statusReady: "جاهز للتوظيف",
  statusLocked: "وظيفة شاغرة",
  statusHired: "مُوظَّف",
  openPositionHint: "ما الذي ما يزال الدماغ بحاجة إليه لفتح هذا الدور:",
  readinessOf: "الجاهزية",
  trainingMinutes: "نحو {min} دقيقة من التعليم لفتح الوظيفة",
  kpiEmpty: "يُفعَّل قياس الأداء عندما يبدأ الموظفون بالعمل عبر الدماغ",
  agentsTitle: "المثيلات الحية (AIAgents)",
  agentsEmpty: "لم يتم إعداد أي وكيل ذكاء اصطناعي لهذه الشركة بعد",
  agentsTruncated: "يتم عرض أول {shown} — تم اقتطاع القائمة",
  agentEnabled: "مفعّل",
  agentDisabled: "معطَّل",
  effectiveConfigTitle: "التهيئة الفعلية (One Brain)",
  effectiveConfigSubtitle: "كل موظف يرث التهيئة نفسها — دماغ واحد لكل شركة",
  cfgModel: "النموذج",
  cfgCredentialSource: "مصدر بيانات الاعتماد",
  cfgCredentialMasked: "يُعرض كتسمية فقط — الأسرار لا تُرسل أبدًا",
  cfgKnowledgeSource: "مصدر المعرفة",
  cfgLanguage: "اللغة",
  cfgPersona: "موجّه الشخصية (Persona)",
  cfgTone: "النبرة",
  cfgToneEmpty: "غير محدَّدة (المحرك لا يخترع قيمة افتراضية)",
  capabilitiesTitle: "القدرات",
  capabilitiesDeferredNote:
    "بعض القدرات ({inputs}) ما تزال تُحسم جزئيًا بواسطة محلِّل الظل (shadow resolver) — قد تُعرض بأقل من قيمتها الحقيقية، ولا تُعرض بأكثر منها أبدًا.",
  channelsTitle: "القنوات المتصلة",
  channelsEmpty: "لا توجد قنوات متصلة بعد",
  channelConnected: "{connected} من أصل {total} متصلة",

  // Knowledge layer
  knowledgeTitle: "Knowledge Explorer",
  knowledgeSubtitle: "كل ما يعرفه الدماغ — من مصادر حقيقية، وبمفردات الدماغ نفسه",
  strategyLabel: "استراتيجية المعرفة",
  strategyAiKnowledge: "مخزن AIKnowledge",
  strategyCompanyKnowledge: "معرفة شركة مهيكلة (companyKnowledge)",
  itemsTitle: "عناصر المعرفة",
  itemsEmpty: "لم يتلقَّ الدماغ أي تعليم بعد — أضف المعرفة في شاشات الإعدادات القائمة",
  itemsTruncated: "يتم عرض {shown} من العناصر — تم اقتطاع القائمة على الخادم",
  itemActive: "نشط",
  itemInactive: "غير نشط",
  contactMemoryTitle: "ذاكرة العملاء (ContactMemory)",
  contactMemoryNote:
    "أعداد فقط — عناصر الذاكرة الخاصة بكل عميل تبقى في بطاقة جهة الاتصال",
  cmSuggested: "اقتراحات معلَّقة",
  cmConfirmed: "مؤكَّدة",
  cmDismissed: "مستبعَدة",
  cmTotal: "الإجمالي",
  orgMemoryTitle: "الذاكرة المؤسسية (BrainOrgMemory)",
  orgMemoryEmpty:
    "الذاكرة المؤسسية فارغة — العلامة BRAIN_ORG_MEMORY_ENABLED مُطفأة. هذه هي الحالة المخطَّط لها.",
  candidatesTitle: "اقتراحات الدماغ (المرشَّحات)",
  candidatesEmpty:
    "لم يقترح الدماغ شيئًا بعد — علامات التعلّم مُطفأة. عند تفعيلها (خلف بوابة منفصلة)، ستظهر الاقتراحات هنا للموافقة البشرية.",
  candidatesViewOnly:
    "عرض فقط — الموافقة/الرفض تُفتح خلف بوابة مالك منفصلة (Tier-2)",
  statusPending: "بانتظار المراجعة",
  statusApproved: "مُوافَق عليه",
  statusRejected: "مرفوض",
  statusExpired: "منتهي الصلاحية",

  // Discovery
  discoveryTitle: "مقابلة التعارف (Discovery) — الدماغ يتعرّف على عملك",
  discoveryIntro:
    "بضعة أسئلة قصيرة تُكيِّف الدماغ مع عملك. لا يتم تفعيل أي شيء ولا تخزينه — تحصل في النهاية على مخطط مقترح (Blueprint) فقط.",
  ephemeralBanner:
    "اقتراح فقط — الإجابات لا تُخزَّن ولا يتغيّر أي سلوك. التفعيل الفعلي يتم دائمًا في شاشات الإعدادات القائمة، وبموافقة بشرية.",
  stepBusinessType: "نوع النشاط التجاري",
  stepMainGoal: "الهدف الرئيسي",
  stepIntake: "ما الذي يجب جمعه من كل استفسار",
  stepHandoff: "متى يتم التحويل إلى إنسان",
  stepChannels: "القنوات",
  stepKpi: "مقياس النجاح",
  archLaw: "مكتب محاماة",
  archRealEstate: "وكالة عقارات",
  archTravel: "وكالة سفر",
  archClinic: "عيادة",
  archEcommerce: "متجر / تجارة إلكترونية",
  archServices: "نشاط خدمات",
  archOther: "غير ذلك",
  goalSales: "مزيد من المبيعات",
  goalSupport: "دعم أسرع",
  goalScheduling: "تحديد المواعيد",
  goalCollections: "التحصيل والمتابعة",
  handoffStrict: "صارم — أي سؤال جوهري يذهب فورًا إلى إنسان",
  handoffMedium: "متوسط — البوت يتولى تحديد المواعيد؛ والتفاوض يذهب إلى إنسان",
  handoffSoft: "مرن — البوت يرافق حتى الإغلاق؛ ويتدخل الإنسان في النهاية فقط",
  channelConnectedTag: "متصلة",
  channelConfiguredNotConnectedTag: "مهيَّأة لكن غير متصلة — تحقّق من شاشة الاتصالات",
  channelNotConnectedTag: "غير متصلة — قم بالربط من شاشة الاتصالات",
  next: "التالي",
  back: "رجوع",
  finish: "عرض الـ Blueprint",
  otherFieldPlaceholder: "التفاصيل…",

  // Blueprint
  blueprintTitle: "مخطط الدماغ المقترح (Blueprint)",
  blueprintHonesty:
    "استنادًا إلى هيكل العمل الذي صرّحت به — لا توجد بيانات استخدام بعد. ستتحدَّث التوصيات عندما يبدأ الدماغ بالمراقبة (خلف بوابة منفصلة).",
  bpRecommended: "يُوصى بتوظيفهم الآن",
  bpRecommendedEmpty: "لا يوجد موظفون جاهزون للتوظيف بعد — علِّم الدماغ لفتح الوظائف",
  bpUpcoming: "وظائف شاغرة (بحسب القرب من الفتح)",
  bpTraining: "خطوات التعليم التالية",
  bpTrainingEmpty: "لا توجد توصيات تعليم نشطة",
  bpUnlocks: "يفتح:",
  bpChannels: "القنوات في الخطة",
  bpProposed: "مقترح",
  bpKeep: "موافقة",
  bpRemove: "إزالة",
  bpLater: "لاحقًا",
  bpLegend:
    "مقترح = الدماغ يقترح ولم يحدث شيء · موافَق عليه = إنسان وافق والسلوك لم يتغيّر · مطبَّق = فقط بعد بوابة تفعيل مستقبلية",
  bpFromInterview: "من مقابلتك",

  // ── Gate 3: Insight panels ──
  insightTicketTitle: "رؤية الدماغ لهذا العميل",
  insightTicketSubtitle: "صورة ذكاء حيّة — للقراءة فقط، صفر عمليات كتابة",
  insightCampaignsTitle: "شرائح جمهور مُفسَّرة من الدماغ",
  insightCampaignsSubtitle: "تقسيم حتمي على بيانات قائمة — اقتراح فقط",
  insightQueuesTitle: "رؤية الدماغ لمساراتك",
  insightSettingsTitle: "تشخيصات الدماغ — Error Copilot",
  noContextData: "لا توجد بيانات من الدماغ عن هذا العميل بعد",
  stage_new: "جديد",
  stage_engaged: "متفاعل",
  stage_qualifying: "قيد التأهيل",
  stage_proposal: "تقديم العرض",
  stage_negotiation: "قيد التفاوض",
  stage_won: "مغلق بنجاح",
  stage_lost: "مفقود",
  stage_dormant: "خامل",
  intent_hot: "ساخنة",
  intent_warm: "فاترة",
  intent_cold: "باردة",
  intent_unknown: "غير معروفة",
  sentiment_positive: "إيجابي",
  sentiment_neutral: "محايد",
  sentiment_negative: "سلبي",
  journeyLabel: "مرحلة الرحلة",
  intentLabel: "نية الشراء",
  sentimentLabel: "المشاعر",
  leadScoreLabel: "درجة العميل المحتمل",
  leadUnscored: "لم تُحتسب درجة بعد — لا توجد إشارة حقيقية، ولا يتم اختلاق أي إشارة",
  missingInfoTitle: "معلومات ناقصة",
  nextQuestionTitle: "أفضل سؤال تالٍ",
  intakeTitle: "ما جُمع حتى الآن",
  confirmedMemoryTitle: "الذاكرة المؤكَّدة",
  evidenceLabel: "مرتكزات الأدلة",
  blocksStagePrefix: "يعيق المرحلة:",

  // Audience preview
  audienceScan: "تم فحص آخر {tickets} من التذاكر المحدَّثة مؤخرًا · {pool} من جهات الاتصال · {analyzed} خضعت لتحليل معمّق",
  audienceTruncatedNote: "تم اقتطاع النافذة — هذا هو النشاط الأخير فقط، وليس كل البيانات",
  audienceProposalOnly: "اقتراح فقط — لا يُضاف أحد تلقائيًا إلى أي قائمة؛ الإرسال يبقى في شاشة الحملات، وبالقوالب المعتمَدة (APPROVED) فقط ({approved} معتمَدة)",
  basis_intent: "نية الشراء",
  basis_stage: "مرحلة الرحلة",
  "basis_ticket-status": "حالة التذكرة",
  basis_recency: "النشاط",
  basis_channel: "القناة",
  "recency_active-7d": "نشط هذا الأسبوع",
  "recency_active-30d": "نشط هذا الشهر",
  recency_older: "أقدم",
  moreNames: "و{n} آخرين",

  // Queues insight + Flow-Builder preview skeleton
  flowGapsTitle: "فجوات المسارات",
  flowNoGaps: "لم يُعثر على فجوات — كل قوائم الانتظار مهيَّأة",
  "gap_missing-greeting": "رسالة الترحيب مفقودة",
  "gap_no-flow-options": "لا توجد خيارات مسار محدَّدة",
  "gap_no-enabled-channels": "لا توجد قنوات مفعَّلة لقائمة الانتظار هذه",
  optionsCountLabel: "{n} خيارات",
  flowBuilderTitle: "Flow Builder — معاينة",
  flowBuilderHonesty: "محرك توليد المسار من الوصف الحر غير موجود بعد — سيصل كشريحة منفصلة بموافقة المالك وخلف علامة تفعيل. هنا ستظهر معاينة المسار المولَّد، للموافقة البشرية، قبل أي استيراد.",
  flowBuilderPlaceholder: "صِف خدمتك بنص حر — يُفتح خلف بوابة مستقبلية",

  // Error Copilot / diagnostics
  diagTitle: "تشخيصات الدماغ",
  diagClean: "لم يُعثر على أعطال في التهيئة — كل الفحوصات نجحت",
  diagCheckedAt: "تم الفحص",
  diagEvidence: "الدليل",
  diagSeverity_critical: "حرج",
  diagSeverity_warning: "تحذير",
  diagSeverity_info: "معلومات",
  diagRetryHelps: "قد تفيد إعادة المحاولة",
  diagRetryWontHelp: "إعادة المحاولة لن تفيد",
  diagNeedsAdmin: "يتطلب صلاحيات مسؤول",
  diagNeedsSupport: "يتطلب الدعم الفني",
  diagFixBtn: "الإصلاح في الشاشة القائمة",
  diagUncertainTitle: "عطل غير معروف",
  diagUncertainBody: "لم أتمكن من تحديد السبب؛ يُعرض هنا بالضبط ما تم فحصه — من دون تخمين.",
  diagStage: "في أي مرحلة",
  diagFault: "مَن المسؤول",
  fault_provider: "المزوّد",
  fault_config: "التهيئة",
  fault_system: "النظام",
  diagShownOf: "يتم عرض {shown} من أصل {total} من القوالب المرفوضة",
  diag_channel_disconnected_title: "القناة غير متصلة",
  diag_channel_disconnected_cause: "الاتصال موجود لكنه لا يُبلِغ بأنه متصل — غالبًا بسبب جلسة منتهية الصلاحية أو جهاز غير متصل بالإنترنت.",
  diag_channel_disconnected_steps: "افتح شاشة الاتصالات وأعد الاتصال.",
  diag_channel_disconnected_stage: "جلسة القناة",
  diag_channel_in_setup_title: "اتصال قيد الإعداد",
  diag_channel_in_setup_cause: "الاتصال في منتصف الإعداد — في انتظار مسح رمز QR أو اكتمال تسجيل الدخول؛ هذا ليس عطلًا.",
  diag_channel_in_setup_steps: "أكمل الإعداد في شاشة الاتصالات.",
  diag_channel_in_setup_stage: "إعداد الاتصال",
  diag_voice_invalid_token_title: "رفض مزوّد الصوت بيانات الاعتماد",
  diag_voice_invalid_token_cause: "رفض المزوّد بيانات الاعتماد — ربما جرى تدويرها أو إلغاؤها.",
  diag_voice_invalid_token_steps: "حدِّث بيانات الاعتماد في إعدادات الصوت؛ إعادة المحاولة لن تفيد قبل ذلك.",
  diag_voice_invalid_token_stage: "المصادقة لدى المزوّد",
  diag_voice_suspended_title: "اتصال الصوت موقوف",
  diag_voice_suspended_cause: "الاتصال مُعلَّم كموقوف.",
  diag_voice_suspended_steps: "راجع الدعم الفني قبل إعادة التفعيل.",
  diag_voice_suspended_stage: "إدارة النظام",
  diag_voice_disconnected_title: "اتصال الصوت غير متحقَّق منه",
  diag_voice_disconnected_cause: "الاتصال مهيَّأ لكنه لم يُتحقَّق منه لدى المزوّد قط.",
  diag_voice_disconnected_steps: "شغِّل اختبار اتصال في إعدادات الصوت.",
  diag_voice_disconnected_stage: "التحقق من الاتصال",
  diag_calendar_error_title: "آخر خطأ في التقويم",
  diag_calendar_error_cause: "فشل آخر استدعاء لمزوّد التقويم؛ يُمسح الخطأ عند أول نجاح تالٍ.",
  diag_calendar_error_steps: "تحقّق من الاتصال في شاشة المواعيد؛ وإذا تكرر الخطأ — أعد الاتصال.",
  diag_calendar_error_stage: "استدعاء مزوّد التقويم",
  diag_template_rejected_title: "قالب مرفوض من Meta",
  diag_template_rejected_cause: "رفضت Meta هذا القالب — لا يمكن استخدامه للإرسال حتى يُصحَّح ويُعتمَد من جديد.",
  diag_template_rejected_steps: "عدِّل القالب وأعد تقديمه في الإعدادات.",
  diag_template_rejected_stage: "مراجعة القوالب لدى Meta",

  // Long-running task contract
  ltStarted: "بدأ",
  ltScope: "النطاق",
  ltStage: "المرحلة الحالية",
  ltCancel: "إلغاء",
  ltPartial: "نتائج جزئية",
  ltNotify: "إشعار عند الانتهاء",
  ltCounter: "{done} من {total}",
  ltNoFake: "لا يوجد عدّاد حقيقي من المحرك — يُعرض مؤشر غير محدَّد، وليس نسبًا مئوية",

  // Intelligence layer
  intelJourneyTitle: "الرحلة والتحويلات",
  intelJourneyEmpty: "لا توجد ملاحظات بعد — علامات التعلّم مُطفأة؛ هذه هي الحالة المخطَّط لها. عند تفعيلها، سترى أين ولماذا حوّل البوت المحادثة إلى إنسان.",
  intelDashLink: "فتح لوحة معلومات الذكاء الاصطناعي القائمة",
  intelAudienceTitle: "شرائح الجمهور (Tier-1)",

  // Automation layer
  autoPoliciesTitle: "السياسات (Policy Registry)",
  autoPoliciesBanner: "سجلات معتمَدة — لا شيء منها نشط، وهذا مقصود بالتصميم (Approved ≠ Active). لا يوجد محرك إنفاذ يستهلكها.",
  policyInactive: "غير نشطة",
  autoFlowsTitle: "مسارات البوت (من قوائم الانتظار الحالية)",
  autoApprovalsTitle: "سلسلة الموافقات",
  approvalsEmpty: "لا توجد سجلات موافقة بعد — ستظهر عند وجود مرشَّحات وموافقات بشرية",
  configVersionsTitle: "إصدارات التهيئة",
  configVersionsEmpty:
    "لم تُسجَّل أي إصدارات بعد. الكاتب موجود ويعمل في كل دور محكوم تغيّر فيه تجزئة الإعدادات — لكن فقط عندما يكون BRAIN_GOVERNANCE_RECORD مفعّلاً.",

  // System layer
  sysFlagsTitle: "علامات النظام (الدماغ)",
  flagsProcessNote: "علامات على مستوى العملية — متطابقة لجميع المستأجرين على الخادم المشترك؛ تغيير علامة = عملية نشر، وليس إعدادًا",
  flagOn: "مفعَّلة",
  flagOff: "مُطفأة",
  sysConfigTitle: "التهيئة الفعلية",
  sysAuditTitle: "التدقيق",

  // ── 2026-08-03: keys that existed only in he/en, so this language
  //    fell back to English for roughly a quarter of the AI-OS surface —
  //    including every area name and every tab label. Translated, not
  //    transliterated; product nouns (the Brain, digital employees) follow
  //    the wording already used elsewhere in this dictionary.
  areaHome: "نظرة عامة",
  areaBrain: "العقل",
  areaBrainSub: "الجاهزية، تغطية المعرفة، الملاحظات ومعايرة الثقة",
  areaEmployees: "الموظفون الرقميون",
  areaEmployeesSub: "14 دوراً في 5 أقسام — من جاهز، من محجوب وما الناقص",
  areaKnowledge: "المعرفة",
  areaKnowledgeSub: "ما يستطيع العقل الاستشهاد به، وما ينقصه بعد",
  areaIntelligence: "الذكاء",
  areaIntelligenceSub: "رؤى مدعومة بالأدلة، رحلة العميل وشرائح الجمهور",
  areaDashboard: "لوحة تحكم الذكاء الاصطناعي",
  areaDashboardSub: "الأرقام التشغيلية خلف الذكاء الاصطناعي",
  areaGrowth: "التسويق",
  areaGrowthSub: "بيانات التسويق والاتصالات والرؤى في مكان واحد",
  areaVoice: "الصوت",
  areaVoiceSub: "الوكيل الصوتي: الجاهزية والمكالمات والاتصالات",
  areaAutomation: "الأتمتة والحوكمة",
  areaAutomationSub: "ما يعمل وحده، وما يحتاج موافقة، ومن وافق",
  areaDiscovery: "تعليم العقل",
  areaDiscoverySub: "مقابلة التعارف، خطة البناء، والعقل في كل شاشة",
  areaSetup: "إعدادات العقل",
  areaSetupSub: "الإعداد، الشخصية، التكوين الفعّال، الرايات والإصدارات",
  areaPlannedTitle: "هذه المنطقة غير متصلة بعد",
  areaPlannedReason: "الشاشة موجودة، لكن المحرك الذي يغذيها لم يُبنَ بعد — لذلك لن يُعرض هنا أي رقم مُختلق.",
  areaPlannedHint: "بمجرد وصول المحرك، ستظهر البيانات الحقيقية هنا تلقائياً.",
  bpApplied: "طُبِّق",
  candidateApprove: "موافقة",
  candidateReject: "رفض",
  candidateResolving: "جارٍ المعالجة…",
  candidateResolveError: "فشل الإجراء — حاول مرة أخرى",
  candidateSupport: "ملاحظات داعمة",
  candidateSupportHint: "كم ملاحظة مستقلة تدعم هذا الاقتراح",
  employeesNoneReady: "لم يجتز أي دور عتبة الجاهزية بعد — تعليم المعرفة الناقصة سيفتح الأول.",
  employeesNoneOpen: "لا توجد وظيفة شاغرة حالياً — كل الأدوار التي يعرفها العقل اجتازت العتبة.",
  homeAreasSub: "كل منطقة تُفتح كصفحة خاصة بها مع علامات تبويب — بلا تمرير لا نهائي",
  homeAsksTitle: "ما يطلبه العقل منك",
  homeAsksSubtitle: "كل موافقة تحرّك العقل خطوة",
  homeBlockersSubtitle: "أهم ثلاثة عوائق",
  homeBlockersEmpty: "لا يوجد عائق مُبلَّغ عنه حالياً — عند تراكم البيانات، سيظهر هنا ما يعيق العقل.",
  homeJumpTitle: "إلى أين تذهب",
  homeJumpSubtitle: "المناطق التي تُقدّم العقل أكثر",
  homePendingApprovals: "قرارات سُجِّلت كطالبة للموافقة",
  homeReadinessSubtitle: "الأرقام خلف النتيجة",
  homeReadyRoles: "أدوار جاهزة للتوظيف",
  homeReadyRolesHint: "اجتازت عتبة الجاهزية وتنتظرك",
  homeReadyRolesBlocked: "لا يمكن فتح أي دور بعد: كل دور يتطلب أيضاً ثقة عقل 50% فأكثر، ولم تُقَس الثقة بعد — تعليم المعرفة وحده لن يفتح دوراً.",
  homeShadowVerdictsSub: "أحكام في وضع الظل — مُسجَّلة فقط. لا يوجد طابور موافقات للقرارات، ولم يُنفَّذ أي منها.",
  inScreenNote: "العقل نفسه يظهر داخل شاشاتك اليومية كبطاقة للقراءة فقط. لا يكتب شيئاً، ومن لا يملك صلاحية لا تظهر له البطاقة أصلاً.",
  inScreenOpen: "افتح الشاشة",
  inScreenWhere: "الموقع",
  inScreenTicketsTitle: "العقل عن هذا العميل (التذاكر)",
  inScreenTicketsBody: "مرحلة الرحلة، نية الشراء، المشاعر، ما ينقص للتقدم، والسؤال التالي الذي يستحق طرحه — مع الأدلة خلف كل ادعاء.",
  inScreenQueuesTitle: "ثغرات نصوص البوت (الطوابير)",
  inScreenQueuesBody: "كشف الثغرات في طوابيرك الحالية — مثل طابور بلا رسالة ترحيب — لتعرف ما تُغلقه أولاً.",
  inScreenCampaignsTitle: "شرائح الجمهور (الحملات)",
  inScreenCampaignsBody: "تقسيم حتمي فوق البيانات الموجودة، كاقتراح فقط: لا أحد يُضاف تلقائياً والإرسال يبقى في شاشة الحملات.",
  inScreenSettingsTitle: "تشخيص الأعطال (الإعدادات)",
  inScreenSettingsBody: "ما تعطّل، ولماذا، ومن يجب أن يصلحه وما الخطوة التالية — كل إصلاح رابط للشاشة القائمة، بلا إصلاح تلقائي.",
  intelCalibrationRatio: "مرشحون / ملاحظات",
  seamUnavailable: "المعطى غير متاح حالياً",
  sentimentNotDerived: "لم يُستنتج بعد — لا إشارة مشاعر، ولا نختلق واحدة",
  setupPersonaSub: "الشخصية والنبرة والنموذج خلف كل إجابة — إلى جانب الترحيب والمصادر والاتصالات التي تغذيها",
  setupWizardSub: "الإعداد الأول للعقل: من أنتم، كيف يتحدث، من أين يقرأ البيانات، ومتى يُحوّل إلى إنسان",
  stepIntakeShort: "جمع المعلومات",
  stepHandoffShort: "تحويل لإنسان",
  tabOverview: "نظرة عامة",
  tabReadiness: "الجاهزية",
  tabCoverage: "تغطية المعرفة",
  tabObservations: "الملاحظات",
  tabCalibration: "معايرة الثقة",
  tabOrg: "المنظمة",
  tabReadyToHire: "جاهزون للتوظيف",
  tabOpenPositions: "وظائف شاغرة",
  tabEffectiveConfig: "التكوين الفعّال",
  tabWorkforceConsole: "لوحة القوى العاملة",
  tabSources: "المصادر",
  tabKnowledgeItems: "عناصر المعرفة",
  tabCustomerMemory: "ذاكرة العملاء",
  tabCandidates: "المرشحون",
  tabInsights: "الرؤى",
  tabJourney: "الرحلة والعملاء",
  tabSegments: "شرائح الجمهور",
  tabAgentPerformance: "أداء الوكلاء",
  tabTopIssues: "أهم المشكلات",
  tabSentimentTrends: "اتجاهات المشاعر",
  tabSalesInsights: "رؤى المبيعات",
  tabConnections: "الاتصالات",
  tabCampaigns: "الحملات",
  tabLearningInsights: "التعلّم والرؤى",
  tabDiagnostics: "التشخيص والأعطال",
  tabCalls: "المكالمات",
  tabVoiceAgents: "الوكلاء",
  tabInCallBilling: "التحصيل",
  tabCallSearch: "بحث",
  tabVoiceSettings: "إعدادات الصوت",
  tabFlows: "العمليات",
  tabPolicies: "السياسات",
  tabDecisionLog: "سجل القرارات",
  tabAuditChain: "سلسلة التدقيق",
  tabInterview: "مقابلة التعارف",
  tabBlueprint: "خطة العقل",
  tabBrainInScreens: "العقل في شاشاتك",
  tabSetupWizard: "معالج الإعداد",
  tabPersonaTone: "الشخصية والنبرة",
  tabSystemFlags: "رايات النظام",
  tabConfigVersions: "الإصدارات",
  tabHealthDiagnostics: "الصحة والتشخيص",
  tabGovernanceSettings: "الحوكمة والإعدادات",
  tabMemoryRecommendations: "الذاكرة والتوصيات",
  tabReportsTimeline: "التقارير والخط الزمني",

  // The design closes every HOME queue card with a gradient CTA and an
  // outlined "details". Ours point at two different screens on purpose:
  // coverage is where the missing knowledge gets taught, observations is
  // where the raw finding lives.
  homeAskCta: "علِّمه الآن",
  homeAskDetails: "التفاصيل",

  // ── Brain prose authored by the BACKEND, said in this language ─────────
  // evaluateBrainEvolution.ts writes readiness.health.issues[],
  // coverage[].missing[] and requirements[].guidance as English sentences,
  // and four screens printed them verbatim — so a Hebrew tenant read English
  // on the HOME hero, on all three queue cards, under every coverage bar and
  // in the Blueprint. brainText() below matches each form and renders it from
  // here; anything it does not recognise still shows the raw sentence.
  biNoSettings: "لم تُضبَط إعدادات الذكاء الاصطناعي لهذه الشركة",
  biAiDisabled: "الذكاء الاصطناعي مُعطَّل لهذه الشركة",
  biNoKnowledge: "لم يُدرَّس أي عنصر معرفة بعد",
  biNoObservations: "لم تُسجَّل أي ملاحظات تعلّم (قد تكون رايات التعلّم مطفأة)",
  biNoMemories: "لا توجد ذكريات عملاء مؤكَّدة بعد",
  biRolesLocked: "{n} من أصل {total} دوراً لا يمكن فتحها بعد — {reasons}",
  biReasonCoverage: "المعرفة أقل من {pct}% في {cats}",
  biReasonNoConfidence: "لم تُسجَّل ثقة قرار بعد",
  biReasonThinSample: "لا توجد أدلة كافية لدرجة ثقة ({n} من {min} قراراً)",
  biReasonLowConfidence: "ثقة القرار {value} أقل من {min}",
  bcConfigureSettings: "اضبط إعدادات الذكاء الاصطناعي للشركة",
  bcConnectCrm: "اربط مصدر بيانات من الـ CRM",
  bcMoreMemories: "أكِّد المزيد من ذكريات العملاء ({n}/{target})",
  bcMoreObservations: "مطلوب المزيد من التفاعلات المرصودة ({n}/{target})",
  bcDefineTone: "حدِّد نبرة صوت الشركة",
  bcVoiceUnavailable: "تعليم الأسلوب الصوتي غير متاح بعد",
  bcAddKnowledge: "أضِف عناصر معرفة في فئة {cat} ({n}/{target})",
  bgTeachCategory: "علِّم العقل عن {cat} حتى تغطية {pct}% على الأقل",
  bgConfidenceGrows: "ثقة قرارات العقل تنمو مع تراكم القرارات الخاضعة للحوكمة",
  bgThinEvidence: "لا توجد أدلة كافية بعد — مطلوب {n} من {min} قراراً مسجَّلاً قبل أن تعني درجة الثقة شيئاً",

  // The shadow-mode banner is a `note` card in the design, and a design note
  // card always has a title. We had the sentence but no name for it, so the
  // card rendered headless in a grid where every sibling is titled.
  shadowNoteTitle: "وضع الظل",

  // Data-First: an absent dataset renders an honest empty state saying WHAT is
  // missing and WHY. The flags card had no such copy, so a failed read drew a
  // card with a head and a footnote and nothing between them.
  flagsEmpty: "لم تُرجَع أي راية عملية. هذه رايات على مستوى الخادم — القائمة الفارغة تعني أن القراءة فشلت، لا أنه لا توجد رايات.",

  // The roadmap counts card had no name — it was the one untitled cell in a
  // grid where every sibling is titled, because nothing in the dictionary
  // named the summary itself (only the three things it counts).
  bpRoadmapTitle: "الخطة بلمحة",
};
const ar_ROLES = {
  sales: "مندوب مبيعات",
  collections: "مندوب تحصيل",
  support: "ممثل دعم",
  receptionist: "موظف استقبال",
  appointment: "منسّق مواعيد",
  marketing: "مسوّق",
  "campaign-manager": "مدير حملات",
  "crm-assistant": "مساعد CRM",
  "voice-agent": "وكيل صوتي",
  "qa-manager": "مدير جودة",
  "knowledge-manager": "مدير معرفة",
  "reporting-analyst": "محلل تقارير",
  supervisor: "مشرف",
  "executive-assistant": "مساعد تنفيذي",
};
const ar_CATEGORIES = {
  products: "المنتجات",
  services: "الخدمات",
  "company-policies": "سياسات الشركة",
  documents: "المستندات",
  "crm-understanding": "فهم الـ CRM",
  "customer-history": "سجل العملاء",
  "conversation-history": "سجل المحادثات",
  "writing-style": "أسلوب الكتابة",
  "voice-style": "أسلوب التحدث",
  "sales-process": "عملية المبيعات",
  "support-process": "عملية الدعم",
  "payment-policies": "سياسات الدفع",
  "automation-rules": "قواعد الأتمتة",
};
const ar_INTAKE = {
  "legal-domain": "المجال القانوني",
  urgency: "مدى الاستعجال",
  "opposing-party": "الطرف المقابل (فحص تضارب المصالح)",
  "buy-sell-rent": "شراء / بيع / إيجار",
  "property-type": "نوع العقار",
  areas: "المناطق",
  "mortgage-approved": "موافقة الرهن العقاري",
  destination: "الوجهة",
  dates: "التواريخ",
  budget: "الميزانية",
  travellers: "عدد المسافرين",
  "service-type": "نوع الخدمة",
  "preferred-times": "الأوقات المفضلة",
  "product-interest": "المنتج المطلوب",
  "order-number": "رقم الطلب",
};
const ar_KPI = {
  "time-to-first-consult": "الوقت حتى أول استشارة",
  "tours-scheduled": "جولات مجدولة",
  "offers-sent": "عروض مُرسلة",
  "appointments-set": "مواعيد محدَّدة",
  "orders-assisted": "طلبات تمت معالجتها",
  "quotes-sent": "عروض أسعار مُرسلة",
  "conversations-resolved": "محادثات تم حلها",
};
const ar_REQKIND = {
  knowledge: "معرفة",
  capability: "قدرة",
  policy: "سياسة",
  confidence: "ثقة",
  tool: "أداة",
  channel: "قناة",
  permission: "إذن",
  governance: "حوكمة",
};
const ru = {
  candidateContributors: "Независимые источники",
  candidatesPageNote: "Показаны первые {shown} по всем статусам — это страница, а не вся очередь.",
  // ── knowledge · what the resolver admitted, and what disagreed ──────
  provSourcesTitle: "Источники знаний",
  provSourcesSub: "Что попадает в ответы, а что задерживает шлюз",
  provGateNote: "Факт без действительного источника или с истёкшей свежестью в ответ не попадает — поэтому мозг не выдумывает.",
  provNoSourceRead: "Ни один источник не был прочитан — это не то же самое, что «проблем не найдено». Загрузчик читает AIKnowledge, хранилище знаний только стратегии godeal-pipeline.",
  provNoSourceReadHint: "Знания этого арендатора лежат в companyKnowledge, откуда этот экран пока не читает.",
  provAdmitted: "Принято",
  provRejected: "Отклонено",
  provSourceValid: "Действителен",
  provSourceBlocked: "Заблокирован",
  provSourceEmpty: "Пусто",
  provConflictsTitle: "Обнаруженные противоречия",
  provConflictsSub: "Два источника, разные ответы — и выбранное решение",
  provNoConflicts: "Среди прочитанных источников противоречий не обнаружено.",
  provWinner: "Решено в пользу",
  provLosers: "Отклонённые источники",
  provDecide: "Решено",
  intelLoadWindowNote: "Эти количества берутся из последних {limit} записей с каждой стороны, поэтому это окно, а не итог; арендатор за пределом увидит ровно {limit}.",
  intelJourneyEmptyNoCause: "Наблюдений пока не записано. Этот экран читает только количества и не может сказать почему — состояние флагов находится в области настроек мозга.",
  contactMemoryWhyItMatters: "Подтверждённая память о контакте питает категорию истории клиентов, которая сегодня блокирует две роли: взыскание и CRM-ассистента.",
  // ── design-file conformance · departments + candidates footnote ─────
  deptSummaryTitle: "Отделы",
  deptSummarySub: "Средняя готовность по отделу",
  deptScoredOf: "Среднее по {scored} из {roles} ролей",
  deptUnplaced: "Ролей, которые схема не может разместить: {count} — {roles}. Учтены в итогах выше",
  deptReady: "Готов",
  deptBlocked: "Заблокирован",
  candidatesApprovalNote: "Только человеческое одобрение продвигает кандидата в организационную память. Одобрение — не активация.",
  // ── system · the audit pair discloses its window ────────────────────
  auditWindowApprovals: "Показаны {shown} новейших одобрений — это окно, а не весь след: запрос ограничен {requested} строками, поэтому общее число на этом экране неизвестно.",
  auditWindowVersions: "Показаны {shown} новейших версий из {total}.",
  auditWindowDecisions: "Показаны {shown} новейших решений — это окно, а не весь журнал: запрос ограничен {requested} строками, поэтому общее число на этом экране неизвестно.",
  // ── coverage · eight categories read a store most tenants never use ──
  coverageUnteachableCaveat:
    "{unteachable} из этих категорий пока нечем обучать — они показаны с их настоящим покрытием, но не входят в оценку, которая усредняется по {scored} из {total} категорий, доступных для обучения.",
  // ── knowledge · the clamp says how much was clamped ─────────────────
  contentChars: "Содержимое",
  // ── employees · published companion fields, now read ─────────────────
  promptChars: "Промпт: {chars} симв.",
  deferredInput_connectedChannels: "подключённые каналы",
  deferredInput_hasActiveLearningAssets: "активные обучающие материалы",
  // ── brain · calibration, per the design file ─────────────────────────
  calibrationKvTitle: "Калибровка уверенности",
  calibrationKvSub: "Оправдана ли уверенность мозга",
  calibrationOverallApproval: "Общее одобрение",
  calibrationOverallApprovalHint: "Одобренные из числа решённых (одобрено или отклонено) — ожидающие и просроченные не считаются",
  calibrationHighConfidence: "Высокая уверенность (≥0.8)",
  calibrationHighConfidenceHint: "Источника пока нет — отчёт калибровки возвращает количества по видам, без разбивки по уровню уверенности",
  calibrationFloorNote: "Процент показывается для вида только после {floor} решений этого вида; ниже показываются сами количества.",
  confidenceDistributionTitle: "Распределение уверенности",
  confidenceDistributionNoFeed: "Этот график ничем не питается — калибровка сегодня измеряется по виду кандидата, а не по уровню уверенности.",
  confidenceDistributionHint: "Нужна разбивка по уровню уверенности на стороне сервера; до тех пор карточка намеренно остаётся пустой.",
  // ── brain · observation kinds said in words, per the design file ──────
  obsKind_memory_confirm: "Память подтверждена",
  obsKind_memory_edit: "Память отредактирована",
  obsKind_memory_dismiss: "Память отклонена",
  obsKind_ai_handoff: "Передача живому оператору",
  obsKind_ai_disable: "ИИ отключён",
  obsKind_execution_outcome: "Результат выполнения",
  obsKind_appointment_outcome: "Результат встречи",
  obsKind_payment_outcome: "Результат платежа",
  obsKind_cancellation: "Отмена",
  obsKind_human_correction: "Правка человека",
  obsKind_business_outcome: "Бизнес-результат",
  obsKind_evaluation: "Оценка качества ответа",
  observationsScopeAllTime: "За всё время — не окно в 30 дней",
  observationsRecentTitle: "Последние наблюдения",
  observationsRecentSub: "Что увидел мозг, по порядку",
  // ── brain · coverage split into all / needs-you, per the design file ──
  coverageAllTitle: "Покрытие знаний по категориям",
  coverageAllSub: "Каждая категория измеряется относительно порога 60%",
  coverageNeedsYouTitle: "Категории, которым нужны вы",
  coverageNeedsYouSub: "Те, что ниже порога — именно они держат следующий уровень",
  // ── brain · the health card's four rows, per the design file ───────
  healthLevel: "Уровень",
  healthOpenIssues: "Открытые проблемы",
  healthNoOpenIssues: "Нет",
  // ── brain · the health card explains its own dash ──────────────────
  healthConfidenceCaveat: "«Средняя уверенность» остаётся «—», пока не накопятся управляемые решения. Мы не выдумываем число.",
  // ── D1 · no area rollups is not the same as no data ────────────────
  stNotWiredTitle: "У этой области нет источника данных",
  stNotWiredBody: "Сервер возвращает только статус и подключения, без сводок по областям — то есть это не «данных пока нет», а читать неоткуда. Это состояние системы, а не вашей работы.",
  // ── F3.1 · Growth tabs in the shell strip ──────────────────────────
  gtabOverview: "Обзор",
  gtabCampaigns: "Кампании",
  gtabConnections: "Подключения",
  gtabLearning: "Обучение и выводы",
  gtabReports: "Отчёты и хронология",
  gtabMemory: "Память и рекомендации",
  gtabHealth: "Состояние и диагностика",
  gtabGovernance: "Управление и настройки",
  gtabCampaignsEmpty: "Результаты кампаний ещё не подключены к этому рабочему пространству. Сами кампании в системе есть, но в данные этого экрана они не входят.",
  gtabCampaignsEmptyHint: "Пока подключение не завершено, они видны на существующем экране кампаний.",
  // ── F3.2 · honest empty states ───────────────────────────────────────
  // Every reason below is the cause traced in the backend, not a restatement
  // of the title. Emptiness without a cause reads as "all is well" when it
  // usually means "nothing is connected", and three of the strings these
  // replace said something stronger and untrue.
  queuesNoneAtAll:
    "Ни одной очереди не создано, поэтому проверять было нечего — это не пройденная проверка.",
  queuesNoneAtAllHint:
    "Создание первой очереди на экране очередей включит эту проверку.",
  channelsNoneConfigured:
    "Ни один канал не заведён — ни WhatsApp, ни Meta, ни голосовая линия. Это не то же самое, что заведённый, но отключённый канал.",
  channelsNoneConfiguredHint:
    "Подключение канала на экране подключений создаст здесь строку ещё до подтверждения связи.",
  diagScanScope:
    "Проверены только активные календари и отклонённые шаблоны WhatsApp. Выключенная настройка не считается неисправностью.",
  orgMemoryEmptyReal:
    "Ни один вывод ещё не подтверждён. Организационная память пополняется только подтверждением человека, а не самими диалогами.",
  orgMemoryEmptyRealHint:
    "Одобрение происходит отдельным шагом с доступом владельца — не на этой вкладке, она только для чтения.",
  candidatesEmptyReal:
    "Кандидатов нет — и не потому, что мало разговоров. Проход вывода, который создаёт кандидатов, нигде не запланирован к запуску, поэтому никакой объём разговоров не создаст ни одного.",
  bpUpcomingEmptyReal:
    "Заблокированных ролей нет — каждая заведённая роль уже отвечает своим условиям разблокировки.",
  bpTrainingFullyTaught:
    "Следующего шага обучения нет, потому что все области знаний покрыты полностью — это успех, а не пробел.",
  calibrationZeroRows:
    "Для этого аккаунта не записано ни наблюдений, ни кандидатов. Счётчик считает реальные строки, а не порог, который нужно перешагнуть.",
  calibrationZeroRowsHint:
    "Строки появляются только при включённом обучении мозга. Если оно выключено, экран останется пустым — и это верно.",
  audienceNoTickets:
    "Сегменты строятся по обращениям в просканированном окне времени, а в нём нет ни одного обращения.",
  audienceNoTicketsHint:
    "Первое обращение внутри окна создаст первый сегмент.",
  contactNotResolved:
    "Единая личность для этого контакта не найдена. Тот же результат возвращается, если контакт принадлежит другому аккаунту.",
  coverageUnavailable:
    "Данные покрытия не пришли с сервера при этой загрузке. Это ошибка, а не нулевое покрытие.",
  coverageUnavailableHint:
    "Перезагрузка страницы запросит их снова; если повторяется — проблема на сервере.",
  effectiveConfigUnavailable:
    "Действующая конфигурация не пришла с сервера. Это не значит, что ничего не настроено.",
  capabilitiesNoneActive:
    "Активных возможностей нет. Часть входных данных для этого расчёта ещё не подключена, поэтому список может занижать реальное состояние.",
  growthAllAreasDenied:
    "У этой учётной записи нет доступа ни к одной из областей маркетинга.",
  growthAllAreasDeniedHint:
    "Администратор аккаунта может открыть области на экране прав доступа.",
  autoPoliciesEmpty: "Записей политик пока нет — они создаются из одобренных человеком кандидатов, когда флаги будут включены за отдельным шлюзом",
  autoFlowsEmpty: "Очереди ещё не настроены — создайте очередь на существующем экране очередей",
  // Shell
  shellTitle: "AI-OS",
  brainOf: "Мозг {name}",
  layerBrain: "Мозг",
  layerEmployees: "Сотрудники",
  layerKnowledge: "Знания",
  layerIntelligence: "Интеллект",
  layerAutomation: "Автоматизация",
  layerSystem: "Система",
  loading: "Загрузка…",
  loadError: "Не удалось загрузить данные",
  retry: "Повторить",

  // Brain layer (MRI)
  readinessTitle: "Готовность Мозга",
  levelLabel: "Уровень {level}",
  scoreLabel: "Оценка готовности",
  healthTitle: "Здоровье Мозга",
  healthOperational: "Настроен и работает",
  healthNotOperational: "Ещё не настроен полностью",
  healthConfidence: "Средняя уверенность",
  healthLearningQuality: "Качество обучающего сигнала",
  issuesTitle: "Что сдерживает Мозг",
  noIssues: "Открытых проблем нет",
  coverageTitle: "Покрытие знаний по категориям",
  coverageEmpty: "Покрытия знаний пока нет — обучите Мозг через экраны знаний",
  coverageMissingPrefix: "Не хватает:",
  observationsTitle: "Наблюдения (обучение)",
  observationsEmpty:
    "Ваш Мозг пока не наблюдает за разговорами — флаг наблюдения (BRAIN_LEARNING_OBSERVE) выключен. Это запланированное состояние, а не сбой.",
  observationsTotal: "Всего наблюдений",
  lastSeen: "Последнее наблюдение",
  decisionsTitle: "Журнал решений (Governance)",
  decisionsEmpty:
    "Записанных решений пока нет — флаг записи Governance (BRAIN_GOVERNANCE_RECORD) выключен. Это запланированное состояние.",
  governanceMetricsTitle: "Метрики Governance",
  totalDecisions: "Всего решений",
  calibrationTitle: "Калибровка уверенности",
  calibrationNoData: "Данных пока недостаточно",
  approvalRate: "Доля одобрений",
  quarantinedConfidence: "Значения уверенности в карантине",
  distinctHumanActors: "Уникальных участников-людей",
  shadowNote:
    "Мозг работает в теневом режиме (shadow) — он только наблюдает и советует; ни одно решение не применяется к реальным разговорам.",
  discoveryBtn: "Интервью Discovery",
  blueprintBtn: "Blueprint Мозга",
  backToBrain: "Назад к Мозгу",

  // Employees layer
  orgChartTitle: "Цифровая организация",
  orgChartSubtitle: "Живая схема — полностью построена на реальных данных; ничего не сохраняется",
  deptSales: "Продажи",
  deptSupport: "Поддержка",
  deptMarketing: "Маркетинг",
  deptVoice: "Голос",
  deptOperations: "Операции",
  statusReady: "Готов к найму",
  statusLocked: "Открытая позиция",
  statusHired: "Нанят",
  openPositionHint: "Что ещё нужно Мозгу, чтобы открыть эту роль:",
  readinessOf: "Готовность",
  trainingMinutes: "~{min} мин обучения до открытия",
  kpiEmpty: "Измерение эффективности включится, когда сотрудники начнут действовать через Мозг",
  agentsTitle: "Живые экземпляры (AIAgents)",
  agentsEmpty: "Для этой компании пока не настроено ни одного AI-агента",
  agentsTruncated: "Показаны первые {shown} — список усечён",
  agentEnabled: "Включён",
  agentDisabled: "Выключен",
  effectiveConfigTitle: "Эффективная конфигурация (One Brain)",
  effectiveConfigSubtitle: "Все сотрудники наследуют одну и ту же конфигурацию — один Мозг на компанию",
  cfgModel: "Модель",
  cfgCredentialSource: "Источник учётных данных",
  cfgCredentialMasked: "Показывается только как метка — секреты никогда не передаются",
  cfgKnowledgeSource: "Источник знаний",
  cfgLanguage: "Язык",
  cfgPersona: "Промпт персоны",
  cfgTone: "Тон",
  cfgToneEmpty: "Не задан (движок не придумывает значение по умолчанию)",
  capabilitiesTitle: "Возможности",
  capabilitiesDeferredNote:
    "Часть возможностей ({inputs}) теневой резолвер пока разрешает лишь частично — они могут быть показаны с занижением, но никогда с завышением.",
  channelsTitle: "Подключённые каналы",
  channelsEmpty: "Каналы пока не подключены",
  channelConnected: "Подключено {connected} из {total}",

  // Knowledge layer
  knowledgeTitle: "Knowledge Explorer",
  knowledgeSubtitle: "Всё, что знает Мозг — из реальных источников, в его собственном словаре",
  strategyLabel: "Стратегия знаний",
  strategyAiKnowledge: "Хранилище AIKnowledge",
  strategyCompanyKnowledge: "Структурированные знания компании (companyKnowledge)",
  itemsTitle: "Элементы знаний",
  itemsEmpty: "Мозг ещё не обучали — добавьте знания в существующих экранах настроек",
  itemsTruncated: "Показано {shown} элементов — список усечён на сервере",
  itemActive: "Активен",
  itemInactive: "Неактивен",
  contactMemoryTitle: "Память о клиентах (ContactMemory)",
  contactMemoryNote: "Только счётчики — записи памяти по каждому клиенту остаются в карточке контакта",
  cmSuggested: "Ожидающие предложения",
  cmConfirmed: "Подтверждено",
  cmDismissed: "Отклонено",
  cmTotal: "Всего",
  orgMemoryTitle: "Организационная память (BrainOrgMemory)",
  orgMemoryEmpty:
    "Организационная память пуста — флаг BRAIN_ORG_MEMORY_ENABLED выключен. Это запланированное состояние.",
  candidatesTitle: "Предложения Мозга (кандидаты)",
  candidatesEmpty:
    "Мозг пока ничего не предложил — флаги обучения выключены. Когда их включат (за отдельным гейтом), предложения появятся здесь для одобрения человеком.",
  candidatesViewOnly: "Только просмотр — одобрение/отклонение откроется за отдельным гейтом владельца (Tier-2)",
  statusPending: "Ожидает проверки",
  statusApproved: "Одобрено",
  statusRejected: "Отклонено",
  statusExpired: "Истекло",

  // Discovery
  discoveryTitle: "Интервью Discovery — Мозг изучает ваш бизнес",
  discoveryIntro:
    "Несколько коротких вопросов, которые адаптируют Мозг к вашему бизнесу. Ничего не активируется и не сохраняется — в конце вы получите только предлагаемый Blueprint.",
  ephemeralBanner:
    "Только предложение — ответы не сохраняются, и поведение не меняется. Реальная активация всегда выполняется в существующих экранах настроек, с одобрения человека.",
  stepBusinessType: "Тип бизнеса",
  stepMainGoal: "Главная цель",
  stepIntake: "Что собирать с каждого обращения",
  stepHandoff: "Когда передавать человеку",
  stepChannels: "Каналы",
  stepKpi: "Метрика успеха",
  archLaw: "Юридическая фирма",
  archRealEstate: "Агентство недвижимости",
  archTravel: "Турагентство",
  archClinic: "Клиника",
  archEcommerce: "Магазин / e-commerce",
  archServices: "Сфера услуг",
  archOther: "Другое",
  goalSales: "Больше продаж",
  goalSupport: "Более быстрая поддержка",
  goalScheduling: "Запись на встречи",
  goalCollections: "Сбор платежей и напоминания",
  handoffStrict: "Строгий — любой содержательный вопрос сразу передаётся человеку",
  handoffMedium: "Средний — бот занимается записью; переговоры передаются человеку",
  handoffSoft: "Мягкий — бот сопровождает до закрытия; человек подключается только в конце",
  channelConnectedTag: "Подключён",
  channelConfiguredNotConnectedTag: "Настроен, но не подключён — проверьте экран подключений",
  channelNotConnectedTag: "Не подключён — подключите на экране подключений",
  next: "Далее",
  back: "Назад",
  finish: "Показать Blueprint",
  otherFieldPlaceholder: "Уточните…",

  // Blueprint
  blueprintTitle: "Предлагаемый Blueprint Мозга",
  blueprintHonesty:
    "Основано на структуре процессов, которую вы описали — данных об использовании пока нет. Рекомендации обновятся, когда Мозг начнёт наблюдать (за отдельным гейтом).",
  bpRecommended: "Рекомендуется нанять сейчас",
  bpRecommendedEmpty: "Сотрудников, готовых к найму, пока нет — обучайте Мозг, чтобы открыть позиции",
  bpUpcoming: "Открытые позиции (по близости к открытию)",
  bpTraining: "Следующие шаги обучения",
  bpTrainingEmpty: "Активных рекомендаций по обучению нет",
  bpUnlocks: "Открывает:",
  bpChannels: "Каналы в плане",
  bpProposed: "Предложено",
  bpKeep: "Одобрить",
  bpRemove: "Убрать",
  bpLater: "Позже",
  bpLegend:
    "Предложено = Мозг предлагает, ничего не произошло · Одобрено = человек одобрил, поведение не изменилось · Применено = только после будущего гейта активации",
  bpFromInterview: "Из вашего интервью",

  // ── Gate 3: Insight panels ──
  insightTicketTitle: "Мозг об этом клиенте",
  insightTicketSubtitle: "Живая картина интеллекта — только чтение, ноль записей",
  insightCampaignsTitle: "Аудитории с объяснениями от Мозга",
  insightCampaignsSubtitle: "Детерминированная сегментация по существующим данным — только предложение",
  insightQueuesTitle: "Мозг о ваших потоках",
  insightSettingsTitle: "Диагностика Мозга — Error Copilot",
  noContextData: "По этому клиенту у Мозга пока нет данных",
  stage_new: "Новый",
  stage_engaged: "В контакте",
  stage_qualifying: "Квалификация",
  stage_proposal: "Предложение",
  stage_negotiation: "Переговоры",
  stage_won: "Успешно закрыт",
  stage_lost: "Потерян",
  stage_dormant: "Спящий",
  intent_hot: "Горячий",
  intent_warm: "Тёплый",
  intent_cold: "Холодный",
  intent_unknown: "Неизвестно",
  sentiment_positive: "Позитивная",
  sentiment_neutral: "Нейтральная",
  sentiment_negative: "Негативная",
  journeyLabel: "Этап пути",
  intentLabel: "Намерение покупки",
  sentimentLabel: "Тональность",
  leadScoreLabel: "Оценка лида",
  leadUnscored: "Оценки пока нет — реального сигнала нет, и никакой не выдумывается",
  missingInfoTitle: "Недостающая информация",
  nextQuestionTitle: "Лучший следующий вопрос",
  intakeTitle: "Уже собрано",
  confirmedMemoryTitle: "Подтверждённая память",
  evidenceLabel: "Якоря доказательств",
  blocksStagePrefix: "Блокирует этап:",

  // Audience preview
  audienceScan: "Просканированы последние обновлённые тикеты: {tickets} · Контакты: {pool} · Глубокий анализ: {analyzed}",
  audienceTruncatedNote: "Окно усечено — это недавняя активность, а не все данные",
  audienceProposalOnly: "Только предложение — никто не добавляется в списки автоматически; отправка остаётся на экране кампаний, только по одобренным шаблонам ({approved} одобрено)",
  basis_intent: "Намерение покупки",
  basis_stage: "Этап пути",
  "basis_ticket-status": "Статус тикета",
  basis_recency: "Активность",
  basis_channel: "Канал",
  "recency_active-7d": "Активен за последнюю неделю",
  "recency_active-30d": "Активен за последний месяц",
  recency_older: "Более давние",
  moreNames: "и ещё {n}",

  // Queues insight + Flow-Builder preview skeleton
  flowGapsTitle: "Пробелы в потоках",
  flowNoGaps: "Пробелов не найдено — все очереди настроены",
  "gap_missing-greeting": "Нет приветственного сообщения",
  "gap_no-flow-options": "Не заданы варианты потока",
  "gap_no-enabled-channels": "Для этой очереди не включены каналы",
  optionsCountLabel: "Вариантов: {n}",
  flowBuilderTitle: "Flow Builder — Preview",
  flowBuilderHonesty: "Движок генерации потока из свободного описания ещё не существует — он появится как отдельный slice, одобренный владельцем и закрытый флагом. Здесь будет показываться Preview сгенерированного потока — для одобрения человеком, перед любым импортом.",
  flowBuilderPlaceholder: "Опишите вашу услугу свободным текстом — откроется за будущим гейтом",

  // Error Copilot / diagnostics
  diagTitle: "Диагностика Мозга",
  diagClean: "Ошибок конфигурации не найдено — все проверки пройдены",
  diagCheckedAt: "Проверено",
  diagEvidence: "Доказательство",
  diagSeverity_critical: "Критично",
  diagSeverity_warning: "Предупреждение",
  diagSeverity_info: "Информация",
  diagRetryHelps: "Повтор может помочь",
  diagRetryWontHelp: "Повтор не поможет",
  diagNeedsAdmin: "Нужен администратор",
  diagNeedsSupport: "Нужна поддержка",
  diagFixBtn: "Исправить в существующем экране",
  diagUncertainTitle: "Нераспознанный сбой",
  diagUncertainBody: "Мне не удалось определить причину; вот ровно то, что было проверено — без догадок.",
  diagStage: "На каком этапе",
  diagFault: "На чьей стороне",
  fault_provider: "Провайдер",
  fault_config: "Конфигурация",
  fault_system: "Система",
  diagShownOf: "Показано {shown} из {total} отклонённых шаблонов",
  diag_channel_disconnected_title: "Канал отключён",
  diag_channel_disconnected_cause: "Подключение существует, но не сообщает статус «подключено» — обычно это истёкшая сессия или устройство офлайн.",
  diag_channel_disconnected_steps: "Откройте экран подключений и подключитесь заново.",
  diag_channel_disconnected_stage: "Сессия канала",
  diag_channel_in_setup_title: "Подключение настраивается",
  diag_channel_in_setup_cause: "Подключение в процессе настройки — ожидает сканирования QR или завершения входа; это не сбой.",
  diag_channel_in_setup_steps: "Завершите настройку на экране подключений.",
  diag_channel_in_setup_stage: "Настройка подключения",
  diag_voice_invalid_token_title: "Голосовой провайдер отклонил учётные данные",
  diag_voice_invalid_token_cause: "Провайдер отклонил учётные данные — возможно, их сменили или отозвали.",
  diag_voice_invalid_token_steps: "Обновите учётные данные в настройках голоса; до этого повтор не поможет.",
  diag_voice_invalid_token_stage: "Аутентификация у провайдера",
  diag_voice_suspended_title: "Голосовое подключение приостановлено",
  diag_voice_suspended_cause: "Подключение помечено как приостановленное.",
  diag_voice_suspended_steps: "Перед повторным включением обратитесь в поддержку.",
  diag_voice_suspended_stage: "Администрирование системы",
  diag_voice_disconnected_title: "Голосовое подключение не проверено",
  diag_voice_disconnected_cause: "Подключение настроено, но ни разу не проверено у провайдера.",
  diag_voice_disconnected_steps: "Запустите проверку подключения в настройках голоса.",
  diag_voice_disconnected_stage: "Проверка подключения",
  diag_calendar_error_title: "Последняя ошибка календаря",
  diag_calendar_error_cause: "Последний вызов провайдера календаря завершился ошибкой; она очистится при следующем успешном вызове.",
  diag_calendar_error_steps: "Проверьте подключение на экране встреч; если повторяется — переподключитесь.",
  diag_calendar_error_stage: "Вызов провайдера календаря",
  diag_template_rejected_title: "Шаблон отклонён Meta",
  diag_template_rejected_cause: "Meta отклонила этот шаблон — отправлять по нему нельзя, пока он не исправлен и не одобрен заново.",
  diag_template_rejected_steps: "Отредактируйте и повторно отправьте шаблон в настройках.",
  diag_template_rejected_stage: "Проверка шаблонов в Meta",

  // Long-running task contract (י״א.16)
  ltStarted: "Начато",
  ltScope: "Объём",
  ltStage: "Текущий этап",
  ltCancel: "Отмена",
  ltPartial: "Частичные результаты",
  ltNotify: "Уведомить по завершении",
  ltCounter: "{done} из {total}",
  ltNoFake: "Реального счётчика от движка нет — показывается индикатор неопределённого прогресса, а не проценты",

  // Intelligence layer
  intelJourneyTitle: "Путь и передачи (Handoff)",
  intelJourneyEmpty: "Наблюдений пока нет — флаги обучения выключены; это запланированное состояние. Когда их включат, здесь будет видно, где и почему бот передал разговор человеку.",
  intelDashLink: "Открыть существующий AI-дашборд",
  intelAudienceTitle: "Сегменты аудитории (Tier-1)",

  // Automation layer
  autoPoliciesTitle: "Политики (Policy Registry)",
  autoPoliciesBanner: "Одобренные записи — ни одна не активна, так задумано (Approved ≠ Active). Ни один движок применения их не использует.",
  policyInactive: "Неактивна",
  autoFlowsTitle: "Потоки бота (из существующих очередей)",
  autoApprovalsTitle: "Цепочка одобрений",
  approvalsEmpty: "Записей об одобрениях пока нет — они появятся, когда возникнут кандидаты и одобрения от людей",
  configVersionsTitle: "Версии конфигурации",
  configVersionsEmpty:
    "Версии ещё не записаны. Писатель существует и срабатывает на каждом управляемом ходе, где изменился хеш конфигурации, — но только когда включён BRAIN_GOVERNANCE_RECORD.",

  // System layer
  sysFlagsTitle: "Системные флаги (Brain)",
  flagsProcessNote: "Флаги уровня процесса — одинаковы для всех тенантов на общем хосте; изменение флага = деплой, а не настройка",
  flagOn: "Вкл.",
  flagOff: "Выкл.",
  sysConfigTitle: "Эффективная конфигурация",
  sysAuditTitle: "Аудит",

  // ── 2026-08-03: keys that existed only in he/en, so this language
  //    fell back to English for roughly a quarter of the AI-OS surface —
  //    including every area name and every tab label. Translated, not
  //    transliterated; product nouns (the Brain, digital employees) follow
  //    the wording already used elsewhere in this dictionary.
  areaHome: "Обзор",
  areaBrain: "Мозг",
  areaBrainSub: "Готовность, покрытие знаний, наблюдения и калибровка уверенности",
  areaEmployees: "Цифровые сотрудники",
  areaEmployeesSub: "14 ролей в 5 отделах — кто готов, кто заблокирован и чего не хватает",
  areaKnowledge: "Знания",
  areaKnowledgeSub: "Что Мозг может процитировать и чего ему ещё не хватает",
  areaIntelligence: "Аналитика",
  areaIntelligenceSub: "Выводы, подкреплённые доказательствами, путь клиента и сегменты",
  areaDashboard: "Панель ИИ",
  areaDashboardSub: "Операционные показатели ИИ",
  areaGrowth: "Маркетинг",
  areaGrowthSub: "Маркетинговые данные, подключения и выводы в одном месте",
  areaVoice: "Голос",
  areaVoiceSub: "Голосовой агент: готовность, звонки и подключения",
  areaAutomation: "Автоматизация и управление",
  areaAutomationSub: "Что работает само, что требует одобрения и кто одобрил",
  areaDiscovery: "Обучение Мозга",
  areaDiscoverySub: "Ознакомительное интервью, план построения и Мозг на каждом экране",
  areaSetup: "Настройки Мозга",
  areaSetupSub: "Настройка, персона, действующая конфигурация, флаги и версии",
  areaPlannedTitle: "Эта область ещё не подключена",
  areaPlannedReason: "Экран существует, но движок, который его питает, ещё не построен — поэтому здесь не будет показано выдуманное число.",
  areaPlannedHint: "Как только движок появится, реальные данные отобразятся здесь автоматически.",
  bpApplied: "Применено",
  candidateApprove: "Одобрить",
  candidateReject: "Отклонить",
  candidateResolving: "Обработка…",
  candidateResolveError: "Действие не удалось — попробуйте снова",
  candidateSupport: "Подтверждающие наблюдения",
  candidateSupportHint: "Сколько независимых наблюдений стоит за этим предложением",
  employeesNoneReady: "Ни одна роль ещё не прошла порог готовности — обучение недостающим знаниям откроет первую.",
  employeesNoneOpen: "Сейчас нет открытых позиций — все роли, известные Мозгу, прошли порог.",
  homeAreasSub: "Каждая область открывается как отдельная страница с вкладками — без бесконечной прокрутки",
  homeAsksTitle: "Что Мозг просит у вас",
  homeAsksSubtitle: "Каждое одобрение продвигает Мозг на шаг",
  homeBlockersSubtitle: "Три главных препятствия",
  homeBlockersEmpty: "Сейчас нет зарегистрированных препятствий — когда данные накопятся, здесь появится то, что сдерживает Мозг.",
  homeJumpTitle: "Куда перейти",
  homeJumpSubtitle: "Области, которые продвигают Мозг сильнее всего",
  homePendingApprovals: "Решения, отмеченные как требующие одобрения",
  homeReadinessSubtitle: "Числа за оценкой",
  homeReadyRoles: "Роли, готовые к найму",
  homeReadyRolesHint: "Прошли порог готовности и ждут вас",
  homeReadyRolesBlocked: "Ни одна роль пока не может быть открыта: каждая также требует уверенности Мозга 50% и выше, а уверенность ещё не измерена — обучение знаниям само по себе роль не откроет.",
  homeShadowVerdictsSub: "Вердикты в теневом режиме — только записаны. Очереди одобрения решений нет, и ни одно не было исполнено.",
  inScreenNote: "Тот же самый Мозг появляется внутри ваших повседневных экранов как карточка только для чтения. Он ничего не пишет, а тем, у кого нет прав, карточка просто не показывается.",
  inScreenOpen: "Открыть экран",
  inScreenWhere: "Расположение",
  inScreenTicketsTitle: "Мозг об этом клиенте (обращения)",
  inScreenTicketsBody: "Этап пути, намерение купить, настроение, чего не хватает для продвижения и следующий вопрос, который стоит задать — с доказательствами за каждым утверждением.",
  inScreenQueuesTitle: "Пробелы в сценариях бота (очереди)",
  inScreenQueuesBody: "Обнаружение пробелов в ваших очередях — например очередь без приветствия — чтобы вы знали, что закрыть первым.",
  inScreenCampaignsTitle: "Сегменты аудитории (кампании)",
  inScreenCampaignsBody: "Детерминированная сегментация по существующим данным, только как предложение: никто не добавляется автоматически, отправка остаётся на экране кампаний.",
  inScreenSettingsTitle: "Диагностика ошибок (настройки)",
  inScreenSettingsBody: "Что сломалось, почему, кто должен исправить и каков следующий шаг — каждое исправление это ссылка на существующий экран, без авто-исправления.",
  intelCalibrationRatio: "Кандидаты / наблюдения",
  seamUnavailable: "Данные сейчас недоступны",
  sentimentNotDerived: "Ещё не выведено — нет сигнала настроения, и он не выдумывается",
  setupPersonaSub: "Персона, тон и модель за каждым ответом — а также приветствие, источники и подключения, которые их питают",
  setupWizardSub: "Первичная настройка Мозга: кто вы, как он говорит, откуда читает данные и когда передаёт человеку",
  stepIntakeShort: "Сбор данных",
  stepHandoffShort: "Передача человеку",
  tabOverview: "Обзор",
  tabReadiness: "Готовность",
  tabCoverage: "Покрытие знаний",
  tabObservations: "Наблюдения",
  tabCalibration: "Калибровка уверенности",
  tabOrg: "Организация",
  tabReadyToHire: "Готовы к найму",
  tabOpenPositions: "Открытые позиции",
  tabEffectiveConfig: "Действующая конфигурация",
  tabWorkforceConsole: "Консоль персонала",
  tabSources: "Источники",
  tabKnowledgeItems: "Элементы знаний",
  tabCustomerMemory: "Память о клиентах",
  tabCandidates: "Кандидаты",
  tabInsights: "Выводы",
  tabJourney: "Путь и клиенты",
  tabSegments: "Сегменты аудитории",
  tabAgentPerformance: "Эффективность агентов",
  tabTopIssues: "Основные проблемы",
  tabSentimentTrends: "Тренды настроений",
  tabSalesInsights: "Выводы по продажам",
  tabConnections: "Подключения",
  tabCampaigns: "Кампании",
  tabLearningInsights: "Обучение и выводы",
  tabDiagnostics: "Диагностика и сбои",
  tabCalls: "Звонки",
  tabVoiceAgents: "Агенты",
  tabInCallBilling: "Оплата в звонке",
  tabCallSearch: "Поиск",
  tabVoiceSettings: "Настройки голоса",
  tabFlows: "Процессы",
  tabPolicies: "Политики",
  tabDecisionLog: "Журнал решений",
  tabAuditChain: "Цепочка аудита",
  tabInterview: "Ознакомительное интервью",
  tabBlueprint: "План Мозга",
  tabBrainInScreens: "Мозг в ваших экранах",
  tabSetupWizard: "Мастер настройки",
  tabPersonaTone: "Персона и тон",
  tabSystemFlags: "Системные флаги",
  tabConfigVersions: "Версии",
  tabHealthDiagnostics: "Здоровье и диагностика",
  tabGovernanceSettings: "Управление и настройки",
  tabMemoryRecommendations: "Память и рекомендации",
  tabReportsTimeline: "Отчёты и хронология",

  // The design closes every HOME queue card with a gradient CTA and an
  // outlined "details". Ours point at two different screens on purpose:
  // coverage is where the missing knowledge gets taught, observations is
  // where the raw finding lives.
  homeAskCta: "Научить сейчас",
  homeAskDetails: "Подробнее",

  // ── Brain prose authored by the BACKEND, said in this language ─────────
  // evaluateBrainEvolution.ts writes readiness.health.issues[],
  // coverage[].missing[] and requirements[].guidance as English sentences,
  // and four screens printed them verbatim — so a Hebrew tenant read English
  // on the HOME hero, on all three queue cards, under every coverage bar and
  // in the Blueprint. brainText() below matches each form and renders it from
  // here; anything it does not recognise still shows the raw sentence.
  biNoSettings: "Для этой компании не настроены параметры ИИ",
  biAiDisabled: "ИИ отключён для этой компании",
  biNoKnowledge: "Пока не изучен ни один элемент знаний",
  biNoObservations: "Наблюдений обучения не записано (флаги обучения могут быть выключены)",
  biNoMemories: "Подтверждённых воспоминаний о клиентах пока нет",
  biRolesLocked: "{n} из {total} ролей пока нельзя открыть — {reasons}",
  biReasonCoverage: "знания ниже {pct}% в разделах: {cats}",
  biReasonNoConfidence: "уверенность решений ещё не измерена",
  biReasonThinSample: "недостаточно данных для оценки уверенности ({n} из {min} решений)",
  biReasonLowConfidence: "уверенность решений {value} ниже {min}",
  bcConfigureSettings: "Настройте параметры ИИ для компании",
  bcConnectCrm: "Подключите источник данных CRM",
  bcMoreMemories: "Подтвердите больше воспоминаний о клиентах ({n}/{target})",
  bcMoreObservations: "Нужно больше наблюдаемых взаимодействий ({n}/{target})",
  bcDefineTone: "Задайте тон общения компании",
  bcVoiceUnavailable: "Обучение голосовому стилю пока недоступно",
  bcAddKnowledge: "Добавьте элементы знаний в категорию {cat} ({n}/{target})",
  bgTeachCategory: "Обучите Мозг теме {cat} минимум до {pct}% покрытия",
  bgConfidenceGrows: "Уверенность решений Мозга растёт по мере накопления управляемых решений",
  bgThinEvidence: "Пока недостаточно данных — нужно {n} из {min} записанных решений, прежде чем оценка уверенности что-то значит",

  // The shadow-mode banner is a `note` card in the design, and a design note
  // card always has a title. We had the sentence but no name for it, so the
  // card rendered headless in a grid where every sibling is titled.
  shadowNoteTitle: "Теневой режим",

  // Data-First: an absent dataset renders an honest empty state saying WHAT is
  // missing and WHY. The flags card had no such copy, so a failed read drew a
  // card with a head and a footnote and nothing between them.
  flagsEmpty: "Не возвращено ни одного флага процесса. Это флаги уровня хоста — пустой список означает, что чтение не удалось, а не что флагов нет.",

  // The roadmap counts card had no name — it was the one untitled cell in a
  // grid where every sibling is titled, because nothing in the dictionary
  // named the summary itself (only the three things it counts).
  bpRoadmapTitle: "План в цифрах",
};
const ru_ROLES = {
  sales: "Менеджер по продажам",
  collections: "Специалист по сбору платежей",
  support: "Специалист поддержки",
  receptionist: "Ресепшионист",
  appointment: "Координатор встреч",
  marketing: "Маркетолог",
  "campaign-manager": "Менеджер кампаний",
  "crm-assistant": "CRM-ассистент",
  "voice-agent": "Голосовой агент",
  "qa-manager": "Менеджер по качеству",
  "knowledge-manager": "Менеджер знаний",
  "reporting-analyst": "Аналитик отчётности",
  supervisor: "Супервайзер",
  "executive-assistant": "Ассистент руководителя",
};
const ru_CATEGORIES = {
  products: "Продукты",
  services: "Услуги",
  "company-policies": "Политики компании",
  documents: "Документы",
  "crm-understanding": "Понимание CRM",
  "customer-history": "История клиентов",
  "conversation-history": "История разговоров",
  "writing-style": "Стиль письма",
  "voice-style": "Стиль речи",
  "sales-process": "Процесс продаж",
  "support-process": "Процесс поддержки",
  "payment-policies": "Политики оплаты",
  "automation-rules": "Правила автоматизации",
};
const ru_INTAKE = {
  "legal-domain": "Область права",
  urgency: "Срочность",
  "opposing-party": "Противоположная сторона (проверка конфликта интересов)",
  "buy-sell-rent": "Покупка / продажа / аренда",
  "property-type": "Тип недвижимости",
  areas: "Районы",
  "mortgage-approved": "Одобрение ипотеки",
  destination: "Направление",
  dates: "Даты",
  budget: "Бюджет",
  travellers: "Количество путешественников",
  "service-type": "Тип услуги",
  "preferred-times": "Предпочтительное время",
  "product-interest": "Интересующий товар",
  "order-number": "Номер заказа",
};
const ru_KPI = {
  "time-to-first-consult": "Время до первой консультации",
  "tours-scheduled": "Запланированные показы",
  "offers-sent": "Отправленные предложения",
  "appointments-set": "Назначенные встречи",
  "orders-assisted": "Обработанные заказы",
  "quotes-sent": "Отправленные ценовые предложения",
  "conversations-resolved": "Решённые диалоги",
};
const ru_REQKIND = {
  knowledge: "Знания",
  capability: "Возможность",
  policy: "Политика",
  confidence: "Уверенность",
  tool: "Инструмент",
  channel: "Канал",
  permission: "Разрешение",
  governance: "Контроль",
};
const es = {
  candidateContributors: "Contribuyentes independientes",
  candidatesPageNote: "Se muestran los primeros {shown} de todos los estados: es una página, no toda la cola.",
  // ── knowledge · what the resolver admitted, and what disagreed ──────
  provSourcesTitle: "Fuentes de conocimiento",
  provSourcesSub: "Qué llega a las respuestas y qué bloquea la puerta",
  provGateNote: "Un dato sin fuente válida o con frescura vencida nunca llega a una respuesta: por eso el cerebro no inventa.",
  provNoSourceRead: "No se leyó ninguna fuente, lo cual no equivale a no haber encontrado problemas. El cargador lee AIKnowledge, el almacén de conocimiento solo de la estrategia godeal-pipeline.",
  provNoSourceReadHint: "El conocimiento de este inquilino está en companyKnowledge, que esta pantalla todavía no lee.",
  provAdmitted: "Admitidos",
  provRejected: "Rechazados",
  provSourceValid: "Válido",
  provSourceBlocked: "Bloqueado",
  provSourceEmpty: "Vacío",
  provConflictsTitle: "Contradicciones detectadas",
  provConflictsSub: "Dos fuentes, respuestas distintas, y la resolución elegida",
  provNoConflicts: "No se detectó ninguna contradicción entre las fuentes leídas.",
  provWinner: "Resuelto a favor de",
  provLosers: "Fuentes descartadas",
  provDecide: "Resuelto",
  intelLoadWindowNote: "Estos recuentos se toman de las {limit} filas más recientes de cada lado, así que son una ventana y no un total; un inquilino que supere el límite lee exactamente {limit}.",
  intelJourneyEmptyNoCause: "Todavía no se ha registrado ninguna observación. Esta pantalla solo lee recuentos y no puede decir por qué: el estado de las banderas está en el área de ajustes del cerebro.",
  contactMemoryWhyItMatters: "Una memoria de contacto confirmada alimenta la categoría de historial de clientes, que hoy bloquea dos roles: cobros y el asistente de CRM.",
  // ── design-file conformance · departments + candidates footnote ─────
  deptSummaryTitle: "Departamentos",
  deptSummarySub: "Preparación media por departamento",
  deptScoredOf: "Promedio sobre {scored} de {roles} roles",
  deptUnplaced: "{count} rol(es) que este gráfico no puede ubicar: {roles} — contados en los totales de arriba",
  deptReady: "Listo",
  deptBlocked: "Bloqueado",
  candidatesApprovalNote: "Solo una aprobación humana promueve a un candidato a la memoria organizativa. Aprobar no es activar.",
  // ── system · the audit pair discloses its window ────────────────────
  auditWindowApprovals: "Se muestran las {shown} aprobaciones más recientes: es una ventana, no el registro completo. La petición está limitada a {requested} filas, así que el total no se conoce en esta pantalla.",
  auditWindowVersions: "Se muestran las {shown} versiones más recientes de {total}.",
  auditWindowDecisions: "Se muestran las {shown} decisiones más recientes: es una ventana, no el registro completo. La petición está limitada a {requested} filas, así que el total no se conoce en esta pantalla.",
  // ── coverage · eight categories read a store most tenants never use ──
  coverageUnteachableCaveat:
    "{unteachable} de estas categorías todavía no se pueden enseñar: se muestran con su cobertura real pero no cuentan en la puntuación, que se promedia sobre las {scored} de {total} categorías que sí puedes enseñar.",
  // ── knowledge · the clamp says how much was clamped ─────────────────
  contentChars: "Contenido",
  // ── employees · published companion fields, now read ─────────────────
  promptChars: "Prompt: {chars} caracteres",
  deferredInput_connectedChannels: "canales conectados",
  deferredInput_hasActiveLearningAssets: "activos de aprendizaje activos",
  // ── brain · calibration, per the design file ─────────────────────────
  calibrationKvTitle: "Calibración de la confianza",
  calibrationKvSub: "Si la confianza del cerebro está justificada",
  calibrationOverallApproval: "Aprobación general",
  calibrationOverallApprovalHint: "Aprobados de los que se decidieron (aprobados o rechazados): los pendientes y caducados no cuentan",
  calibrationHighConfidence: "Confianza alta (≥0,8)",
  calibrationHighConfidenceHint: "Todavía no hay fuente: el informe de calibración devuelve recuentos por tipo, sin desglose por nivel de confianza",
  calibrationFloorNote: "Un porcentaje aparece para un tipo solo después de {floor} decisiones de ese tipo; por debajo se muestran los propios recuentos.",
  confidenceDistributionTitle: "Distribución de la confianza",
  confidenceDistributionNoFeed: "Nada alimenta este gráfico: hoy la calibración se mide por tipo de candidato, no por nivel de confianza.",
  confidenceDistributionHint: "Necesita un desglose por nivel de confianza en el servidor; hasta entonces esta tarjeta permanece vacía a propósito.",
  // ── brain · observation kinds said in words, per the design file ──────
  obsKind_memory_confirm: "Memoria confirmada",
  obsKind_memory_edit: "Memoria editada",
  obsKind_memory_dismiss: "Memoria descartada",
  obsKind_ai_handoff: "Traspaso a un agente humano",
  obsKind_ai_disable: "IA desactivada",
  obsKind_execution_outcome: "Resultado de ejecución",
  obsKind_appointment_outcome: "Resultado de la cita",
  obsKind_payment_outcome: "Resultado del pago",
  obsKind_cancellation: "Cancelación",
  obsKind_human_correction: "Corrección humana",
  obsKind_business_outcome: "Resultado de negocio",
  obsKind_evaluation: "Evaluación de la calidad de la respuesta",
  observationsScopeAllTime: "Desde siempre, no una ventana de 30 días",
  observationsRecentTitle: "Observaciones recientes",
  observationsRecentSub: "Lo que vio el cerebro, en orden",
  // ── brain · coverage split into all / needs-you, per the design file ──
  coverageAllTitle: "Cobertura de conocimiento por categoría",
  coverageAllSub: "Cada categoría se mide frente a un umbral del 60%",
  coverageNeedsYouTitle: "Categorías que te necesitan",
  coverageNeedsYouSub: "Las que están por debajo del umbral: son las que frenan el siguiente nivel",
  // ── brain · the health card's four rows, per the design file ───────
  healthLevel: "Nivel",
  healthOpenIssues: "Problemas abiertos",
  healthNoOpenIssues: "Ninguno",
  // ── brain · the health card explains its own dash ──────────────────
  healthConfidenceCaveat: "La \"confianza media\" permanece en \"—\" hasta que se acumulen decisiones gobernadas. No inventamos un número.",
  // ── D1 · no area rollups is not the same as no data ────────────────
  stNotWiredTitle: "Esta área no tiene fuente de datos",
  stNotWiredBody: "El servidor devuelve solo estado y conexiones, sin resúmenes por área: no es \"aún no hay datos\", es que no hay de dónde leer. Es un hecho del sistema, no de tu actividad.",
  // ── F3.1 · Growth tabs in the shell strip ──────────────────────────
  gtabOverview: "Resumen",
  gtabCampaigns: "Campañas",
  gtabConnections: "Conexiones",
  gtabLearning: "Aprendizaje e ideas",
  gtabReports: "Informes y cronología",
  gtabMemory: "Memoria y recomendaciones",
  gtabHealth: "Estado y diagnóstico",
  gtabGovernance: "Gobernanza y ajustes",
  gtabCampaignsEmpty: "El rendimiento de las campañas aún no está conectado a este espacio de trabajo. Las campañas existen en el sistema, pero no forman parte de los datos que carga esta pantalla.",
  gtabCampaignsEmptyHint: "La pantalla de Campañas existente las muestra hasta que se complete esta conexión.",
  // ── F3.2 · honest empty states ───────────────────────────────────────
  // Every reason below is the cause traced in the backend, not a restatement
  // of the title. Emptiness without a cause reads as "all is well" when it
  // usually means "nothing is connected", and three of the strings these
  // replace said something stronger and untrue.
  queuesNoneAtAll:
    "No hay ninguna cola definida, así que no había nada que comprobar: esto no es una comprobación superada.",
  queuesNoneAtAllHint:
    "Crear la primera cola en la pantalla de colas activa esta comprobación.",
  channelsNoneConfigured:
    "No hay ningún canal configurado: ni WhatsApp, ni Meta, ni línea de voz. Es distinto de un canal configurado y desconectado.",
  channelsNoneConfiguredHint:
    "Conectar un canal en la pantalla de conexiones crea una fila aquí, incluso antes de verificar la conexión.",
  diagScanScope:
    "Se analizaron solo los calendarios activos y las plantillas de WhatsApp rechazadas. Un ajuste desactivado no es un fallo y no se cuenta aquí.",
  orgMemoryEmptyReal:
    "Todavía no se ha aprobado ninguna lección. La memoria organizativa se escribe solo con una aprobación humana, nunca por las conversaciones.",
  orgMemoryEmptyRealHint:
    "La aprobación ocurre en un paso aparte, restringido al propietario, no en esta pestaña, que es de solo lectura.",
  candidatesEmptyReal:
    "No hay candidatos en espera, y no porque haya pocas conversaciones. La pasada de inferencia que crea candidatos no está programada para ejecutarse en ningún sitio, así que ningún volumen de conversación producirá uno.",
  bpUpcomingEmptyReal:
    "No hay roles bloqueados: cada rol existente ya cumple sus condiciones de desbloqueo.",
  bpTrainingFullyTaught:
    "No hay un siguiente paso de enseñanza porque todas las áreas de conocimiento están cubiertas por completo: es un éxito, no una carencia.",
  calibrationZeroRows:
    "No se ha registrado ninguna observación ni candidato para esta cuenta. El contador cuenta filas reales, no un umbral pendiente.",
  calibrationZeroRowsHint:
    "Las filas aparecen solo con el aprendizaje del cerebro activado. Si está apagado, esto seguirá vacío, y es correcto.",
  audienceNoTickets:
    "Los segmentos se derivan de los tickets dentro de la ventana analizada, y no hay ninguno en ella.",
  audienceNoTicketsHint:
    "El primer ticket dentro de la ventana genera el primer segmento.",
  contactNotResolved:
    "No se encontró una identidad unificada para este contacto. Se devuelve el mismo resultado cuando el contacto pertenece a otra cuenta.",
  coverageUnavailable:
    "Los datos de cobertura no llegaron del servidor en esta carga. Es un estado de error, no cobertura cero.",
  coverageUnavailableHint:
    "Recargar la página vuelve a solicitarlos; si se repite, el fallo es del servidor.",
  effectiveConfigUnavailable:
    "La configuración efectiva no llegó del servidor. No interpretes esto como que no hay nada configurado.",
  capabilitiesNoneActive:
    "No hay ninguna capacidad activa. Algunas entradas de este cálculo aún no están conectadas, así que la lista puede quedarse corta.",
  growthAllAreasDenied:
    "Esta cuenta de usuario no tiene permiso para ninguna de las áreas de marketing.",
  growthAllAreasDeniedHint:
    "Un administrador de la cuenta puede abrir áreas en la pantalla de permisos.",
  autoPoliciesEmpty: "Aún no hay registros de políticas — se crean a partir de aprobaciones humanas de candidatos, cuando las banderas se activen tras una puerta separada",
  autoFlowsEmpty: "Aún no hay colas configuradas — crea una en la pantalla de colas existente",
  shellTitle: "AI-OS",
  brainOf: "El Cerebro de {name}",
  layerBrain: "Cerebro",
  layerEmployees: "Empleados",
  layerKnowledge: "Conocimiento",
  layerIntelligence: "Inteligencia",
  layerAutomation: "Automatización",
  layerSystem: "Sistema",
  loading: "Cargando…",
  loadError: "Error al cargar los datos",
  retry: "Reintentar",

  readinessTitle: "Preparación del Cerebro",
  levelLabel: "Nivel {level}",
  scoreLabel: "Puntuación de preparación",
  healthTitle: "Salud del Cerebro",
  healthOperational: "Configurado y operativo",
  healthNotOperational: "Aún no está configurado por completo",
  healthConfidence: "Confianza media",
  healthLearningQuality: "Calidad de la señal de aprendizaje",
  issuesTitle: "Qué frena al Cerebro",
  noIssues: "Sin problemas abiertos",
  coverageTitle: "Cobertura de conocimiento por categoría",
  coverageEmpty: "Aún no hay cobertura de conocimiento — enseñe al Cerebro desde las pantallas de conocimiento",
  coverageMissingPrefix: "Falta:",
  observationsTitle: "Observaciones (Aprendizaje)",
  observationsEmpty:
    "Su Cerebro aún no observa las conversaciones — el flag de observación (BRAIN_LEARNING_OBSERVE) está desactivado. Este es el estado previsto, no un fallo.",
  observationsTotal: "Total de observaciones",
  lastSeen: "Visto por última vez",
  decisionsTitle: "Registro de decisiones (Governance)",
  decisionsEmpty:
    "Aún no hay decisiones registradas — el flag de registro de Governance (BRAIN_GOVERNANCE_RECORD) está desactivado. Este es el estado previsto.",
  governanceMetricsTitle: "Métricas de Governance",
  totalDecisions: "Total de decisiones",
  calibrationTitle: "Calibración de confianza",
  calibrationNoData: "Aún no hay datos suficientes",
  approvalRate: "Tasa de aprobación",
  quarantinedConfidence: "Valores de confianza en cuarentena",
  distinctHumanActors: "Contribuidores humanos distintos",
  shadowNote:
    "El Cerebro funciona en modo sombra (shadow) — solo observa y aconseja; ninguna decisión se aplica sobre conversaciones reales.",
  discoveryBtn: "Entrevista de descubrimiento (Discovery)",
  blueprintBtn: "Plan del Cerebro (Blueprint)",
  backToBrain: "Volver al Cerebro",

  orgChartTitle: "La organización digital",
  orgChartSubtitle: "Un organigrama vivo — derivado por completo de datos reales; nada se guarda",
  deptSales: "Ventas",
  deptSupport: "Soporte",
  deptMarketing: "Marketing",
  deptVoice: "Voz",
  deptOperations: "Operaciones",
  statusReady: "Listo para contratar",
  statusLocked: "Puesto abierto",
  statusHired: "Contratado",
  openPositionHint: "Qué le falta al Cerebro para abrir este puesto:",
  readinessOf: "Preparación",
  trainingMinutes: "~{min} min de enseñanza para desbloquear",
  kpiEmpty: "La medición de desempeño se activa cuando los empleados actúan a través del Cerebro",
  agentsTitle: "Instancias activas (AIAgents)",
  agentsEmpty: "Aún no hay agentes de IA configurados para esta empresa",
  agentsTruncated: "Se muestran los primeros {shown} — la lista fue truncada",
  agentEnabled: "Activado",
  agentDisabled: "Desactivado",
  effectiveConfigTitle: "Configuración efectiva (One Brain)",
  effectiveConfigSubtitle: "Todos los empleados heredan la misma configuración — un solo Cerebro por empresa",
  cfgModel: "Modelo",
  cfgCredentialSource: "Origen de credenciales",
  cfgCredentialMasked: "Se muestra solo como etiqueta — los secretos nunca se envían",
  cfgKnowledgeSource: "Fuente de conocimiento",
  cfgLanguage: "Idioma",
  cfgPersona: "Prompt de persona",
  cfgTone: "Tono",
  cfgToneEmpty: "Sin definir (el motor no inventa un valor por defecto)",
  capabilitiesTitle: "Capacidades",
  capabilitiesDeferredNote:
    "Algunas capacidades ({inputs}) aún se resuelven parcialmente en el resolver sombra — pueden mostrarse de menos, nunca de más.",
  channelsTitle: "Canales conectados",
  channelsEmpty: "Aún no hay canales conectados",
  channelConnected: "{connected} de {total} conectados",

  knowledgeTitle: "Knowledge Explorer",
  knowledgeSubtitle: "Todo lo que el Cerebro sabe — de fuentes reales, en el vocabulario propio del Cerebro",
  strategyLabel: "Estrategia de conocimiento",
  strategyAiKnowledge: "Almacén AIKnowledge",
  strategyCompanyKnowledge: "Conocimiento estructurado de la empresa (companyKnowledge)",
  itemsTitle: "Elementos de conocimiento",
  itemsEmpty: "El Cerebro aún no ha sido enseñado — agregue conocimiento en las pantallas de configuración existentes",
  itemsTruncated: "Se muestran {shown} elementos — la lista fue truncada en el servidor",
  itemActive: "Activo",
  itemInactive: "Inactivo",
  contactMemoryTitle: "Memoria de clientes (ContactMemory)",
  contactMemoryNote: "Solo recuentos — los elementos de memoria por cliente permanecen en la ficha del contacto",
  cmSuggested: "Sugerencias pendientes",
  cmConfirmed: "Confirmados",
  cmDismissed: "Descartados",
  cmTotal: "Total",
  orgMemoryTitle: "Memoria organizacional (BrainOrgMemory)",
  orgMemoryEmpty:
    "La memoria organizacional está vacía — el flag BRAIN_ORG_MEMORY_ENABLED está desactivado. Este es el estado previsto.",
  candidatesTitle: "Sugerencias del Cerebro (candidatos)",
  candidatesEmpty:
    "El Cerebro aún no ha sugerido nada — los flags de aprendizaje están desactivados. Cuando se activen (tras un gate separado), las sugerencias aparecerán aquí para aprobación humana.",
  candidatesViewOnly: "Solo lectura — aprobar/rechazar se abrirá tras un gate de propietario separado (Tier-2)",
  statusPending: "Pendiente de revisión",
  statusApproved: "Aprobado",
  statusRejected: "Rechazado",
  statusExpired: "Expirado",

  discoveryTitle: "Entrevista de descubrimiento — el Cerebro aprende su negocio",
  discoveryIntro:
    "Unas pocas preguntas breves que adaptan el Cerebro a su negocio. Nada se activa ni se guarda — al final recibe solo un Blueprint propuesto.",
  ephemeralBanner:
    "Solo una propuesta — las respuestas no se guardan y ningún comportamiento cambia. La activación real siempre ocurre en las pantallas de configuración existentes, con aprobación humana.",
  stepBusinessType: "Tipo de negocio",
  stepMainGoal: "Objetivo principal",
  stepIntake: "Qué recopilar de cada consulta",
  stepHandoff: "Cuándo transferir a un humano",
  stepChannels: "Canales",
  stepKpi: "Métrica de éxito",
  archLaw: "Bufete de abogados",
  archRealEstate: "Agencia inmobiliaria",
  archTravel: "Agencia de viajes",
  archClinic: "Clínica",
  archEcommerce: "Tienda / e-commerce",
  archServices: "Negocio de servicios",
  archOther: "Otro",
  goalSales: "Más ventas",
  goalSupport: "Soporte más rápido",
  goalScheduling: "Programación de citas",
  goalCollections: "Cobranza y seguimiento",
  handoffStrict: "Estricto — cualquier pregunta sustancial pasa directamente a un humano",
  handoffMedium: "Medio — el bot gestiona la agenda; la negociación pasa a un humano",
  handoffSoft: "Suave — el bot acompaña hasta el cierre; un humano interviene solo al final",
  channelConnectedTag: "Conectado",
  channelConfiguredNotConnectedTag: "Configurado pero no conectado — revise la pantalla de Conexiones",
  channelNotConnectedTag: "No conectado — conéctelo en la pantalla de Conexiones",
  next: "Siguiente",
  back: "Atrás",
  finish: "Mostrar el Blueprint",
  otherFieldPlaceholder: "Detalles…",

  blueprintTitle: "Blueprint propuesto del Cerebro",
  blueprintHonesty:
    "Basado en la estructura de proceso que usted declaró — aún no hay datos de uso. Las recomendaciones se actualizarán cuando el Cerebro empiece a observar (tras un gate separado).",
  bpRecommended: "Recomendado contratar ahora",
  bpRecommendedEmpty: "Aún no hay empleados listos para contratar — enseñe al Cerebro para abrir puestos",
  bpUpcoming: "Puestos abiertos (por cercanía al desbloqueo)",
  bpTraining: "Próximos pasos de enseñanza",
  bpTrainingEmpty: "No hay recomendaciones de enseñanza activas",
  bpUnlocks: "Desbloquea:",
  bpChannels: "Canales en el plan",
  bpProposed: "Propuesto",
  bpKeep: "Aprobar",
  bpRemove: "Quitar",
  bpLater: "Más tarde",
  bpLegend:
    "Propuesto = el Cerebro sugiere, nada ocurrió · Aprobado = un humano aprobó, el comportamiento no cambió · Aplicado = solo tras un futuro gate de activación",
  bpFromInterview: "De su entrevista",

  // ── Gate 3: Insight panels ──
  insightTicketTitle: "El Cerebro sobre este cliente",
  insightTicketSubtitle: "Panorama de inteligencia en vivo — solo lectura, cero escrituras",
  insightCampaignsTitle: "Audiencias explicadas por el Cerebro",
  insightCampaignsSubtitle: "Segmentación determinista sobre datos existentes — solo una propuesta",
  insightQueuesTitle: "El Cerebro sobre sus flujos",
  insightSettingsTitle: "Diagnóstico del Cerebro — Error Copilot",
  noContextData: "Aún no hay datos del Cerebro para este cliente",
  stage_new: "Nuevo",
  stage_engaged: "En contacto",
  stage_qualifying: "En calificación",
  stage_proposal: "Propuesta",
  stage_negotiation: "Negociación",
  stage_won: "Ganado",
  stage_lost: "Perdido",
  stage_dormant: "Inactivo",
  intent_hot: "Caliente",
  intent_warm: "Tibio",
  intent_cold: "Frío",
  intent_unknown: "Desconocido",
  sentiment_positive: "Positivo",
  sentiment_neutral: "Neutral",
  sentiment_negative: "Negativo",
  journeyLabel: "Etapa del recorrido",
  intentLabel: "Intención de compra",
  sentimentLabel: "Sentimiento",
  leadScoreLabel: "Puntuación del lead",
  leadUnscored: "Aún sin puntuación — no hay señal real, y ninguna se inventa",
  missingInfoTitle: "Información faltante",
  nextQuestionTitle: "Próxima mejor pregunta",
  intakeTitle: "Recopilado hasta ahora",
  confirmedMemoryTitle: "Memoria confirmada",
  evidenceLabel: "Anclas de evidencia",
  blocksStagePrefix: "Bloquea la etapa:",

  // Audience preview
  audienceScan: "Se escanearon los últimos {tickets} tickets actualizados recientemente · {pool} contactos · {analyzed} analizados en profundidad",
  audienceTruncatedNote: "La ventana fue truncada — esto es la actividad reciente, no todos los datos",
  audienceProposalOnly: "Solo una propuesta — nadie se agrega automáticamente a ninguna lista; el envío permanece en la pantalla de Campañas, únicamente con plantillas APROBADAS ({approved} aprobadas)",
  basis_intent: "Intención de compra",
  basis_stage: "Etapa del recorrido",
  "basis_ticket-status": "Estado del ticket",
  basis_recency: "Actividad",
  basis_channel: "Canal",
  "recency_active-7d": "Activo esta semana",
  "recency_active-30d": "Activo este mes",
  recency_older: "Más antiguo",
  moreNames: "y {n} más",

  // Queues insight + Flow-Builder preview skeleton
  flowGapsTitle: "Brechas de flujo",
  flowNoGaps: "No se encontraron brechas — todas las colas están configuradas",
  "gap_missing-greeting": "Falta el mensaje de bienvenida",
  "gap_no-flow-options": "No hay opciones de flujo definidas",
  "gap_no-enabled-channels": "No hay canales habilitados para esta cola",
  optionsCountLabel: "{n} opciones",
  flowBuilderTitle: "Flow Builder — Preview",
  flowBuilderHonesty: "El motor de generación de descripción-a-flujo aún no existe — llegará como un slice separado, aprobado por el propietario y protegido por flag. Aquí aparecerá el Preview de un flujo generado, para aprobación humana, antes de cualquier importación.",
  flowBuilderPlaceholder: "Describa su servicio en texto libre — se abrirá tras un gate futuro",

  // Error Copilot / diagnostics
  diagTitle: "Diagnóstico del Cerebro",
  diagClean: "No se encontraron fallos de configuración — todas las verificaciones pasaron",
  diagCheckedAt: "Verificado",
  diagEvidence: "Evidencia",
  diagSeverity_critical: "Crítico",
  diagSeverity_warning: "Advertencia",
  diagSeverity_info: "Información",
  diagRetryHelps: "Reintentar puede ayudar",
  diagRetryWontHelp: "Reintentar no ayudará",
  diagNeedsAdmin: "Requiere un administrador",
  diagNeedsSupport: "Requiere soporte",
  diagFixBtn: "Corregir en la pantalla existente",
  diagUncertainTitle: "Fallo no reconocido",
  diagUncertainBody: "No pude determinar la causa; esto es exactamente lo que se verificó — sin conjeturas.",
  diagStage: "En qué etapa",
  diagFault: "De quién es el fallo",
  fault_provider: "Proveedor",
  fault_config: "Configuración",
  fault_system: "Sistema",
  diagShownOf: "Se muestran {shown} de {total} plantillas rechazadas",
  diag_channel_disconnected_title: "Canal desconectado",
  diag_channel_disconnected_cause: "La conexión existe pero no reporta estar conectada — por lo general, una sesión expirada o un dispositivo sin conexión.",
  diag_channel_disconnected_steps: "Abra la pantalla de Conexiones y vuelva a conectar.",
  diag_channel_disconnected_stage: "La sesión del canal",
  diag_channel_in_setup_title: "Conexión en proceso de configuración",
  diag_channel_in_setup_cause: "La conexión está a mitad de la configuración — a la espera de un escaneo de QR o de que termine el inicio de sesión; esto no es un fallo.",
  diag_channel_in_setup_steps: "Complete la configuración en la pantalla de Conexiones.",
  diag_channel_in_setup_stage: "Configuración de la conexión",
  diag_voice_invalid_token_title: "El proveedor de voz rechazó las credenciales",
  diag_voice_invalid_token_cause: "El proveedor rechazó las credenciales — es posible que hayan sido rotadas o revocadas.",
  diag_voice_invalid_token_steps: "Actualice las credenciales en la Configuración de voz; reintentar no ayudará antes de eso.",
  diag_voice_invalid_token_stage: "Autenticación con el proveedor",
  diag_voice_suspended_title: "Conexión de voz suspendida",
  diag_voice_suspended_cause: "La conexión está marcada como suspendida.",
  diag_voice_suspended_steps: "Consulte con soporte antes de reactivarla.",
  diag_voice_suspended_stage: "Administración del sistema",
  diag_voice_disconnected_title: "Conexión de voz no validada",
  diag_voice_disconnected_cause: "La conexión está configurada pero nunca fue validada con el proveedor.",
  diag_voice_disconnected_steps: "Ejecute una prueba de conexión en la Configuración de voz.",
  diag_voice_disconnected_stage: "Validación de la conexión",
  diag_calendar_error_title: "Último error del calendario",
  diag_calendar_error_cause: "La última llamada al proveedor de calendario falló; el error se limpia con el próximo éxito.",
  diag_calendar_error_steps: "Revise la conexión en la pantalla de Citas; si se repite — vuelva a conectar.",
  diag_calendar_error_stage: "Llamada al proveedor de calendario",
  diag_template_rejected_title: "Plantilla rechazada por Meta",
  diag_template_rejected_cause: "Meta rechazó esta plantilla — no puede usarse para enviar hasta corregirla y obtener una nueva aprobación.",
  diag_template_rejected_steps: "Edite y vuelva a enviar la plantilla en Configuración.",
  diag_template_rejected_stage: "Revisión de plantillas de Meta",

  // Long-running task contract (י״א.16)
  ltStarted: "Iniciado",
  ltScope: "Alcance",
  ltStage: "Etapa actual",
  ltCancel: "Cancelar",
  ltPartial: "Resultados parciales",
  ltNotify: "Notificar al finalizar",
  ltCounter: "{done} de {total}",
  ltNoFake: "No hay un contador real del motor — se muestra un indicador indeterminado, no porcentajes",

  // Intelligence layer
  intelJourneyTitle: "Recorrido y transferencias (Handoff)",
  intelJourneyEmpty: "Aún no hay observaciones — los flags de aprendizaje están desactivados; este es el estado previsto. Cuando se activen, verá aquí dónde y por qué el bot transfirió a un humano.",
  intelDashLink: "Abrir el dashboard de IA existente",
  intelAudienceTitle: "Segmentos de audiencia (Tier-1)",

  // Automation layer
  autoPoliciesTitle: "Políticas (Policy Registry)",
  autoPoliciesBanner: "Registros aprobados — ninguno está activo, por diseño (Approved ≠ Active). Ningún motor de aplicación los consume.",
  policyInactive: "Inactiva",
  autoFlowsTitle: "Flujos del bot (de las colas existentes)",
  autoApprovalsTitle: "Cadena de aprobaciones",
  approvalsEmpty: "Aún no hay registros de aprobación — aparecerán cuando existan candidatos y aprobaciones humanas",
  configVersionsTitle: "Versiones de configuración",
  configVersionsEmpty:
    "Todavía no se han registrado versiones. El escritor existe y se ejecuta en cada turno gobernado cuyo hash de configuración cambió, pero solo mientras BRAIN_GOVERNANCE_RECORD esté activado.",

  // System layer
  sysFlagsTitle: "Flags del sistema (Brain)",
  flagsProcessNote: "Flags a nivel de proceso — idénticos para todos los tenants en el host compartido; cambiar uno = un despliegue, no una configuración",
  flagOn: "Encendido",
  flagOff: "Apagado",
  sysConfigTitle: "Configuración efectiva",
  sysAuditTitle: "Auditoría",

  // ── 2026-08-03: keys that existed only in he/en, so this language
  //    fell back to English for roughly a quarter of the AI-OS surface —
  //    including every area name and every tab label. Translated, not
  //    transliterated; product nouns (the Brain, digital employees) follow
  //    the wording already used elsewhere in this dictionary.
  areaHome: "Resumen",
  areaBrain: "El Cerebro",
  areaBrainSub: "Preparación, cobertura de conocimiento, observaciones y calibración de confianza",
  areaEmployees: "Empleados digitales",
  areaEmployeesSub: "14 roles en 5 departamentos — quién está listo, quién bloqueado y qué falta",
  areaKnowledge: "Conocimiento",
  areaKnowledgeSub: "Lo que el Cerebro puede citar y lo que aún le falta",
  areaIntelligence: "Inteligencia",
  areaIntelligenceSub: "Hallazgos respaldados por evidencia, recorrido del cliente y segmentos",
  areaDashboard: "Panel de IA",
  areaDashboardSub: "Los números operativos detrás de la IA",
  areaGrowth: "Marketing",
  areaGrowthSub: "Datos de marketing, conexiones e insights en un solo lugar",
  areaVoice: "Voz",
  areaVoiceSub: "El agente de voz: preparación, llamadas y conexiones",
  areaAutomation: "Automatización y gobernanza",
  areaAutomationSub: "Qué corre solo, qué requiere aprobación y quién aprobó",
  areaDiscovery: "Enseñar al Cerebro",
  areaDiscoverySub: "Entrevista de descubrimiento, el plan de construcción y el Cerebro en cada pantalla",
  areaSetup: "Ajustes del Cerebro",
  areaSetupSub: "Configuración, persona, configuración efectiva, banderas y versiones",
  areaPlannedTitle: "Esta área todavía no está conectada",
  areaPlannedReason: "La pantalla existe, pero el motor que la alimenta aún no se ha construido — por eso aquí no se mostrará ningún número inventado.",
  areaPlannedHint: "En cuanto llegue el motor, los datos reales aparecerán aquí automáticamente.",
  bpApplied: "Aplicado",
  candidateApprove: "Aprobar",
  candidateReject: "Rechazar",
  candidateResolving: "Procesando…",
  candidateResolveError: "La acción falló — inténtalo de nuevo",
  candidateSupport: "Observaciones de apoyo",
  candidateSupportHint: "Cuántas observaciones independientes respaldan esta sugerencia",
  employeesNoneReady: "Ningún rol ha superado aún el umbral de preparación — enseñar el conocimiento que falta abrirá el primero.",
  employeesNoneOpen: "No hay ninguna posición abierta ahora mismo — todos los roles que el Cerebro conoce superaron el umbral.",
  homeAreasSub: "Cada área se abre como su propia página con pestañas — sin desplazamiento infinito",
  homeAsksTitle: "Lo que el Cerebro te pide",
  homeAsksSubtitle: "Cada aprobación mueve al Cerebro un paso",
  homeBlockersSubtitle: "Los tres principales bloqueos",
  homeBlockersEmpty: "No hay ningún bloqueo reportado ahora mismo — cuando se acumulen datos, aquí aparecerá lo que frena al Cerebro.",
  homeJumpTitle: "A dónde ir",
  homeJumpSubtitle: "Las áreas que más hacen avanzar al Cerebro",
  homePendingApprovals: "Decisiones registradas como pendientes de aprobación",
  homeReadinessSubtitle: "Los números detrás de la puntuación",
  homeReadyRoles: "Roles listos para contratar",
  homeReadyRolesHint: "Superaron el umbral de preparación y te esperan",
  homeReadyRolesBlocked: "Ningún rol puede abrirse todavía: cada rol también requiere una confianza del Cerebro del 50% o más, y aún no se ha medido ninguna confianza — enseñar conocimiento por sí solo no abrirá ninguno.",
  homeShadowVerdictsSub: "Veredictos en modo sombra — solo registrados. No hay cola de aprobación para decisiones y ninguna se ejecutó.",
  inScreenNote: "El mismo Cerebro aparece dentro de tus pantallas del día a día como una tarjeta de solo lectura. Nunca escribe nada, y a quien no tiene permiso simplemente no se le muestra.",
  inScreenOpen: "Abrir pantalla",
  inScreenWhere: "Ubicación",
  inScreenTicketsTitle: "El Cerebro sobre este cliente (tickets)",
  inScreenTicketsBody: "Etapa del recorrido, intención de compra, sentimiento, qué falta para avanzar y la siguiente pregunta que vale la pena hacer — cada una con la evidencia detrás.",
  inScreenQueuesTitle: "Vacíos en los guiones del bot (colas)",
  inScreenQueuesBody: "Detección de vacíos sobre tus colas existentes — por ejemplo una cola sin mensaje de bienvenida — para que sepas qué cerrar primero.",
  inScreenCampaignsTitle: "Segmentos de audiencia (campañas)",
  inScreenCampaignsBody: "Segmentación determinista sobre los datos existentes, solo como propuesta: nadie se añade automáticamente y el envío permanece en la pantalla de campañas.",
  inScreenSettingsTitle: "Diagnóstico de errores (ajustes)",
  inScreenSettingsBody: "Qué se rompió, por qué, quién debería arreglarlo y cuál es el siguiente paso — cada arreglo es un enlace a la pantalla existente, nunca un arreglo automático.",
  intelCalibrationRatio: "Candidatos / observaciones",
  seamUnavailable: "El dato no está disponible ahora mismo",
  sentimentNotDerived: "Aún no derivado — no hay señal de sentimiento y no se inventa ninguna",
  setupPersonaSub: "La persona, el tono y el modelo detrás de cada respuesta — junto al saludo, las fuentes y las conexiones que la alimentan",
  setupWizardSub: "La configuración inicial del Cerebro: quiénes sois, cómo habla, de dónde lee los datos y cuándo pasa a una persona",
  stepIntakeShort: "Recogida de datos",
  stepHandoffShort: "Paso a persona",
  tabOverview: "Resumen",
  tabReadiness: "Preparación",
  tabCoverage: "Cobertura de conocimiento",
  tabObservations: "Observaciones",
  tabCalibration: "Calibración de confianza",
  tabOrg: "La organización",
  tabReadyToHire: "Listos para contratar",
  tabOpenPositions: "Posiciones abiertas",
  tabEffectiveConfig: "Configuración efectiva",
  tabWorkforceConsole: "Consola de plantilla",
  tabSources: "Fuentes",
  tabKnowledgeItems: "Elementos de conocimiento",
  tabCustomerMemory: "Memoria de clientes",
  tabCandidates: "Candidatos",
  tabInsights: "Hallazgos",
  tabJourney: "Recorrido y clientes",
  tabSegments: "Segmentos de audiencia",
  tabAgentPerformance: "Rendimiento de agentes",
  tabTopIssues: "Problemas principales",
  tabSentimentTrends: "Tendencias de sentimiento",
  tabSalesInsights: "Hallazgos de ventas",
  tabConnections: "Conexiones",
  tabCampaigns: "Campañas",
  tabLearningInsights: "Aprendizaje y hallazgos",
  tabDiagnostics: "Diagnóstico y fallos",
  tabCalls: "Llamadas",
  tabVoiceAgents: "Agentes",
  tabInCallBilling: "Cobro en llamada",
  tabCallSearch: "Búsqueda",
  tabVoiceSettings: "Ajustes de voz",
  tabFlows: "Procesos",
  tabPolicies: "Políticas",
  tabDecisionLog: "Registro de decisiones",
  tabAuditChain: "Cadena de auditoría",
  tabInterview: "Entrevista de descubrimiento",
  tabBlueprint: "Plan del Cerebro",
  tabBrainInScreens: "El Cerebro en tus pantallas",
  tabSetupWizard: "Asistente de configuración",
  tabPersonaTone: "Persona y tono",
  tabSystemFlags: "Banderas del sistema",
  tabConfigVersions: "Versiones",
  tabHealthDiagnostics: "Salud y diagnóstico",
  tabGovernanceSettings: "Gobernanza y ajustes",
  tabMemoryRecommendations: "Memoria y recomendaciones",
  tabReportsTimeline: "Informes y cronología",

  // The design closes every HOME queue card with a gradient CTA and an
  // outlined "details". Ours point at two different screens on purpose:
  // coverage is where the missing knowledge gets taught, observations is
  // where the raw finding lives.
  homeAskCta: "Enseñárselo ahora",
  homeAskDetails: "Detalles",

  // ── Brain prose authored by the BACKEND, said in this language ─────────
  // evaluateBrainEvolution.ts writes readiness.health.issues[],
  // coverage[].missing[] and requirements[].guidance as English sentences,
  // and four screens printed them verbatim — so a Hebrew tenant read English
  // on the HOME hero, on all three queue cards, under every coverage bar and
  // in the Blueprint. brainText() below matches each form and renders it from
  // here; anything it does not recognise still shows the raw sentence.
  biNoSettings: "No hay ajustes de IA configurados para esta empresa",
  biAiDisabled: "La IA está desactivada para esta empresa",
  biNoKnowledge: "Todavía no se ha enseñado ningún elemento de conocimiento",
  biNoObservations: "No se han registrado observaciones de aprendizaje (puede que las banderas de aprendizaje estén apagadas)",
  biNoMemories: "Aún no hay memorias de cliente confirmadas",
  biRolesLocked: "{n} de {total} roles todavía no se pueden abrir — {reasons}",
  biReasonCoverage: "conocimiento por debajo del {pct}% en {cats}",
  biReasonNoConfidence: "todavía no se ha registrado confianza de decisión",
  biReasonThinSample: "no hay evidencia suficiente para una puntuación de confianza ({n} de {min} decisiones)",
  biReasonLowConfidence: "la confianza de decisión {value} está por debajo de {min}",
  bcConfigureSettings: "Configura los ajustes de IA de la empresa",
  bcConnectCrm: "Conecta una fuente de datos del CRM",
  bcMoreMemories: "Confirma más memorias de cliente ({n}/{target})",
  bcMoreObservations: "Hacen falta más interacciones observadas ({n}/{target})",
  bcDefineTone: "Define el tono de voz de la empresa",
  bcVoiceUnavailable: "La enseñanza de estilo de voz todavía no está disponible",
  bcAddKnowledge: "Añade elementos de conocimiento en la categoría {cat} ({n}/{target})",
  bgTeachCategory: "Enseña al Cerebro sobre {cat} hasta al menos un {pct}% de cobertura",
  bgConfidenceGrows: "La confianza de las decisiones del Cerebro crece a medida que se acumulan decisiones gobernadas",
  bgThinEvidence: "Todavía no hay evidencia suficiente — hacen falta {n} de {min} decisiones registradas antes de que una puntuación de confianza signifique algo",

  // The shadow-mode banner is a `note` card in the design, and a design note
  // card always has a title. We had the sentence but no name for it, so the
  // card rendered headless in a grid where every sibling is titled.
  shadowNoteTitle: "Modo sombra",

  // Data-First: an absent dataset renders an honest empty state saying WHAT is
  // missing and WHY. The flags card had no such copy, so a failed read drew a
  // card with a head and a footnote and nothing between them.
  flagsEmpty: "No se devolvió ninguna bandera de proceso. Son banderas a nivel de host — una lista vacía significa que la lectura falló, no que no haya ninguna.",

  // The roadmap counts card had no name — it was the one untitled cell in a
  // grid where every sibling is titled, because nothing in the dictionary
  // named the summary itself (only the three things it counts).
  bpRoadmapTitle: "El plan de un vistazo",
};

const es_ROLES = {
  sales: "Representante de ventas",
  collections: "Representante de cobranza",
  support: "Representante de soporte",
  receptionist: "Recepcionista",
  appointment: "Coordinador de citas",
  marketing: "Especialista en marketing",
  "campaign-manager": "Gerente de campañas",
  "crm-assistant": "Asistente de CRM",
  "voice-agent": "Agente de voz",
  "qa-manager": "Gerente de QA",
  "knowledge-manager": "Gerente de conocimiento",
  "reporting-analyst": "Analista de reportes",
  supervisor: "Supervisor",
  "executive-assistant": "Asistente ejecutivo",
};

const es_CATEGORIES = {
  products: "Productos",
  services: "Servicios",
  "company-policies": "Políticas de la empresa",
  documents: "Documentos",
  "crm-understanding": "Comprensión del CRM",
  "customer-history": "Historial de clientes",
  "conversation-history": "Historial de conversaciones",
  "writing-style": "Estilo de escritura",
  "voice-style": "Estilo de voz",
  "sales-process": "Proceso de ventas",
  "support-process": "Proceso de soporte",
  "payment-policies": "Políticas de pago",
  "automation-rules": "Reglas de automatización",
};

const es_INTAKE = {
  "legal-domain": "Área legal",
  urgency: "Urgencia",
  "opposing-party": "Parte contraria (verificación de conflicto de intereses)",
  "buy-sell-rent": "Compra / venta / alquiler",
  "property-type": "Tipo de propiedad",
  areas: "Zonas",
  "mortgage-approved": "Aprobación de hipoteca",
  destination: "Destino",
  dates: "Fechas",
  budget: "Presupuesto",
  travellers: "Viajeros",
  "service-type": "Tipo de servicio",
  "preferred-times": "Horarios preferidos",
  "product-interest": "Producto de interés",
  "order-number": "Número de pedido",
};

const es_KPI = {
  "time-to-first-consult": "Tiempo hasta la primera consulta",
  "tours-scheduled": "Visitas programadas",
  "offers-sent": "Ofertas enviadas",
  "appointments-set": "Citas programadas",
  "orders-assisted": "Pedidos asistidos",
  "quotes-sent": "Cotizaciones enviadas",
  "conversations-resolved": "Conversaciones resueltas",
};

const es_REQKIND = {
  knowledge: "Conocimiento",
  capability: "Capacidad",
  policy: "Política",
  confidence: "Confianza",
  tool: "Herramienta",
  channel: "Canal",
  permission: "Permiso",
  governance: "Gobernanza",
};

ROLE_LABELS.ar = ar_ROLES;
CATEGORY_LABELS.ar = ar_CATEGORIES;
INTAKE_LABELS.ar = ar_INTAKE;
KPI_LABELS.ar = ar_KPI;
REQ_KIND_LABELS.ar = ar_REQKIND;
ROLE_LABELS.ru = ru_ROLES;
CATEGORY_LABELS.ru = ru_CATEGORIES;
INTAKE_LABELS.ru = ru_INTAKE;
KPI_LABELS.ru = ru_KPI;
REQ_KIND_LABELS.ru = ru_REQKIND;
ROLE_LABELS.es = es_ROLES;
CATEGORY_LABELS.es = es_CATEGORIES;
INTAKE_LABELS.es = es_INTAKE;
KPI_LABELS.es = es_KPI;
REQ_KIND_LABELS.es = es_REQKIND;

const DICTS = { he, en, ar, ru, es };

function currentLang() {
  const lang = (i18n.language || "he").slice(0, 2);
  // Staged-content rule: ar/ru/es fall back to English until Gate 4.
  return DICTS[lang] ? lang : "en";
}

/** The effective AI-OS content language ("he" | "en") after staged-content fallback. */
export function aiosLang() {
  return currentLang();
}

/** Translate an AI-OS key with optional {var} interpolation. */
export function aiosT(key, vars) {
  const lang = currentLang();
  let text = DICTS[lang][key] ?? en[key] ?? key;
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      text = text.split(`{${name}}`).join(String(value));
    }
  }
  return text;
}

/* ══════════════════════════════════════════════════════════════════════════
   BACKEND-AUTHORED BRAIN PROSE → this language
   ══════════════════════════════════════════════════════════════════════════

   The readiness evaluator writes its explanations as English sentences —
   `health.issues[]`, `coverage[].missing[]`, `requirements[].guidance` — and
   four screens printed them straight to the page: the HOME hero note, all
   three HOME queue-card titles, the hint under every knowledge-coverage bar,
   and the Blueprint's per-requirement guidance. On a Hebrew tenant that is the
   loudest thing on the screen, and it is the same in all five languages.

   Fixing it in the backend means changing a seam's response shape, which is a
   separate and riskier change. This does it at the edge instead: the set of
   sentences is CLOSED and enumerable (evaluateBrainEvolution.ts), so each form
   is matched, its numbers and category names are pulled out, and the sentence
   is re-rendered from the dictionary above.

   Two rules this must never break:
     · An unrecognised sentence is returned AS IS. A backend that adds a
       nineteenth message must show it in English rather than have it vanish —
       a silently dropped explanation is worse than an untranslated one.
     · Numbers are passed through untouched, so <Num>/bdi still isolate them.

   `categoryLabel` is reused for category names so "products" reads the same
   here as it does on its own coverage bar. */

const PCT = "(\\d+)";
const NUM = "([\\d.]+)";

/** [regex, key, [param names in capture order]] — order matters: first match wins. */
const BRAIN_FORMS = [
  [/^No AI settings configured for this company$/, "biNoSettings", []],
  [/^AI is disabled for this company$/, "biAiDisabled", []],
  [/^No knowledge items taught yet$/, "biNoKnowledge", []],
  [/^No learning observations recorded \(learning flags may be off\)$/, "biNoObservations", []],
  [/^No confirmed contact memories yet$/, "biNoMemories", []],

  // Reasons — matched before the roles line so the recursive call below resolves.
  [new RegExp("^knowledge under " + PCT + "% in (.+)$"), "biReasonCoverage", ["pct", "cats"]],
  [/^no decision confidence recorded yet$/, "biReasonNoConfidence", []],
  [
    new RegExp("^not enough evidence for a confidence score \\(" + NUM + " of " + NUM + " decisions\\)$"),
    "biReasonThinSample",
    ["n", "min"],
  ],
  [
    new RegExp("^decision confidence " + NUM + " is under " + NUM + "$"),
    "biReasonLowConfidence",
    ["value", "min"],
  ],

  [new RegExp("^Configure the AI settings for this company$"), "bcConfigureSettings", []],
  [/^Connect a CRM data source$/, "bcConnectCrm", []],
  [
    new RegExp("^Confirm more contact memories \\(" + NUM + "/" + NUM + "\\)$"),
    "bcMoreMemories",
    ["n", "target"],
  ],
  [
    new RegExp("^More observed interactions needed \\(" + NUM + "/" + NUM + "\\)$"),
    "bcMoreObservations",
    ["n", "target"],
  ],
  [/^Define the company tone of voice$/, "bcDefineTone", []],
  [/^Voice-style teaching is not yet available$/, "bcVoiceUnavailable", []],
  [
    new RegExp("^Add knowledge items in category (.+?) \\(" + NUM + "/" + NUM + "\\)$"),
    "bcAddKnowledge",
    ["cat", "n", "target"],
  ],

  [
    new RegExp('^Teach the Brain about "(.+?)" to at least ' + PCT + "% coverage$"),
    "bgTeachCategory",
    ["cat", "pct"],
  ],
  [/^Brain decision confidence grows as governed decisions accumulate$/, "bgConfidenceGrows", []],
  [
    new RegExp(
      "^Not enough evidence yet — " + NUM + " of " + NUM +
        " recorded decisions needed before a confidence score is meaningful$"
    ),
    "bgThinEvidence",
    ["n", "min"],
  ],
];

/** The comma-joined category list inside a reason, each name said in this language. */
const localizeCategories = (list) =>
  String(list)
    .split(", ")
    .map((c) => categoryLabel(c.trim()))
    .join(", ");

/**
 * A backend-authored Brain sentence, in the reader's language.
 * Returns the input unchanged when the sentence is not one we know.
 */
export function brainText(sentence) {
  if (typeof sentence !== "string" || !sentence) return sentence;
  const text = sentence.trim();

  // The roles line wraps one or more reasons; translate the wrapper, then each
  // reason through this same function, so a new reason still degrades to English
  // inside an otherwise translated sentence.
  const locked = /^(\d+) of (\d+) roles cannot be unlocked yet — (.+)$/.exec(text);
  if (locked) {
    return aiosT("biRolesLocked", {
      n: locked[1],
      total: locked[2],
      reasons: locked[3].split("; ").map(brainText).join("; "),
    });
  }

  for (const [re, key, names] of BRAIN_FORMS) {
    const m = re.exec(text);
    if (!m) continue;
    const vars = {};
    names.forEach((name, i) => {
      const raw = m[i + 1];
      vars[name] = name === "cats" ? localizeCategories(raw)
        : name === "cat" ? categoryLabel(raw)
        : raw;
    });
    return aiosT(key, vars);
  }
  return sentence;
}

/**
 * A structured Brain finding, in the reader's language.
 *
 * `readiness.health.issueCodes` carries a code plus its parameters; this maps the code to
 * the SAME translation key brainText() reaches through a regex, and interpolates the
 * parameters directly. No sentence is parsed.
 *
 * WHY THIS EXISTS BESIDE brainText(). brainText matches backend English with a table of
 * regexes — it works until someone rewords a sentence, and then it silently stops
 * translating while looking exactly as correct as before. The codes remove the guessing:
 * the backend says which finding it is, and this says it in the reader's language.
 *
 * Returns null when the shape is not one we know, so a caller can fall back rather than
 * render a blank row.
 */
export function brainIssueText(issue) {
  if (!issue || typeof issue !== "object" || typeof issue.code !== "string") return null;

  const SIMPLE = {
    "settings-missing": "biNoSettings",
    "settings-disabled": "biAiDisabled",
    "knowledge-empty": "biNoKnowledge",
    "observations-empty": "biNoObservations",
    "contact-memories-empty": "biNoMemories",
  };
  if (SIMPLE[issue.code]) return aiosT(SIMPLE[issue.code]);

  if (issue.code !== "roles-locked") return null;

  /* The reasons, in the order the backend composed them, so a reader comparing the panel
     with a log sees the same sequence. Each category name is said in this language —
     categoryLabel, the same call the coverage bars use. */
  const reasons = [];
  if (Array.isArray(issue.blockingCategories) && issue.blockingCategories.length > 0) {
    reasons.push(
      aiosT("biReasonCoverage", {
        pct: issue.coverageThresholdPct,
        cats: issue.blockingCategories.map(categoryLabel).join(", "),
      })
    );
  }
  const c = issue.confidence;
  if (c && typeof c.reason === "string") {
    if (c.reason === "none") reasons.push(aiosT("biReasonNoConfidence"));
    else if (c.reason === "insufficient-evidence") {
      reasons.push(aiosT("biReasonThinSample", { n: c.sampleSize, min: c.required }));
    } else if (c.reason === "below-threshold") {
      reasons.push(aiosT("biReasonLowConfidence", { value: c.value, min: c.threshold }));
    }
  }
  return aiosT("biRolesLocked", {
    n: issue.locked,
    total: issue.total,
    reasons: reasons.join("; "),
  });
}

/** Human label for an EmployeeRole (falls back to the raw role key). */
export function roleLabel(role) {
  const lang = currentLang();
  return ROLE_LABELS[lang][role] ?? ROLE_LABELS.en[role] ?? role;
}

/** Human label for a KnowledgeCategory (falls back to the raw key). */
export function categoryLabel(category) {
  const lang = currentLang();
  return CATEGORY_LABELS[lang][category] ?? CATEGORY_LABELS.en[category] ?? category;
}

/** Human label for a Discovery intake-field key (falls back to the raw key). */
export function intakeLabel(key) {
  const lang = currentLang();
  return INTAKE_LABELS[lang][key] ?? INTAKE_LABELS.en[key] ?? key;
}

/** Human label for a Discovery KPI id (falls back to the raw id). */
export function kpiLabel(kpi) {
  const lang = currentLang();
  return KPI_LABELS[lang][kpi] ?? KPI_LABELS.en[kpi] ?? kpi;
}

/** Human label for a Discovery handoff id (falls back to the raw id). */
export function handoffLabel(id) {
  const key = HANDOFF_LABEL_KEYS[id];
  return key ? aiosT(key) : id;
}

/** Human label for an EmployeeUnlockRequirement kind (falls back to the raw kind). */
export function reqKindLabel(kind) {
  const lang = currentLang();
  return REQ_KIND_LABELS[lang][kind] ?? REQ_KIND_LABELS.en[kind] ?? kind;
}
